/* ============================================================
   River City — custom bowstring builder
   Ported from the prototype. The rendering engine and step logic
   are unchanged; what's new is the data layer (Storefront API
   instead of hard-coded arrays), pricing (Shopify variants
   instead of constants) and the cart.

   Data comes from three metaobject definitions:
     bow_model  · string_colour · string_material
   Prices come from the variants of `custom-bowstring-set` and
   `build-options`, so the admin is the only place a price lives.
   ============================================================ */
(function () {
"use strict";

var CFG  = window.RC_BUILDER_CONFIG || {};
var ROOT = null;

/* ============================================================
   PRICING — read from Shopify, not from this file
   ============================================================ */
var COMPOUND_PACKAGES = [];      // filled from CFG.variants
var CROSSBOW_PACKAGE  = null;   // 1-3 piece: Excalibur, TenPoint, Barnett and the rest
var CROSSBOW_MULTI    = null;   // 4+ piece: the Ravin helical-cable sets
var TRAD_PACKAGE      = null;
var SINGLES           = {};     // individual strings and cables, keyed scs/tcs/ctl/bus/yok/xbs/xbc
var OPTIONS           = [];

/* Nock fit decides the centre serving diameter, so the order has to carry it.
   The builder only captures the answer — the bench sizes and test-fits from
   what prints on the slip. The menu is a section setting rather than a
   metaobject: about a dozen slow-changing entries, and no gating depends on
   it, so an admin textarea beats a fourth definition. */
/* Heat shrink over the speed nocks. A short list of what is actually stocked,
   not the string palette — offering 68 colours of shrink would promise
   something the bench cannot build. First entry is the default. */
/* "Main, Second, Pinstripe" from the `showpiece` section setting; the last two optional.
   Blank or missing: no showpiece. Names are checked against the catalogue once it has
   loaded (update()), so a typo costs that one colour and never reaches a packing slip. */
var SHOWPIECE = (function () {
  var p = String((typeof CFG !== "undefined" && CFG && CFG.showpiece) || "").split(",").map(function (x) { return x.trim(); });
  return p[0] ? { main: p[0], second: p[1] || null, pin: p[2] || null } : null;
})();
var SHRINKS = [];
function buildShrinks() {
  SHRINKS = String(CFG.shrinkColours || "Black,White,Red").split(",")
    .map(function (x) { return x.trim(); })
    .filter(Boolean);
}
/* AMO lengths for step 4 on a recurve or longbow, where a compound has its
   model year. A menu rather than a free-text box: an archer knows their bow is
   a 62", and typing it invites 62, 62", 62 inches, sixty-two — all of which the
   bench then has to interpret. "Other" is always appended and opens the text
   box, because odd and imperial-fraction lengths exist and a list that cannot
   express them would turn a sale away. */
/* AMO lengths carry a literal double quote — 62" — which is also what closes an
   HTML attribute, so anything from this list or from the customer's own typing
   has to be escaped before it goes near innerHTML. */
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
var AMO_LENGTHS = [];
function buildAmoLengths() {
  AMO_LENGTHS = String(CFG.amoLengths || '48",50",52",54",56",58",60",62",64",66",68",70",72"')
    .split(",")
    .map(function (x) { return x.trim(); })
    .filter(Boolean);
}
/* D‑loop rope colours: what is on the shelf, not BCY's catalogue range, so
   the site never promises stock we do not hold. #24 and #23 each have a list. */
var LOOP_COLOURS = [];
/* #23 has its own, shorter list — Quinn, 2026-09-12: "kill flo purple for bcy 23".
   Blank setting means #23 offers the same list as #24. */
var LOOP_COLOURS_23 = [];
function buildLoopColours() {
  var split = function (v) { return String(v).split(",").map(function (x) { return x.trim(); }).filter(Boolean); };
  LOOP_COLOURS = split(CFG.dloopColours || "Black,Electric Red,Flo Green,Flo Yellow,Flo Purple");
  LOOP_COLOURS_23 = split(CFG.dloopColours23 == null ? "Black,Electric Red,Flo Green,Flo Yellow" : CFG.dloopColours23);
}
function loopColours() {
  return (S.opts.loopRope === "23" && LOOP_COLOURS_23.length) ? LOOP_COLOURS_23 : LOOP_COLOURS;
}

var NOCKS = [];
/* Last tile, always. A fixed list can never cover every nock, and a customer
   whose nock is missing would otherwise leave the default answer in place and
   we would never know. Asking for the arrow instead is the useful question —
   the bench can work the nock out from the shaft. */
var NOCK_OTHER = "Not listed — tell us your arrow";
function nockDefault() { return (CFG.nockDefault || "").trim() || "Not sure"; }
function buildNocks() {
  var list = String(CFG.nockList || "").split("\n")
    .map(function (x) { return x.trim(); })
    .filter(Boolean);
  /* The default sits first and is always present, so the step is answered from
     the moment it renders and an empty setting still yields a working control. */
  NOCKS = [nockDefault()].concat(list).concat([NOCK_OTHER]);
}

function buildPricing() {
  var v = CFG.variants || {};
  function pack(key, name, min, max) {
    var x = v[key];
    /* "Default Title" is Shopify's placeholder for a product with one variant,
       not a name. The recurve string is such a product, so its summary line read
       "Default Title $59.99" on the live store (audit, 2026-10-08) while the
       harness, which sent a real title, never showed it. The fallback name is
       the one already written beside each key below. */
    var title = (x && x.title && x.title !== "Default Title") ? x.title : name;
    return x ? { id: x.id, name: title, price: x.price, min: min, max: max } : null;
  }
  COMPOUND_PACKAGES = [
    pack("set23", "2–3 Piece Set", 2, 3),
    pack("set45", "4–5 Piece Set", 4, 5),
    pack("set67", "6–7 Piece Set", 6, 9)
  ].filter(Boolean);
  CROSSBOW_PACKAGE = pack("setXb",      "Crossbow Complete Set", 0, 3);
  CROSSBOW_MULTI   = pack("setXbMulti", "Crossbow Multi-Cable Set", 4, 99);
  TRAD_PACKAGE     = pack("setTrad", "Recurve / Longbow String", 0, 99);

  /* Individual strings and cables, keyed the way the bow's own piece_breakdown
     names them. Same shape as a package so pkgFor() can return either and
     nothing downstream has to know the difference. */
  SINGLES = {
    scs: pack("sglScs", "Single cam string",  0, 99),
    tcs: pack("sglTcs", "Two cam string",     0, 99),
    ctl: pack("sglCtl", "Control cable",      0, 99),
    bus: pack("sglBus", "Split buss cable",   0, 99),
    yok: pack("sglYok", "Yoke cable",         0, 99),
    xbs: pack("sglXbs", "Crossbow main string", 0, 99),
    xbc: pack("sglXbc", "Crossbow buss cable",  0, 99)
  };

  /* Pinstripe is not in here: it is rendered in step 6 beside the swatches it
     controls, not in Options. Kept out of OPTIONS entirely rather than hidden
     with type:"auto", so there is one place it comes from. */
  OPTIONS = [
    { id:"colour2", name:"Second colour", note:"Two-tone through the bundle", price:0, type:"auto" }
  ];
  /* One quantity for the D‑loop and a separate choice of rope, rather than a
     stepper each. They are the same product in two diameters — two steppers
     would invite an order for both and read as twice the decision it is. */
  var ropes = [];
  if (v.loop24) ropes.push({ k:"24", label:"BCY #24", note:"Stiffer and thicker",
                             price:v.loop24.price, variantId:v.loop24.id });
  if (v.loop23) ropes.push({ k:"23", label:"BCY #23", note:"Less torque and lasts longer, but slips through some Carter releases",
                             price:v.loop23.price, variantId:v.loop23.id });
  if (ropes.length) OPTIONS.push({ id:"loop", name:"D‑loop, 2 pack",
                                   note:"Pre‑cut BCY loop rope", price:ropes[0].price,
                                   type:"qty", ropes:ropes, variantId:ropes[0].variantId });
  /* RUSH BUILD. Offered on ANYTHING BUILDABLE — Quinn, 2026-08-18 — so unlike
     the D-loop and the peep it is not gated to compounds, and a crossbow, a
     recurve or a single cable can all be rushed.

     THE SEVEN SLOTS A WEEK ARE SHOPIFY INVENTORY, NOT CODE. The variant is
     inventory-tracked at 7; when the week's slots are gone `available` comes
     back false and the row locks itself. That is deliberate: overselling a rush
     is the one failure here that cannot be put right, because the customer paid
     for speed and a refund does not give them their hunt back. A counter in the
     theme would have to be trusted; stock cannot be sold twice. */
  if (v.rush) OPTIONS.push({ id:"rush", name:v.rush.title || "Rush build",
                             note:rushNote(), price:v.rush.price, type:"check",
                             variantId:v.rush.id, available:v.rush.available !== false,
                             soldOut:"Rush builds are not open this week. Standard build time applies.",
                             soldOutPrice:"Not open" });
  /* THE PEEP READS ITS STOCK, and until 2026-09-26 it did not — Quinn: "mark the
     tru peep out of stock", which the STORE already said. It is inventory-tracked
     with DENY and sat at -6, so `available` came through false and the builder
     offered the tickbox anyway.

     That was not a cosmetic miss. addToCart sends the set and its fittings as ONE
     items array, and /cart/add.js is atomic — a real POST of this variant answers
     422 — so ticking a $14.99 peep failed the WHOLE add and the customer lost the
     entire build, not the peep. "Couldn't add that to the cart" was all it said.

     Same mechanism as the rush lane, which has read `available` since 2026-08-18;
     this simply stops the peep being the one fitting that did not. */
  if (v.peep) OPTIONS.push({ id:"peep", name:v.peep.title || '3/16" Tru Peep',
                             note:"Supplied loose with your set — we fit it over the counter in Campbell River or Kamloops",
                             price:v.peep.price, type:"check", variantId:v.peep.id,
                             available:v.peep.available !== false,
                             soldOut:"Out of stock just now. Nothing else about your build is affected.",
                             soldOutPrice:"Out of stock" });
  /* ADD-ONS: THE HAT, AND A STICKER WHEN THERE IS ONE. Quinn, 9 October 2026:
     "people should be able to add a hat or sticker to the cart here (stickers
     to come)". Each is its own product, so the section emits it only when the
     product exists; no product, no row. Offered on every build, a single cable
     included: unlike the D-loop and the peep they have nothing to do with the
     string. They read `available` for the peep's reason: everything goes to
     the cart as ONE atomic request, so a hat that has sold out would fail the
     whole build. The name is the product's own title, so renaming it in the
     admin renames the row. */
  ADDONS.forEach(function (a) {
    if (v[a.id]) OPTIONS.push({ id:a.id, name:v[a.id].title || a.name, note:a.note,
                                price:v[a.id].price, type:"check", addon:true,
                                image:v[a.id].image || "", short:a.short,
                                variants:(v[a.id].variants && v[a.id].variants.length > 1) ? v[a.id].variants : null,
                                variantId:v[a.id].id, available:v[a.id].available !== false,
                                soldOut:"Out of stock just now. Nothing else about your build is affected.",
                                soldOutPrice:"Out of stock" });
  });
}
/* WHICH ONE of an add-on that comes in a colour and a size (the hoodie, the t-shirt).
   The answer is a variant id in S.opts[<id>Var]; anything stale or sold out falls
   back to the first one in stock, so the cart is never handed a variant that fails
   the whole atomic add. An add-on with one variant answers with itself. */
function addonPick(o) {
  if (!o.variants) return { id:o.variantId, title:"", price:o.price, image:o.image };
  var live = o.variants.filter(function (x) { return x.available !== false; });
  var want = S.opts[o.id + "Var"];
  var hit = live.find(function (x) { return x.id === want; }) || live[0] || o.variants[0];
  return { id:hit.id, title:hit.title, price:hit.price, image:hit.image || o.image };
}
function setAddonVar(id, value) {
  S.opts[id + "Var"] = Number(value);
  renderOpts(); update();
}
function addonsOn() {
  return OPTIONS.filter(function (o) { return o.addon && S.opts[o.id] && o.available !== false; });
}
/* The rope carries its own price and variant, so the two can diverge later
   without touching anything here. Falls back to the first rope so a store
   with only one of the two SKUs still sells D‑loops. */
function ropeOf() {
  var o = OPTIONS.find(function (x) { return x.id === "loop"; });
  if (!o || !o.ropes || !o.ropes.length) return null;
  return o.ropes.find(function (r) { return r.k === S.opts.loopRope; }) || o.ropes[0];
}
/* Said in business days against the standard queue in weeks, because "5 days"
   next to "4 weeks" is the whole proposition and a customer should not have to
   do the conversion. Both numbers are theme settings, so neither can drift from
   what the homepage and the cart say. */
function rushNote() {
  var d = parseInt(CFG.rushDays, 10) || 5;
  var w = parseInt(CFG.leadWeeks, 10) || 4;
  return "Built and shipped in about " + d + " business day" + (d === 1 ? "" : "s")
       + ", instead of the usual " + w + " week" + (w === 1 ? "" : "s") + ".";
}
var ADDONS = [
  { id:"hat",     name:"River City hat",     short:"Hat",     note:"Added to this order as its own line" },
  { id:"sticker", name:"River City sticker", short:"Sticker", note:"Added to this order as its own line" },
  { id:"hoodie",  name:"River City hoodie",  short:"Hoodie",  note:"Added to this order as its own line. Tick to choose a colour and size." },
  { id:"tee",     name:"River City t-shirt", short:"T-shirt", note:"Added to this order as its own line. Tick to choose a colour and size." }
];
function priceOf(id) {
  var o = OPTIONS.find(function (x) { return x.id === id; });
  return o ? o.price : 0;
}
function variantOf(id) {
  var o = OPTIONS.find(function (x) { return x.id === id; });
  return o ? o.variantId : null;
}

/* ============================================================
   DATA — Storefront API
   Liquid can only loop 50 metaobjects (250 with pagination, one
   page per render), so 854 bows can't come through the theme.
   We page the Storefront API instead, which also means the admin
   stays the single source of truth: add a bow there and it shows
   up here on the next load.
   ============================================================ */
var BOWS = { compound:{}, crossbow:{}, trad:{} };
var COLOURS = [];
var MATERIALS = [];

var QUERY = [
  'query RCMeta($type: String!, $cursor: String) {',
  '  metaobjects(type: $type, first: 250, after: $cursor) {',
  '    pageInfo { hasNextPage endCursor }',
  '    nodes { handle fields { key value } }',
  '  }',
  '}'
].join("\n");

function gql(type, cursor) {
  var ctl = window.AbortController ? new AbortController() : null;
  var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 20000);
  return fetch("/api/" + (CFG.apiVersion || "2025-01") + "/graphql.json", {
    method: "POST",
    signal: ctl ? ctl.signal : undefined,
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": CFG.token || ""
    },
    body: JSON.stringify({ query: QUERY, variables: { type: type, cursor: cursor || null } })
  }).then(function (r) {
    clearTimeout(timer);
    if (!r.ok) throw new Error("Storefront API returned " + r.status);
    return r.json();
  }, function (e) {
    clearTimeout(timer);
    throw new Error(e && e.name === "AbortError"
      ? "The Storefront API didn't answer within 20 seconds."
      : "Couldn't reach the Storefront API. " + (e && e.message ? e.message : ""));
  }).then(function (j) {
    if (j.errors && j.errors.length) throw new Error(j.errors[0].message);
    if (!j.data || !j.data.metaobjects) throw new Error("No data for type " + type);
    return j.data.metaobjects;
  });
}

function fetchAll(type) {
  var all = [];
  function page(cursor) {
    return gql(type, cursor).then(function (mo) {
      mo.nodes.forEach(function (n) {
        var o = { handle: n.handle };
        n.fields.forEach(function (f) { o[f.key] = f.value == null ? "" : f.value; });
        all.push(o);
      });
      return mo.pageInfo.hasNextPage ? page(mo.pageInfo.endCursor) : all;
    });
  }
  return page(null);
}

/* The make tiles are shown in the order Quinn asked for on 2026-09-17:
   "mathews, hoyt, pse, darton, bear, diamond, elite, bowtech, then everything
   else". The eight are selling order; the remainder is alphabetical.

   The remainder needed a rule at all because what it replaced was not a rule.
   `Object.keys()` on the shaped object handed back whatever order the
   Storefront API paged the metaobjects in — arbitrary, and it MOVES when
   entries are added, which is what the 2015-2017 import did to it. So this is
   a fix as much as a preference: the tiles now sit still.

   Two things to know before editing the list. **All eight are compounds**, so
   the crossbow and traditional tabs are the alphabetical remainder on its own —
   nothing to reorder there. And **matching is exact** (case aside): "Bear X",
   which is Bear's crossbow line under its own make name, is NOT promoted by the
   "Bear" entry. Loose matching would have promoted it on the crossbow tab ahead
   of Excalibur and TenPoint, which is a decision nobody made. Add "Bear X" to
   the list if that is wanted. */
var MAKE_ORDER = ["Mathews", "Hoyt", "PSE", "Darton", "Bear", "Diamond",
                  "Elite", "Bowtech"];

function orderMakes(byMake) {
  var rank = {};
  MAKE_ORDER.forEach(function (m, i) { rank[m.toLowerCase()] = i; });
  var out = {};
  Object.keys(byMake).sort(function (a, b) {
    var ra = rank[a.toLowerCase()], rb = rank[b.toLowerCase()];
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;          // a listed make outranks any other
    if (rb !== undefined) return 1;
    return a.localeCompare(b);
  }).forEach(function (k) { out[k] = byMake[k]; });
  return out;                                 // key order IS the tile order
}

/* ---- reshape into exactly the structures the ported code expects ---- */
function shapeBows(rows) {
  var out = { compound:{}, crossbow:{}, trad:{} };
  rows.forEach(function (r) {
    var t = (r.bow_type || "").trim();
    if (!out[t]) return;                       // unknown type: skip rather than crash
    var make = (r.make || "").trim();
    if (!make) return;
    (out[t][make] = out[t][make] || []).push({
      m: (r.model || "").trim(),
      y: String(r.years || "").split(",").map(function (x) { return parseInt(x, 10); })
           .filter(function (x) { return !isNaN(x); }),
      p: String(r.piece_breakdown || "").split(" + ").map(function (x) { return x.trim(); })
           .filter(Boolean)
      /* No `u` flag. This used to read a `year_confirmed` field that HAS NEVER
         EXISTED on the bow_model definition — zero of 854 entries carried it, so
         the "year range unconfirmed" note it fed could never render. Removed
         2026-08-14 rather than populated: the note promised the bench would check
         the year against a serial, and the year does not change the string set
         (Quinn), so there was nothing for it to check. */
    });
  });
  // newest bows first within each make, then alphabetical — same order the prototype had
  Object.keys(out).forEach(function (t) {
    Object.keys(out[t]).forEach(function (mk) {
      out[t][mk].sort(function (a, b) {
        var ay = Math.max.apply(null, a.y.length ? a.y : [0]);
        var by = Math.max.apply(null, b.y.length ? b.y : [0]);
        return by - ay || a.m.localeCompare(b.m);
      });
    });
    // and the makes themselves into selling order, here rather than in
    // renderMakes() so every reader of BOWS sees the same order
    out[t] = orderMakes(out[t]);
  });
  return out;
}

function shapeColours(rows) {
  return rows
    .slice()
    .sort(function (a, b) { return (parseInt(a.sort_order,10)||0) - (parseInt(b.sort_order,10)||0); })
    .map(function (r) {
      var two = (r.hex_secondary || "").trim();
      return {
        n:  (r.name || "").trim(),
        h:  two ? (r.hex_primary + "|" + two) : (r.hex_primary || "#555555"),
        sp: r.speckled === "true",
        mats: String(r.materials || "").split(",")
                .map(function (x) { return x.trim(); }).filter(Boolean)
      };
    })
    .filter(function (c) { return c.n; });
}

/* Not every material suits every bow. BCY lists 452Xtra and X-99 for compounds;
   8125 and B55 are the recurve and longbow materials. Driven by a `bow_types`
   field on the metaobject so adding a material is an admin job, not a code change.
   A material with no bow_types set is treated as valid everywhere, so an
   un-migrated store keeps working. */
function materialsFor(type) {
  var list = MATERIALS.filter(function (m) {
    return !m.bowTypes.length || m.bowTypes.indexOf(type) >= 0;
  });
  return list.length ? list : MATERIALS;
}

function shapeMaterials(rows) {
  return rows
    .slice()
    .sort(function (a, b) { return (parseInt(a.sort_order,10)||0) - (parseInt(b.sort_order,10)||0); })
    .map(function (r) {
      return {
        id: r.handle, name: (r.name || "").trim(), spec: (r.spec || "").trim(),
        blurb: (r.blurb || "").trim(), best: (r.best_for || "").trim(),
        speckleCapable: r.speckle_capable === "true",
        bowTypes: String(r.bow_types || "").split(",")
                    .map(function (x) { return x.trim(); }).filter(Boolean)
      };
    });
}

/* ============================================================
   COLOUR GATING
   Two separate rules, both BCY's, both driven by metaobject fields
   rather than a hard-coded list.

   1. Speckles. BCY's colour chart: "Multicolors (speckled) are only
      available in 452Xtra and 8125." Carried by `speckle_capable` on
      the material.

   2. Per-material colours. BCY do NOT restrict solids by material —
      "a range of colors is available for all materials" — with one
      exception. Natural is not a dye: the chart says "452Xtra and X-99
      untreated are 'off white' (Natural)", and BCY list B55 as
      available in every solid EXCEPT natural. So Natural belongs to
      452Xtra and X-99 only, and cannot exist in B55, Mercury 2 or DF97.
      Carried by `materials` on the colour.

   A colour with no `materials` set is allowed everywhere, matching how
   materialsFor treats a material with no bow_types. That matters: it
   means an un-migrated store shows its whole palette rather than an
   empty grid, which is the failure mode this gate could otherwise cause.
   ============================================================ */
function speckleOk(id) {
  var m = MATERIALS.find(function (x) { return x.id === id; });
  return m ? m.speckleCapable : false;
}
var SPECKLE_OK = { includes: function (id) { return speckleOk(id); } };
function materialOk(c) { return !c.mats.length || c.mats.indexOf(S.material) >= 0; }
function colourAllowed(c) { return (!c.sp || speckleOk(S.material)) && materialOk(c); }
function colourByName(n)  { return COLOURS.find(function (c) { return c.n === n; }); }

/* Some colours only make sense on a serving. Clear is the reason this exists:
   it is not a dye, it is unpigmented serving thread, so it can go on an end or
   centre serving but there is no such thing as a clear bowstring.

   This cannot ride on the `materials` field — that gates by material, and this
   gates by which tab you are on. It is a section setting rather than a new
   metaobject field because adding a field needs a definition change, and field
   keys are permanent once created. */
/* OUT OF STOCK, which is not the same thing as not offered. Quinn, 2026-09-17:
   "sunset orange is out of stock in both materials, x99 and 452xtra."

   A SECTION SETTING rather than a metaobject field, for the reason the file
   keeps running into: field keys are permanent once created, and what is on the
   shelf this month is the most changeable thing in the whole catalogue. Quinn
   can put a colour back from Customize with no push and no import.

   It gates the STRING rows only — main, second colour and pinstripe. Serving
   thread is a different BCY product line from string thread, which is already
   why servings never come speckled, so being out of 452Xtra and X99 says
   nothing about the serving spools. If a serving colour ever goes out too, that
   wants its own list rather than this one quietly covering both. */
var OUT_OF_STOCK = [];
function buildOutOfStock() {
  OUT_OF_STOCK = String(CFG.outOfStockColours || "").split(",")
    .map(function (x) { return x.trim(); }).filter(Boolean);
}
/* THE SERVING SPOOLS' OWN LIST - the one the comment above said would be needed,
   asked for by the builder on 2026-10-21: "86 black cherry serving ... I'm out."
   Serving thread is a different BCY product line, so this restricts the two
   serving rows and nothing else, exactly as `out_of_stock_colours` restricts the
   string rows and nothing else. Blank means unrestricted, which is how both rows
   behaved before this existed and is the safe state if the field is cleared. */
var SERVING_OUT = [];
function buildServingOut() {
  SERVING_OUT = String(CFG.servingOutOfStockColours || "").split(",")
    .map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
}
/* One gate for both kinds of thread, because every caller already asks this one
   question - the swatch grid, firstAllowed(), shareColourOk() and the update()
   guard. A second function would have to be added at all four and would be
   forgotten at one. The dimmed swatch's "- out of stock" is true either way. */
function inStock(c, id) {
  if (servingSurface(id)) {
    if (!SERVING_OUT.length) return true;
    return SERVING_OUT.indexOf(String(c.n).trim().toLowerCase()) < 0;
  }
  if (!OUT_OF_STOCK.length) return true;
  return OUT_OF_STOCK.indexOf(c.n) < 0;
}

/* WHAT EACH MATERIAL IS STOCKED IN — Quinn, 2026-09-26: "D97 I've got black,
   brown, flo orange and flo green, disable the other colours for now."

   `out_of_stock_colours` above could not express this: it is one list for the
   whole store, and being out of Sunset Orange in 452Xtra says nothing about
   DynaFLIGHT 97. This is PER MATERIAL, written as `handle: a, b, c` lines —
   the same shape `preselect_map` already uses, so there is one syntax in this
   section rather than two.

   A HANDLE WITH NO LINE IS UNRESTRICTED, which is how every material behaved
   before this existed and is the safe state if the field is ever cleared. So
   adding a material to the catalogue does not silently empty its grid.

   Matched lower-cased and trimmed for the reason the centre serving list is:
   this is an admin field typed by hand, and "Flo Green" is not what the
   catalogue calls Fluorescent Green. A name matching nothing is simply absent
   from the list, which is silent, so `debug` prints the misses. */
var MATERIAL_COLOURS = {};
function buildMaterialColours() {
  MATERIAL_COLOURS = {};
  String(CFG.materialColours || "").split(/[\r\n]+/).forEach(function (line) {
    var i = line.indexOf(":");
    if (i < 0) return;
    var handle = line.slice(0, i).trim().toLowerCase();
    var names = line.slice(i + 1).split(",")
      .map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
    if (handle && names.length) MATERIAL_COLOURS[handle] = names;
  });
}

/* The speckles a material is stocked in, same shape and same rules as the list above,
   read only for colours flagged speckled. See materialStocks() for why it is separate. */
var SPECKLED_COLOURS = {};
function buildSpeckledColours() {
  SPECKLED_COLOURS = {};
  String(CFG.speckledColours || "").split(/[\r\n]+/).forEach(function (line) {
    var i = line.indexOf(":");
    if (i < 0) return;
    var handle = line.slice(0, i).trim().toLowerCase();
    var names = line.slice(i + 1).split(",")
      .map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
    if (handle && names.length) SPECKLED_COLOURS[handle] = names;
  });
}

/* Is this colour stocked in the material currently chosen?

   THE SERVING ROWS ARE EXEMPT, and that is not a convenience — it is the rule
   Quinn confirmed on 2026-09-17 ("Serving is separate") and the same exemption
   `inStock()` carries. The serving thread is a different BCY product line from
   the string thread; a string material being down to four colours says nothing
   whatever about the serving spools, and dimming the end serving row because of
   it would be wrong twice over. */
function materialStocks(c, id) {
  if (servingSurface(id)) return true;
  var mat = String(S.material || "").toLowerCase();
  var name = String(c.n).trim().toLowerCase();
  var list = MATERIAL_COLOURS[mat];
  if (list && list.length && list.indexOf(name) < 0) return false;
  /* A SPECKLE CAN BE OUT WHILE THE SOLIDS ARE FINE, and that is the whole reason this
     is a second list rather than more names on the first. Quinn, 17 October 2026, gave
     the six speckles he has in 452Xtra; putting those on a material_colours line would
     have dimmed all 23 solids the queue is built from. Composes with the list above:
     either, both, or neither, and neither means unrestricted. */
  if (c.sp) {
    var sp = SPECKLED_COLOURS[mat];
    if (sp && sp.length && sp.indexOf(name) < 0) return false;
  }
  return true;
}

/* The material's own name, for the title on a dimmed swatch — "not stocked in
   BCY DynaFLIGHT 97" says why, where "out of stock" would be a different and
   untrue claim. Falls back to the handle if the material list has not loaded. */
function materialName(){
  var m = MATERIALS.find(function (x) { return x.id === S.material; });
  return (m && m.name) || S.material || "this material";
}

var SERVING_ONLY = [];
function buildServingOnly() {
  SERVING_ONLY = String(CFG.servingOnlyColours || "Clear").split(",")
    .map(function (x) { return x.trim(); }).filter(Boolean);
}
function isServingOnly(name) { return SERVING_ONLY.indexOf(name) >= 0; }

/* THE CENTRE SERVING IS RESTRICTED TO WHAT IS ON THE SHELF — Quinn, 2026-09-05.
   The serving thread is a different product line from the string, so the centre
   row offers nine colours rather than the whole palette. END SERVINGS ARE NOT
   RESTRICTED: he was asked directly and said centre only.

   Matched case-insensitively and trimmed, because this is a comma-separated
   admin field and "Flo Green" is not what the catalogue calls Fluorescent
   Green. A name that matches nothing is simply absent from the row, which is
   silent — so `debug` prints the misses rather than leaving someone to notice a
   colour is missing.

   BLANK MEANS NO RESTRICTION, which is how this row behaved before the setting
   existed and is the safe state if the field is ever cleared. */
var CENTRE_COLOURS = [];
function buildCentreColours() {
  CENTRE_COLOURS = String(CFG.centreServingColours || "")
    .split(",").map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
}
function centreAllows(c, id) {
  if (id !== "centre" || !CENTRE_COLOURS.length) return true;
  return CENTRE_COLOURS.indexOf(String(c.n).toLowerCase()) >= 0;
}
/* The first colour the centre row will actually show, for when the stored one
   stops being offered. Falls back to whatever is there rather than to null: an
   empty centre serving is not a state the slip can carry. */
function firstCentreColour() {
  var c = COLOURS.find(function (x) {
    return colourAllowed(x) && centreAllows(x, "centre") && inStock(x, "centre");
  });
  return c ? c.n : S.colours.centre;
}
/* The replacement for a serving colour that has gone out. Mirrors what the row
   actually draws (colourAllowed + tabTakes) and then the two gates, so it can
   never land on a swatch that is itself dimmed. Falls back to whatever is stored
   rather than to null: a blank serving is not a state the slip can carry. */
function firstServingColour(id) {
  var c = COLOURS.find(function (x) {
    return colourAllowed(x) && tabTakes(x, id) && inStock(x, id) && centreAllows(x, id);
  });
  return c ? c.n : S.colours[id];
}
/* The two serving rows. Named once because two rules key off it: Clear is
   offered ONLY here, and speckles are offered everywhere BUT here. */
function servingSurface(id) { return id === "serving" || id === "centre"; }
function tabTakes(c, tab) {
  return !isServingOnly(c.n) || tab === "serving" || tab === "centre";
}
/* Replacement for a colour the new material can't carry. Must itself pass the
   gate — COLOURS[0] is not safe to assume, since the first swatch could be the
   restricted one. */
function firstAllowed()   {
  /* Never falls back to a serving-only colour — this is what replaces a colour
     the new material cannot carry, and landing on Clear for a main colour would
     be worse than the problem it is solving. */
  var c = COLOURS.find(function (x) {
    return colourAllowed(x) && !isServingOnly(x.n) && inStock(x, "main")
        && materialStocks(x, "main");
  });
  return c ? c.n : (COLOURS[0]||{}).n;
}
/* IS THE THING BEING BOUGHT A STRING? True for any full set, and for a single
   piece only when that piece is the Bowstring itself.

   Everything gated on this is a fitting OF THE STRING and does not exist on a
   cable: a D-loop is tied to the string, a peep sits between its strands, speed
   nocks clamp to it, and the arrow nock question exists to size the CENTRE
   SERVING — which a cable does not have.

   Quinn reported it on 2026-08-15: buying a single cable or yoke still asked
   for all four. Item 28 of CLAUDE.md recorded the opposite — that singles "go
   through the whole process" on his own instruction of 2026-08-13 — so this is
   a deliberate reversal of that, not a bug that slipped through. What the
   original decision got right is that a single piece should still carry the
   full BUILD spec; what it got wrong is that it also carried fittings the part
   cannot physically take. */
function stringPiece() {
  return S.buy !== "single" || !S.piece || S.piece === "Bowstring";
}
function nocksIncluded()  { return S.type === "compound" && stringPiece(); }

/* D-loops and peeps are compound fittings, and step 7 offers neither on
   anything else. A recurve or longbow shoots off a nocking point rather than a
   loop, and neither a stickbow nor a crossbow takes a peep — a crossbow is
   aimed through a scope, and a peep needs a string that comes back to an
   anchor. Quinn, 2026-08-11.

   Offering them is not merely noise: a peep ticked on a crossbow order is a
   part in the parcel that cannot be fitted to the bow it arrived with.

   The nock question stays on every type. Crossbow bolts and arrows both have
   nocks, and the centre serving has to be sized to whichever it is. */
function accessoriesOffered() { return S.type === "compound" && stringPiece(); }

/* What the last canvas pass actually drew. Written by the renderer, read by the
   tests — the canvas cannot be inspected through the DOM the way a button can. */
var RENDER_INFO = { speedNock: false, gpu: false, gpuError: null };

/* Canada: free over $50, $10 flat under it. Lower 48: $20 flat.
   Named rather than written into the sentence, because the same numbers appear
   on the Materials page and in the announcement bar and the three drifted apart
   once already — the builder was promising free shipping anywhere in Canada
   with no threshold at all, which is not what the store charges. Confirmed
   against Settings > Shipping by Quinn, 2026-08-10.

   Compared against the subtotal, which is what this builder totals and what
   Shopify's own condition tests. Prices here are tax-exclusive. */
var FREE_SHIP_OVER = 50;
var SHIP_FLAT_CA   = 10;
var SHIP_FLAT_US   = 20;

/* ============================================================
   STATE
   ============================================================ */
var S = {
  type:null, make:null, bow:null, year:null, amo:"", amoOther:false,
  /* Full set, or one string/cable. Compound and crossbow only — a recurve or
     longbow string IS the single item, so the question has one answer there and
     is not asked. `piece` is a part name straight out of the bow's own
     piece_breakdown, so the list can only ever offer parts that bow actually
     has: no yoke cable for a bow with no yokes. */
  buy:"set", piece:null,
  /* Whether that question has been ANSWERED, by a tap or by a link that made
     the choice. `buy` keeps "set" as its default so pricing and every reader of
     it are unchanged; this only decides whether a row is drawn ticked. Until
     2026-10 "Full set" arrived ticked and the builder still waited for it to be
     tapped: the one place in steps 1 to 4 that looked finished and was not. */
  buyPicked:false,
  /* THE STRING THE PREVIEW OPENS ON CAN BE A SHOWPIECE, AND A SHOWPIECE IS NOT AN ANSWER.
     Quinn, 10 October 2026: Gunmetal and Mountain Berry with a Natural pinstripe, which
     shows the second colour and the pinstripe (both included) where a plain red string
     showed neither. `colourPicked` is what stops it being BUILT by accident: while a
     showpiece is set, missingStep() holds the order until a main colour has been tapped.
     No showpiece (the setting blank) is the builder as it always was: Red, and no gate. */
  colourPicked: !SHOWPIECE,
  colours:{ main: SHOWPIECE ? SHOWPIECE.main : "Red", second: SHOWPIECE ? SHOWPIECE.second : null,
            serving:"Black", centre:"Black", pinstripe: SHOWPIECE ? SHOWPIECE.pin : null, shrink:null },
  activeTab:"main",
  material:"452xtra",
  /* loopRope defaults to #24: it is the thicker standard, and #23 is the one
     that can pull through a Carter release, so the safe rope is the one you
     get without reading anything.

     THE BADGE AND THE DEFAULT ARE DELIBERATELY ALLOWED TO DISAGREE. Quinn put
     "Our pick" on #23 on 2026-08-14 (the `rope_pick` setting); the default here
     stays #24. A recommendation is an argument the customer can read and take,
     a default is what happens to someone who reads nothing — and the rope that
     cannot fail is the right thing to give the second person. Change this
     string, not the setting, if the default should follow the badge. */
  opts:{ pin: !!(SHOWPIECE && SHOWPIECE.pin), peep:false, rush:false, loop:0, loopRope:"24", loopColour:null },
  nock:null,           // set from the section default once config is read
  nockOther:"",        // free text, only when the nock is not on the list
  zoom:1               // wheel zoom on the preview; 1 is the default framing
};

/* How far the wheel can take the preview. The floor is set by the renderer, not
   by taste: below about 0.6 the filaments fall under a pixel wide and the twist
   stops resolving into anything. */
var ZOOM_MIN = 0.5, ZOOM_MAX = 2.6;

var $ = function (id) { return document.getElementById(id); };
var money = function (n) { return "$" + Number(n).toFixed(2); };
const TYPES = [
  {id:"compound",name:"Compound bow",note:"Hunting & target"},
  {id:"crossbow",name:"Crossbow",note:"String & cables"},
  {id:"trad",name:"Recurve / Longbow",note:"Barebow, Olympic & traditional"}
];
/* THE STARTING PRICE BESIDE EACH BOW TYPE. Conversion audit, 2026-10-08: the
   builder's first screen showed no price at all and the first one was three
   answers in. It is READ FROM THE VARIANTS buildPricing() already holds, never
   typed here, so a price moved in Shopify moves this line with no push.

   "From" is the cheapest FULL SET for the type (compound: the three piece
   tiers; crossbow: its two sets). A recurve or longbow string is one item at
   one price, so it states the price with no "from". Singles are left out on
   purpose: one yoke cable is not what a set starts at.

   A type whose price did not arrive (product unpublished, SKU renamed) returns
   "" and the row keeps its hint alone, which is how it read before this. */
function typeFrom(id) {
  var packs = id === "compound" ? COMPOUND_PACKAGES
            : id === "crossbow" ? [CROSSBOW_PACKAGE, CROSSBOW_MULTI]
            : id === "trad"     ? [TRAD_PACKAGE]
            : [];
  var low = null;
  packs.forEach(function (p) {
    var n = p ? Number(p.price) : NaN;
    if (n > 0 && (low === null || n < low)) low = n;
  });
  if (low === null) return "";
  return (id === "trad" ? "" : "full sets from ") + money(low);
}
/* ============================================================
   STEP PLUMBING
   ============================================================ */
const NSTEPS = 7;
/* The furthest step a click may open. Everything up to and including the first
   unanswered step is reachable; past that is locked.

   Derived from missingStep() rather than from its own rule. Two definitions of
   "what is unanswered" would drift, and the drift shows as a step that opens
   when it should not, or add-to-cart refusing on a build that looks finished. */
function maxOpenStep(){
  const m = missingStep();
  return m ? m.n : NSTEPS;
}

function toggleStep(n){
  /* Forward only. A completed step stays clickable for ever — locking those
     would mean someone changing their mind about material has to start again,
     which is worse than the problem this solves. */
  if(n > maxOpenStep()){
    const m = missingStep();
    if(m) needStep(m);
    return;
  }
  const el = $("s"+n);
  const wasOpen = el.classList.contains("open");
  for(let i=1;i<=NSTEPS;i++){ $("s"+i).classList.remove("open"); $("s"+i).classList.remove("active"); }
  if(!wasOpen){ el.classList.add("open"); el.classList.add("active"); }
  panelRoom();
}
/* ROOM FOR THE BUILD PANEL BESIDE THE OPEN STEP (desktop). The panel is sticky,
   and a sticky box can only stay put while its row is tall enough to hold it:
   the row is as tall as the taller of the steps and the panel. Opening a late
   step scrolls that step to the top, and when the steps below it are shorter
   than the panel the row ends first, so the panel is carried up under the site
   header with its heading, Share button and string preview hidden. Found on the
   last step once the promises went in under Add to cart (pre-push review,
   8 October 2026); a crossbow's and a recurve's last step had always done it.

   So the steps column is given the height the panel needs from the open step
   down, and no more. The 8px is the difference between where the step stops
   (scroll-margin-top 100px) and where the panel sticks (92px). Phones and
   tablets have a static panel and get nothing. */
function panelRoom(){
  const steps = document.querySelector(".rc-builder .steps");
  const sum = document.querySelector(".rc-builder .summary");
  if(!steps || !sum) return;
  const open = steps.querySelector(".step.open");
  if(!open || getComputedStyle(sum).position !== "sticky"){ steps.style.minHeight = ""; return; }
  /* Inside Colours the builder scrolls a colour ROW to the top, not the step,
     so the room is measured from the open row; with every row shut, from the
     last one, which is the lowest a row's name can be left at the top. */
  const heads = open.querySelectorAll(".shead");
  const row = open.querySelector(".srow.open .shead") || heads[heads.length - 1];
  const from = (row || open).getBoundingClientRect().top - steps.getBoundingClientRect().top;
  steps.style.minHeight = Math.ceil(from + sum.offsetHeight - 8) + "px";
}
function goStep(n){
  for(let i=1;i<=NSTEPS;i++){ $("s"+i).classList.remove("open"); $("s"+i).classList.remove("active"); }
  $("s"+n).classList.add("open"); $("s"+n).classList.add("active");
  panelRoom();
  /* Scroll on every width, not just under 960. Answering a step opens the next
     one below the fold, and the desktop layout left you to find it yourself —
     while needStep() scrolled, so the builder moved for you when you got it
     wrong and not when you got it right.

     `start`, not `center`: the colours step is taller than the viewport and
     centring it puts its own heading off-screen. The sticky header is cleared
     by scroll-margin-top in the CSS rather than by an offset here. */
  $("s"+n).scrollIntoView({ behavior: "smooth", block: "start" });
}

/* What is stopping the order, if anything. Returns the step to send someone to
   and what to say when they get there, or null when the build can be added.

   Kept as one function because two places need the same answer: the button's
   appearance, and what happens when it is clicked. Two copies of this rule
   would drift, and the drift would show as a button that looks ready and then
   refuses. */
function missingStep() {
  if (!S.type) return { n: 1, msg: "Start here — what are you shooting?" };
  /* Set or one piece is a question, and since "Full set" stopped being ticked
     for the customer it can be walked past: the Make header opened, the build
     priced as a full set, and step 1 went on showing no answer. Asked for here
     so the line under Add to cart names it and Make waits for it. Only while
     there is no make: a link that arrives with a bow has already answered. */
  if (singlesOffered() && !S.buyPicked && !S.make) {
    return { n: 1, msg: "Do you need the whole set, or one piece?" };
  }
  if (!S.make) return { n: 2, msg: "Choose the make of your bow." };
  if (!S.bow) {
    /* The not-listed panel has no list to choose from: say what it asks. */
    var nl = $("modelList");
    if (nl && nl.classList.contains("nolist")) {
      return { n: 3, msg: document.querySelector("#nlPanel .tiles") ? "Pick what your bow takes." : "Tell us about your bow." };
    }
    return { n: 3, msg: "Choose your model." };
  }
  /* A not-listed bow has to be NAMED, make and model both, before anything
     else opens. Order #1019 (2026-09-05) reached the bench reading just
     "Bear": the fields sat below the configuration tiles, a tile click jumped
     to step 5 and collapsed them, and nothing ever asked again. The bench
     cannot check a set against a bow it cannot identify, and the check is the
     whole safeguard on this path. In the gate rather than in chooseConfig()
     so add-to-cart, the step locks and the button all refuse for the same
     reason — Quinn, 2026-09-08. */
  if (S.bow.custom && !customBowNamed()) {
    return { n: 3, msg: "Tell us the make and model of your bow. We check the set against it before we cut anything, and we cannot check a bow we cannot name." };
  }
  if (S.type !== "trad" && S.bow.y.length > 1 && !S.year) {
    /* The year never changes what is cut: one catalogue entry covers several
       years precisely because the string spec is the same across them. It is
       asked so the right year prints on the build sheet. The message used to
       say the lengths differ by year, which is not true of any listing. */
    return { n: 4, msg: "Pick your model year. It goes on your build sheet so we can identify the bow." };
  }
  if (S.type === "trad" && !S.amo.trim()) {
    return { n: 4, msg: "Enter your AMO string length — we cannot cut a recurve or longbow string without it." };
  }
  /* Buying one piece and not saying which is the one way this build can be
     unfinished without any step looking empty — the bow is chosen, the colours
     have defaults, and only the part is missing. */
  if (S.buy === "single" && singlesOffered() && !S.piece) {
    return { n: 4, msg: "Which piece do you need? Pick the string or cable you're replacing." };
  }
  /* The nock has to be chosen, not inherited. It used to be preselected so the
     question could never cost an order — that was a build-time decision, and
     Quinn reversed it on 2026-08-10: an answer nobody made is not an answer,
     and the bench was getting "not sure" from people who had never seen the
     question. "Not sure" is still a perfectly good answer; it just has to be
     picked. */
  /* Only when the question is actually on screen. Gating the block without
     gating this would refuse Add to cart and send the customer to step 7 to
     answer something that is not there — a dead end with no way out. */
  /* THE SHOWPIECE NO LONGER HOLDS THE ORDER. 10-14 October 2026 it did: a customer
     who had not tapped a main colour was refused at Add to cart and sent back to step
     6. It was put there so nobody bought Gunmetal and Mountain Berry by accident, which
     is a real risk and still is - but a block at the buy button is the most expensive
     place in the builder to put a correction, and 73% of this store's traffic arrives
     on a page it never sees. The same job is now done by showpieceNote(), which says
     the string is an example from the moment step 6 opens, where it costs nothing.
     S.colourPicked is still kept: update() reads it for the recurve pinstripe. */
  if (nockAsked() && !S.nock) {
    return { n: 7, msg: "Which nock do you shoot? Pick \"Not sure\" if you do not know and we serve the centre to the standard .107\"." };
  }
  return null;
}

/* Sends someone to the step that is stopping them and says why, rather than
   leaving a dead button to be interpreted. Replaces `disabled`, which gave no
   reason and was unreachable by keyboard besides. */
function needStep(miss) {
  goStep(miss.n);
  const el = $("s" + miss.n);
  let note = el.querySelector(".rc-need");
  if (!note) {
    note = document.createElement("div");
    note.className = "rc-need";
    el.querySelector(".stepbody").prepend(note);
  }
  note.textContent = miss.msg;
  /* goStep() above already scrolled. A second scrollIntoView here fought it —
     two smooth scrolls to different alignments in the same frame. */
  el.classList.add("flash");
  setTimeout(function () { el.classList.remove("flash"); }, 1400);
  /* The AMO is the only answer on a trad build that nobody but the customer can
     supply, so put the cursor on it — after the scroll, or the browser fights
     the smooth scroll. */
  if (miss.n === 4 && S.type === "trad") {
    setTimeout(focusAmo, 520);
  }
}

/* The same reason, cut down to one short line for under Add to cart: "One thing
   left: which nock do you shoot?". The button is full red whether or not the
   build is finished, so the line is what says a click will take you somewhere
   rather than buy something.

   DERIVED from the sentence missingStep() already holds, not kept as a second
   list: a second list would drift, and the drift would show as the line under
   the button naming one thing and the prompt in the step naming another. It is
   the FIRST sentence of that message. Where the sentence carries a dash, a
   question keeps what follows it ("Start here — what are you shooting?") and a
   statement keeps what comes before ("Enter your AMO string length — we
   cannot…"), which also keeps the dash itself out of the line. A reason that
   reads badly cut this way can carry its own `left` and that wins. */
function leftReason(miss) {
  if (miss.left) return miss.left;
  var s = String(miss.msg || "");
  var first = s.match(/^[\s\S]*?[.?!](?=\s|$)/);
  if (first) s = first[0];
  var parts = s.split(" — ");
  if (parts.length > 1) {
    s = /\?$/.test(s) ? parts[parts.length - 1] : parts[0] + ".";
  }
  /* Mid-sentence now, so the capital goes; an initialism ("AMO") keeps its own. */
  if (/^[A-Z][a-z]/.test(s)) s = s.charAt(0).toLowerCase() + s.slice(1);
  return s;
}

/* The dropdown normally, the text box when "Other" has opened it. One place,
   because there are two callers and they must not disagree about which field
   the customer is being sent to. */
function focusAmo() {
  const i = $("amoInput");
  if (i && !i.hidden) { i.focus(); return; }
  const s = $("amoSelect");
  if (s) s.focus();
}

/* ============================================================
   STEP 1 — TYPE
   ============================================================ */
function renderTypes(){
  $("typeTiles").innerHTML = TYPES.map(t => {
    const from = typeFrom(t.id);
    return `<div class="tile ${S.type===t.id?"sel":""}" onclick="RC.pickType('${t.id}')">${t.name}<small>${t.note}${from ? ` · <span class="tfrom">${from}</span>` : ""}</small></div>`;
  }).join("");
}
/* Full set or one piece. Two tiles rather than a dropdown so it reads as the
   same kind of question as the bow type directly above it. */
const BUY_MODES = [
  {id:"set",    name:"Full set",              note:"Everything your bow takes"},
  {id:"single", name:"Individual string/cable", note:"Replace one piece"}
];
function renderBuy(){
  var box = $("buyTiles");
  if(!box) return;
  /* Neither row is ticked until one has been chosen (S.buyPicked). The default
     behind them is still "set"; it is just not drawn as an answer. */
  box.innerHTML = BUY_MODES.map(function(b){
    return '<div class="tile ' + (S.buyPicked && S.buy===b.id?"sel":"") + '" onclick="RC.pickBuy(\'' + b.id + '\')">'
         + b.name + '<small>' + b.note + '</small></div>';
  }).join("");
}
/* What the closed step 1 says: the bow type, and once the second question is
   answered, which way it went ("Compound bow · Full set"). A single-piece buyer
   could not see their mode anywhere once the step had closed.

   HTML, not text, for one reason: on a phone the header has room for about
   fourteen characters beside the title, so this wraps, and the span keeps
   "One piece" whole so the break falls after the dot instead of leaving
   "piece" on a line of its own. The text itself is plain, ordinary spaces. */
function step1Label(){
  var t = TYPES.find(function(x){ return x.id === S.type; });
  if(!t) return "";
  if(!singlesOffered() || !S.buyPicked) return esc(t.name);
  return esc(t.name) + ' · <span class="stepmode">'
       + (S.buy === "single" ? "One piece" : "Full set") + '</span>';
}
function pickBuy(id){
  /* ALWAYS ADVANCE, even when the mode has not changed. "Full set" is the
     default, so an early return here meant clicking it did nothing at all —
     the one answer most people will give, and the builder sat still for it.
     Only the reset work is conditional. */
  S.buyPicked = true;
  if(S.buy !== id){
    S.buy = id;
    /* Changing your mind clears the part. Leaving a stale piece selected is how
       a "full set" order would carry a single cable's variant. */
    S.piece = null;
    renderBuy(); renderSpec(); update();
  }
  /* The tick and the header follow the tap even when the mode did not change,
     which is every "Full set" answer. */
  renderBuy();
  $("v1").innerHTML = step1Label();
  /* The answer is what unlocks Make, and a tap on the mode that was already the
     default changes nothing else, so the locks and the line under Add to cart
     have to be redrawn here. */
  update();
  goStep(2);
}
/* The part list depends on the bow, so it lives with the spec in step 4 rather
   than in step 1 — there is nothing to choose from until a bow is known. */
function pickPiece(part){
  S.piece = part;
  var v = singleFor(part);
  $("v4").textContent = part + (v ? " · " + money(v.price) : "");
  $("s4").classList.add("done");
  renderSpec(); update();
  goStep(5);
}

function pickType(id){
  if(S.type !== id){ S.make=null; S.bow=null; S.year=null; $("modelSearch").value=""; }
  S.type = id;
  /* A trad string is already a single item, so the question is not asked and the
     mode must not survive a switch out of compound or crossbow. */
  if(!singlesOffered()){ S.buy = "set"; S.buyPicked = false; }
  S.piece = null;
  /* A compound material can't carry over to a longbow. Fall to the first valid
     one and re-run the colour gate, since both speckle availability and the
     per-material colour list move with it. */
  var valid = materialsFor(id);
  if(!valid.some(function(m){ return m.id === S.material; })){
    S.material = valid[0].id;
    ["main","second","serving","centre","pinstripe"].forEach(function(k){
      var c = S.colours[k] && colourByName(S.colours[k]);
      if(c && !colourAllowed(c)){
        S.colours[k] = (k === "second" || k === "pinstripe") ? null : firstAllowed();
      }
    });
    $("v5").textContent = (MATERIALS.find(function(m){ return m.id === S.material; })||{}).name || "";
  }
  $("v1").innerHTML = step1Label();
  $("s1").classList.add("done");
  $("s3").classList.remove("done"); $("s4").classList.remove("done");
  $("v3").textContent=""; $("v4").textContent="";
  renderTypes(); renderBuy(); renderMakes(); renderModels(); renderYears(); renderMats(); update();
  /* Compound and crossbow now have a second question on this step, so picking a
     type must NOT jump to step 2 — doing so collapses step 1 and the question is
     never seen. pickBuy() advances instead. Trad has no such question and goes
     straight on as it always did. */
  if(!singlesOffered()){ goStep(2); return; }

  /* BUT IT STILL HAS TO GO TO THE QUESTION. Quinn, 2026-08-17: on a phone,
     picking Compound left the screen exactly where it was and the Full set /
     Individual tiles appeared below the fold, so the builder looked like it had
     done nothing and the customer had to find the next question by scrolling.

     Every other forward move in the flow scrolls — goStep() on a step,
     advanceAfterColour() on a colour row — and this was the one place that
     revealed something new and left you to hunt for it.

     `nearest`, matching advanceAfterColour(): on a desktop the tiles are already
     on screen and the page must not jump, which is why this is not scrollIntoView
     with `start`. */
  var buy = $("buyMode");
  if (buy && !buy.hidden) buy.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

/* ============================================================
   STEP 2 — MAKE
   ============================================================ */
function renderMakes(){
  if(!S.type){ $("makeTiles").innerHTML = '<p class="hint">Pick a bow type first.</p>'; return; }
  const makes = Object.keys(BOWS[S.type]);
  /* "Not listed" is a full-width row ABOVE the makes, not the last tile —
     Quinn, 2026-09-09: on a phone it sat at the bottom of a long grid of
     makes, exactly where the model list's escape hatch used to be before it
     was moved to the top for the same reason. Same class as the model list's
     row and, like it, deliberately NOT a `.tile`: four suites pick a make with
     `#makeTiles .tile` .first, and a hatch that answered to that selector
     would turn them into tests of the not-listed panel. */
  $("makeTiles").innerHTML =
    `<div class="nlrow" onclick="RC.notListed()"><b>My bow is not listed</b><em>Tell us your bow →</em></div>`
    + makes.map(m =>
    `<div class="tile ${S.make===m?"sel":""}" onclick="RC.pickMake('${m.replace(/'/g,"\\'")}')">${m}<small>${BOWS[S.type][m].length} model${BOWS[S.type][m].length>1?"s":""}</small></div>`
  ).join("");
}
function pickMake(m){
  if(S.make !== m){ S.bow=null; S.year=null; $("modelSearch").value=""; }
  S.make = m;
  $("v2").textContent = m;
  $("s2").classList.add("done");
  $("s3").classList.remove("done"); $("s4").classList.remove("done");
  $("v3").textContent=""; $("v4").textContent="";
  renderMakes(); renderModels(); renderYears(); update();
  goStep(3);
}
/* Reached from the "Not listed" tile and from the no-search-results link.
   Renders in place rather than firing an alert — a bow we don't carry is a lead,
   not an error, and it should be as easy to send as picking one off the list. */
function notListed(keepMake){
  /* The mailto and the contact-page link are gone. A mailto depends on the
     visitor having a mail client wired up, which on a phone is a coin toss, and
     the cam photo it used to ask for is not needed \u2014 Quinn, 2026-08-10. The
     Shopify contact form in the section markup replaces both. */

  /* "Not listed" IS an answer to step 2. Without this S.make stays null, the
     step gate reports step 2 as unfinished, and step 3 renders at the locked
     opacity while being open \u2014 it looked disabled while working perfectly. */
  /* Reached from STEP 3 the make is already answered and has to SURVIVE.
     Someone who picked Hoyt and cannot find their Powermax has told us "Hoyt";
     blanking step 2 would throw that away, make them retype it in the form
     below, and lose the single most useful field on the lead. Only the step-2
     tile — which calls this with no argument — resets it. */
  if(!(keepMake && S.make && S.make !== "Not listed")){
    S.make = "Not listed";
  }
  $("v2").textContent = S.make;
  $("s2").classList.add("done");
  S.bow = null; S.year = null;

  var box = $("modelList");
  if(box){
    box.classList.add("nolist");   // this panel is a form, not a list to scroll
    box.innerHTML =
      '<div class="noresult" style="text-align:left">'
      + '<b>Can\'t find your bow?</b>'
      + '<p style="margin:8px 0 14px">We build for plenty of bows that are not on this list. '
      + 'If you know what your bow takes you can order right now; if not, tell us and we will '
      + 'come back with the spec and a price. It gets added to the list while we are at it.</p>'
      + '<div class="rc-nl-ask"><b>Do you know what set you need?</b></div>'
      + '<div class="rc-nl-yn">'
      +   '<button class="tile" onclick="RC.notListedPath(true)">Yes'
      +     '<small>Pick it and carry on</small></button>'
      +   '<button class="tile" onclick="RC.notListedPath(false)">No'
      +     '<small>Send it to us instead</small></button>'
      + '</div>'
      + '<div id="nlPanel"></div>'
      + '</div>';
  }
  var sp = $("specBox"); if(sp) sp.innerHTML = "";
  var f = $("notListedForm"); if(f) f.hidden = true;
  renderModelCount(false); // the panel is not a list, so the count line goes quiet
  /* update() is what recomputes the step locks. Setting S.make above without it
     left step 3 open and still wearing the locked styling. */
  update();
  goStep(3);
}

/* THE CONFIRMATION WAS INVISIBLE, and had been since the form shipped.
   Shopify reloads the page with ?contact_posted=true and Liquid renders the
   thank-you INSIDE #notListedForm — which boots `hidden`, because JS is what
   reveals it. So a customer told us about their bow, pressed Send, and landed
   back on step 1 with no acknowledgement of any kind. The reasonable readings
   are "it failed" and "they ignored me", and both cost the lead the form exists
   to capture.

   It is LIFTED to the top of the builder rather than un-hiding the form, on
   purpose. After the reload nothing is answered, so step 3 is gated shut and
   `goStep(3)` would either refuse or open a step wearing its locked styling.
   Moving the one element out of the step machinery sidesteps all of that and
   cannot be re-broken by a change to the gate.

   Detected by the element Liquid rendered, not by the query string: that is the
   same signal `form.posted_successfully?` used, and it cannot drift from it. */
function showContactPosted(){
  var ok = document.querySelector("#notListedForm .rc-nl-ok");
  var main = $("rcMain");
  if(!ok || !main) return;
  var banner = document.createElement("div");
  banner.className = "rc-nl-posted";
  banner.appendChild(ok);              // MOVE it: a clone would leave a hidden twin
  main.insertBefore(banner, main.firstChild);
  banner.scrollIntoView({ behavior: "smooth", block: "center" });
}

/* What a bow takes, said the way someone can check by looking at it rather than
   by knowing a piece count. Each maps to the same `p` array a catalogue bow
   carries, so pkgFor(), the spec block, pricing and the packing slip all work
   on a not-listed bow with no special cases. */
var CONFIGS = {
  compound: [
    { label: "Bowstring and one cable", note: "Older single-cam bows",
      p: ["Bowstring", "Cable"] },
    { label: "Bowstring and two cables", note: "Most modern compounds",
      p: ["Bowstring", "Buss cable", "Control cable"] },
    { label: "Bowstring, two cables and two yokes", note: "Mathews ARC and Lift, most twin-cam Prime",
      p: ["Bowstring", "Buss cable", "Control cable", "Yoke", "Yoke"] },
    { label: "Six pieces or more", note: "Anything with more than two yokes",
      p: ["Bowstring", "Buss cable", "Control cable", "Yoke", "Yoke", "Yoke"] }
  ],
  crossbow: [
    { label: "One string, no cables", note: "Recurve crossbows such as Excalibur",
      p: ["Bowstring"] },
    { label: "String and two cables", note: "Most compound crossbows",
      p: ["Bowstring", "Cable", "Cable"] },
    { label: "String and four cables or more", note: "Helical-cable sets",
      p: ["Bowstring", "Cable", "Cable", "Cable", "Cable"] }
  ],
  trad: [
    { label: "Recurve or longbow string", note: "Built to the AMO length you give us",
      p: ["Bowstring"] }
  ]
};

/* PHOTO UPLOADS ON THE LEAD FORM.
   Shopify's contact form cannot carry attachments, so the file never travels
   with the form at all. It goes to our own Worker (workers/bow-photos), and
   what rides on the form is the URL it returns, in a hidden text field the
   contact form CAN carry. docs/decisions.md has the reasoning.

   EVERY PATH HERE DEGRADES TO A WORKING FORM. No endpoint configured, Worker
   unreachable, upload refused, browser too old for FormData -- in each case the
   photo control is absent or the row says what went wrong and names the email
   address instead, and the lead still sends. Photos are a convenience on top of
   a form that already works; they are never a precondition for it. */
var PHOTO_MAX   = 4;
var PHOTO_BYTES = 8 * 1024 * 1024;
var photoUrls   = [];

function syncPhotoField(){
  var f = $("rcPhotoUrls");
  if (f) f.value = photoUrls.join("\n");
}

function removePhoto(i){
  photoUrls.splice(i, 1);
  syncPhotoField();
  var row = document.querySelector('[data-photo="' + i + '"]');
  if (row && row.parentNode) row.parentNode.removeChild(row);
  /* Re-index what is left. The indices are the array positions, so leaving a
     hole would make the next Remove take the wrong photo off. */
  var rows = document.querySelectorAll("#rcPhotoList .rc-photo");
  for (var j = 0; j < rows.length; j++) rows[j].setAttribute("data-photo", j);
}

function photoRow(file){
  var list = $("rcPhotoList");
  if (!list) return null;
  var row = document.createElement("div");
  row.className = "rc-photo";
  var thumb = document.createElement("span");
  thumb.className = "rc-photo-thumb";
  /* A local preview, so it appears the instant the file is chosen rather than
     after a round trip. HEIC does not decode in most browsers -- the onerror
     drops back to a plain chip rather than leaving a broken image. */
  try {
    var img = document.createElement("img");
    img.src = URL.createObjectURL(file);
    img.alt = "";
    img.onerror = function(){ thumb.classList.add("noimg"); img.remove(); };
    thumb.appendChild(img);
  } catch (e) { thumb.classList.add("noimg"); }
  var name = document.createElement("b");
  name.textContent = file.name || "photo";
  var stat = document.createElement("em");
  stat.textContent = "Uploading...";
  row.appendChild(thumb); row.appendChild(name); row.appendChild(stat);
  list.appendChild(row);
  return { row: row, stat: stat };
}

function uploadPhoto(file){
  var ui = photoRow(file);
  if (!ui) return;
  if (file.size > PHOTO_BYTES) {
    ui.stat.textContent = "Over 8 MB - email this one to orders@rivercitystrings.ca";
    ui.row.classList.add("bad");
    return;
  }
  fetch(CFG.photoUploadUrl, {
    method: "POST",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" }
  }).then(function(r){
    return r.json().then(function(j){ return { ok: r.ok, body: j }; });
  }).then(function(res){
    if (!res.ok || !res.body || !res.body.url) {
      throw new Error((res.body && res.body.error) || "Upload failed");
    }
    photoUrls.push(res.body.url);
    syncPhotoField();
    ui.row.setAttribute("data-photo", String(photoUrls.length - 1));
    ui.row.classList.add("done");
    ui.stat.innerHTML = "";
    var btn = document.createElement("button");
    btn.type = "button";               // inside a form: a bare button would SUBMIT it
    btn.className = "rc-photo-x";
    btn.textContent = "Remove";
    btn.onclick = function(){ removePhoto(parseInt(ui.row.getAttribute("data-photo"), 10)); };
    ui.stat.appendChild(btn);
  }).catch(function(e){
    ui.row.classList.add("bad");
    ui.stat.textContent = (e && e.message ? e.message : "Upload failed")
      + " - you can email it to orders@rivercitystrings.ca instead";
  });
}

function bindPhotoUpload(){
  var input = $("rcPhotoInput");
  if (!input || !CFG.photoUploadUrl || !window.fetch) return;
  input.addEventListener("change", function(){
    var files = Array.prototype.slice.call(input.files || []);
    /* Clearing the input matters: without it, picking the SAME file again fires
       no change event and looks like the control has stopped working. */
    input.value = "";
    var room = PHOTO_MAX - photoUrls.length;
    if (room <= 0) return;
    files.slice(0, room).forEach(uploadPhoto);
  });
}

function notListedPath(knows){
  var panel = $("nlPanel");
  var form = $("notListedForm");
  if(!panel) return;

  if(!knows){
    panel.innerHTML = "";
    if(form){
      /* Same courtesy the order-now path gets: a make already answered on
         step 2 is filled in rather than asked for again. Only into an empty
         box — a form half-typed and then reopened keeps what was typed. */
      var mkIn = form.querySelector('input[name="contact[Make]"]');
      if (mkIn && !mkIn.value && S.make && S.make !== "Not listed") mkIn.value = S.make;
      form.hidden = false;
      form.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return;
  }

  if(form) form.hidden = true;
  var list = CONFIGS[S.type] || CONFIGS.compound;
  /* THE BOW COMES FIRST, THE TILES SECOND. The tiles used to sit on top and a
     tile click navigated to step 5, taking the Year / Make / Model fields off
     screen before anyone had reached them — which is how order #1019 arrived
     reading just "Bear". Ask for the bow before offering the thing that leaves
     the step. Make and model are required (missingStep() enforces it); the
     rest is optional, but each item shortens the check the bench has to do
     before cutting, so it is asked for rather than left to the notes box. */
  var c = S.custom || {};
  var mk = nlKnownMake() || esc(c.make || "");
  var inp = function (id, key, extra) {
    return '<input type="text" id="' + id + '" value="' + esc(c[key] || "")
         + '" oninput="RC.setCustomBow()"' + (extra || "") + '>';
  };
  panel.innerHTML =
      '<div class="rc-nl-ask" style="margin-top:16px"><b>Which bow is it?</b>'
    + '<em> — we check the set against it before we cut anything</em></div>'
    + '<div class="rc-nl-grid">'
    +   '<label>Make <i>required</i><input type="text" id="nlMake" value="' + mk
    +     '" oninput="RC.setCustomBow()" autocomplete="off"></label>'
    +   '<label>Model <i>required</i>' + inp("nlModel", "model", ' autocomplete="off"') + '</label>'
    +   '<label>Year' + inp("nlYear", "year", ' inputmode="numeric" autocomplete="off"') + '</label>'
    + '</div>'
    + '<div class="rc-nl-ask"><b>Anything else you know about it?</b>'
    + '<em> — all optional; any of it makes the check quicker</em></div>'
    + '<div class="rc-nl-grid">'
    +   '<label>String length' + inp("nlString", "string", ' placeholder="e.g. 58 3/4&quot;"') + '</label>'
    +   '<label>Cable length(s)' + inp("nlCable", "cable", ' placeholder="e.g. 35 1/2&quot; ×2"') + '</label>'
    +   '<label>Serial number' + inp("nlSerial", "serial", ' autocomplete="off"') + '</label>'
    + '</div>'
    + '<label class="rc-nl-wide">Notes on the bow'
    +   '<textarea id="nlNotes" rows="2" oninput="RC.setCustomBow()" '
    +   'placeholder="Where the lengths came from (limb sticker, manual, old string), cam names, anything unusual">'
    +   esc(c.notes || "") + '</textarea></label>'
    + '<div class="rc-nl-ask" style="margin-top:16px"><b>What does your bow take?</b>'
    + '<em> — count the string and every cable on it</em></div>'
    + '<div class="tiles">'
    + list.map(function (cfg, i) {
        var sel = S.bow && S.bow.custom && S.bow.cfg === cfg.label ? " sel" : "";
        return '<div class="tile' + sel + '" onclick="RC.chooseConfig(' + i + ')">' + cfg.label
             + '<small>' + cfg.note + '</small></div>';
      }).join("")
    + '</div>'
    + '<button type="button" class="btn sm rc-nl-go" id="nlContinue" '
    +   'onclick="RC.customContinue()">Continue →</button>';
  /* Capture the prefilled make immediately. setCustomBow() only fires on input,
     so without this a customer who never touches the Make box would send a lead
     with the field visibly reading "Hoyt" and S.custom.make empty. */
  setCustomBow();
  /* YES HAS TO SHOW WHAT IT OPENED. With the two answers stacked as buttons on
     a phone, the fields open below the bottom of a short screen (360 x 640)
     and the tap looked like it had done nothing. `nearest`, like the Full set
     question in pickType(): nothing moves when the fields are already in view. */
  var firstFields = panel.querySelector(".rc-nl-grid");
  if (firstFields) firstFields.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

/* A not-listed bow in the customer's own words, for the summary panel and the
   price line. Without this both read "Bear Not listed" — the model list's
   placeholder leaking into the customer's order. Plain text; callers that put
   it into HTML escape it. */
function customBowName(){
  var c = S.custom || {};
  return [c.year, c.make, c.model].filter(Boolean).join(" ").trim() || "Your bow (not listed)";
}

/* Make and model, both, non-blank. The one rule missingStep(), chooseConfig()
   and the Continue button all share, so they cannot disagree about what counts
   as a named bow. */
function customBowNamed(){
  var c = S.custom || {};
  return !!(String(c.make || "").trim() && String(c.model || "").trim());
}

/* The Continue button under the tiles. A tile click already advances when the
   bow is named; this exists for the other order of events — tile first, then
   the fields — where there is otherwise nothing left to press. */
function customContinue(){
  if (!S.bow || !S.bow.custom) {
    needStep({ n: 3, msg: "Pick what your bow takes — count the string and every cable on it." });
    return;
  }
  var miss = missingStep();
  if (miss && miss.n === 3) { needStep(miss); focusCustomField(); return; }
  $("s3").classList.add("done");
  update();
  goStep(miss && miss.n === 4 ? 4 : 5);
}

/* Cursor on the first required field that is still empty, after the scroll
   needStep() started — same reason focusAmo() waits. */
function focusCustomField(){
  setTimeout(function () {
    var ids = ["nlMake", "nlModel"];
    for (var i = 0; i < ids.length; i++) {
      var el = $(ids[i]);
      if (el && !String(el.value || "").trim()) { el.focus(); return; }
    }
  }, 520);
}

/* The make as an attribute value. Escaped because it comes from catalogue data
   and lands inside a quoted HTML attribute. */
function nlKnownMake(){
  return (S.make && S.make !== "Not listed") ? esc(S.make) : "";
}

/* Their own words about the bow, carried onto the order. The bench reads this
   off the slip and checks it before cutting, which is the whole safeguard
   against someone picking the wrong configuration. */
function setCustomBow(){
  var read = function (id) { return String((($(id) || {}).value) || "").trim(); };
  /* Only overwrite from fields that are on screen. notListed() rebuilds the
     panel, and the "No" path has no fields at all; reading absent inputs as
     empty strings would wipe what the customer typed a moment ago. */
  var prev = S.custom || {};
  var has = function (id) { return !!$(id); };
  S.custom = {
    year:   has("nlYear")   ? read("nlYear")   : (prev.year   || ""),
    make:   has("nlMake")   ? read("nlMake")   : (prev.make   || ""),
    model:  has("nlModel")  ? read("nlModel")  : (prev.model  || ""),
    string: has("nlString") ? read("nlString") : (prev.string || ""),
    cable:  has("nlCable")  ? read("nlCable")  : (prev.cable  || ""),
    serial: has("nlSerial") ? read("nlSerial") : (prev.serial || ""),
    notes:  has("nlNotes")  ? read("nlNotes")  : (prev.notes  || "")
  };
  /* S.make has to hold something. The "Not listed" tile calls notListed()
     rather than pickMake(), so nothing ever set it — and missingStep() reads it
     to decide what is unfinished, which left every later step locked on a bow
     that was otherwise complete. */
  if(S.bow && S.bow.custom){
    S.make = S.custom.make || "Not listed";
    $("v2").textContent = S.make;
    /* The step summary names the bow as well as the set, and the step is only
       DONE once the bow is named — the gate says so, and a green tick on a step
       the gate still refuses is the kind of thing nobody goes back to. */
    var said = [S.custom.year, S.custom.make, S.custom.model].filter(Boolean).join(" ").trim();
    $("v3").textContent = said ? said + " · " + S.bow.cfg : S.bow.cfg;
    $("s3").classList.toggle("done", customBowNamed());
  }
  ["nlMake", "nlModel"].forEach(function (id) {
    var el = $(id);
    if (el) el.classList.toggle("miss", !String(el.value || "").trim());
  });
  update();
}

function chooseConfig(i){
  var c = (CONFIGS[S.type] || CONFIGS.compound)[i];
  if(!c) return;
  /* A synthetic bow, shaped exactly like a catalogue one. `y: []` matters:
     missingStep() only demands a model year when there is more than one to
     choose between, so a not-listed bow never gets stuck on step 4. */
  S.bow  = { m: "Not listed", y: [], p: c.p.slice(), custom: true, cfg: c.label };
  S.year = null;
  $("s2").classList.add("done");
  $("v4").textContent = "";
  /* Step 4 has to be drawn for this bow. Nothing else draws it on this path
     (pickBow() does for a catalogue bow), so a not-listed recurve was asked for
     an AMO length by a step that still read "Pick a model first." and had no
     field: nothing could be added to the cart. */
  renderYears();
  /* Deliberately NOT renderModels(): the panel lives inside #modelList, and
     re-rendering the model list would wipe the thing being clicked. */
  var panel = $("nlPanel");
  if(panel){
    panel.querySelectorAll(".tiles .tile").forEach(function (t) { t.classList.remove("sel"); });
    var tiles = panel.querySelectorAll(".tiles .tile");
    if(tiles[i]) tiles[i].classList.add("sel");
  }
  setCustomBow();          // fills S.make and S.custom, which the step gate reads
  /* Advance only when the bow is named. Before this, a tile click went to
     step 5 regardless and took the fields with it — see missingStep(). The
     choice is kept either way; what is refused is leaving the step. */
  var miss = missingStep();
  if (miss && miss.n === 3) {
    $("s3").classList.remove("done");
    needStep(miss);
    focusCustomField();
    return;
  }
  $("s3").classList.add("done");
  update();
  /* Step 4 when it still has a question (which piece, or the AMO length);
     otherwise straight to Material as before. */
  goStep(miss && miss.n === 4 ? 4 : 5);
}

/* ============================================================
   STEP 3 — MODEL + YEAR
   ============================================================ */
/* THE MODEL SEARCH IS FORGIVING — 2026-10-08. It was a plain in-order text
   match, so under Hoyt "rx7" said No match while "Carbon RX-7" sat in the list,
   and "phase 4" missed "Phase4 29". Typing is how a phone finds a bow (the list
   shows two rows at a time), and "No match" sends people down the not-listed
   route to count their own cables. The rule:
     - both sides are folded: capitals, spaces, hyphens, dots, slashes and
       apostrophes go, so "rx7" is "RX-7" and "v3x33" is "V3X 33";
     - each typed word is tested on its own and every one must match, so
       "carbon rx7" and "evo 33" (EVO NXT 33) work in any order;
     - a four-digit year also matches a bow whose year range holds it, the
       range being the one printed on the row. The old test compared typed
       text with numbers and could never be true;
     - the make's own name is ignored. They chose it one step ago, so
       "hoyt rx7" must not fail on the "hoyt".
   Only the list is filtered here. What a search that finds nothing shows is
   renderModels()'s business and is unchanged. */
function foldSearch(s){
  return String(s == null ? "" : s).toLowerCase()
    .replace(/[\s.\/\\'`\u2018\u2019\u2010-\u2015-]+/g, "");
}
function searchModels(list, raw, make){
  var words = String(raw || "").split(/\s+/).map(foldSearch).filter(Boolean);
  var mk = foldSearch(make);
  var mkWords = String(make || "").split(/\s+/).map(foldSearch).filter(Boolean).concat(mk);
  words = words.filter(function (w) { return mkWords.indexOf(w) === -1; });
  function hit(b, w){
    if(foldSearch(b.m).indexOf(w) !== -1) return true;
    if(!/^\d{4}$/.test(w) || !b.y.length) return false;
    return +w >= Math.min.apply(null, b.y) && +w <= Math.max.apply(null, b.y);
  }
  /* A looser match must not push the bow that was actually typed down the
     list: "lift 33" also finds the newer Lift X 33, which the catalogue sorts
     first, and the first row is the one a thumb lands on. So the bow named
     exactly comes first, then names holding the words run together in the
     order typed (all the old search ever found), then the rest. Catalogue
     order is kept inside each group. */
  function run(ws){
    var all = ws.join(""), exact = [], tight = [], loose = [];
    list.forEach(function (b) {
      if(!ws.every(function (w) { return hit(b, w); })) return;
      var f = foldSearch(b.m);
      (f === all ? exact : f.indexOf(all) !== -1 ? tight : loose).push(b);
    });
    return exact.concat(tight, loose);
  }
  var out = run(words);
  if(out.length || !mk) return out;
  /* Nothing matched. Before saying so, forgive the make once more: half typed
     on the way to "hoyt rx7" ("hoy" would otherwise flash No match), or run
     into the model ("hoyt-rx7"). Only reached when the answer would have been
     nothing, so it can never hide a bow that a plain match finds. */
  var rest = [];
  words.forEach(function (w) {
    if(mkWords.some(function (m) { return m.indexOf(w) === 0; })) return;
    rest.push(w.length > mk.length && w.indexOf(mk) === 0 ? w.slice(mk.length) : w);
  });
  return rest.join(" ") === words.join(" ") ? out : run(rest);
}
/* THE LINE UNDER THE SEARCH BOX: "54 Mathews models. Scroll or type to find
   yours." The list is a window onto a long column (Hoyt has over a hundred
   models) and nothing said so; on a desktop the window used to end exactly on a
   row boundary, so Mathews read as a four-model make with "My bow is not
   listed" above it (audit, 2026-10-08).

   Made here rather than in the section's markup, and placed before #modelList
   rather than inside it: the list's first child has to stay the not-listed row
   (tests/t_notlisted.py), and its innerHTML is replaced on every keystroke.

   SILENT IN THREE CASES. A search is typed: the list is then the answer, not a
   window. The not-listed panel is showing: there is no list. And the make has
   only a handful of models (a recurve "make" has two): everything is already on
   screen, so "scroll" would be an instruction to do nothing. */
var MODEL_COUNT_FROM = 5;
function renderModelCount(isList){
  var box = $("modelList"), inp = $("modelSearch");
  if(!box || !inp) return;
  var el = $("modelCount");
  if(!el){
    el = document.createElement("p");
    el.id = "modelCount";
    el.className = "modelcount";
    box.parentNode.insertBefore(el, box);
  }
  var all = (S.type && S.make && BOWS[S.type] && BOWS[S.type][S.make]) || [];
  var show = !!isList && all.length >= MODEL_COUNT_FROM && !inp.value.trim();
  el.textContent = show ? all.length + " " + S.make + " models. Scroll or type to find yours." : "";
  el.hidden = !show;
}
function renderModels(){
  const box = $("modelList");
  /* The not-listed panel reached from the Make step has no catalogue make
     behind it (S.make is "Not listed", or whatever the customer typed), so
     there is no list to draw: leave the panel as it is. Typing in the search
     box used to throw here, after taking `nolist` off the panel. */
  if(S.type && S.make && !(BOWS[S.type] || {})[S.make]) return;
  renderModelCount(true);
  box.classList.remove("nolist");   // back to being a scrolling list
  if(!S.type || !S.make){ box.innerHTML = '<p class="hint">Pick a make first.</p>'; $("specBox").innerHTML=""; return; }
  const q = ($("modelSearch").value || "").toLowerCase().trim();
  const list = searchModels(BOWS[S.type][S.make], q, S.make);
  if(!list.length){
    box.innerHTML = `<div class="noresult">No match for "<b>${q}</b>".<br><a href="#" onclick="RC.notListed(true);return false">Tell us your bow →</a></div>`;
    return;
  }
  box.innerHTML = nlRow() + list.map(b => {
    const sel = S.bow && S.bow.m === b.m;
    /* Buying one piece: no price on the row. The price depends on WHICH piece,
       which has not been chosen yet, so pkgFor() returns null and every bow in
       the catalogue would read "quote" — which looks like "we can't build this". */
    const single = S.buy === "single" && singlesOffered();
    const pk = single ? null : pkgFor(b);
    const tail = single ? "" : ` · ${pk?money(pk.price):"quote"}`;
    return `<div class="mrow ${sel?"sel":""}" onclick="RC.pickBow('${b.m.replace(/'/g,"\\'")}')">
      <b>${b.m}</b><em>${yrLabel(b.y)} · ${b.p.length} pc${tail}</em></div>`;
  }).join("");
}

/* The "not listed" escape hatch, at the TOP of the model list — Quinn,
   2026-08-18. It shipped at the BOTTOM that morning and he moved it the same
   day, which was right: the list is capped at 290px and scrolls, and Bear
   carries 83 models, so at the foot it sat three screens down and nobody
   looking for it would ever meet it. An escape hatch you have to scroll to is
   not an escape hatch.

   IT IS DELIBERATELY NOT AN `.mrow`. Every suite picks a bow with
   `#modelList .mrow` .first, so a row that is NOT a bow must not answer to that
   selector — otherwise putting it first turns fifteen passing tests into tests
   of this panel, still green and testing nothing. Its own class makes that
   structural rather than a convention someone has to remember, which is why the
   ordering is no longer load-bearing and this comment can say so. */
function nlRow(){
  const mk = (S.make && S.make !== "Not listed") ? S.make : "";
  return `<div class="nlrow" onclick="RC.notListed(true)"><b>My bow is not listed</b>`
       + `<em>${mk ? "Tell us your " + esc(mk) : "Tell us your bow"} →</em></div>`;
}
function yrLabel(ys){
  if(!ys || !ys.length) return "";
  const a = Math.min(...ys), b = Math.max(...ys);
  return a === b ? `${a}` : `${a}–${b}`;
}
function pickBow(m){
  S.bow = BOWS[S.type][S.make].find(b => b.m === m);
  S.year = S.bow.y.length === 1 ? S.bow.y[0] : null;
  $("v3").textContent = S.bow.m;
  $("s3").classList.add("done");
  renderModels(); renderYears(); update();
  if(S.type === "trad"){
    /* Step 4 is the AMO length for a recurve or longbow, and it is REQUIRED —
       missingStep() has always said so. This used to mark the step done, write
       "n/a" into it and jump to step 5, which then sat locked with every step
       after it greyed out and nothing on screen saying why. The answer was in
       step 4 all along, behind a heading that read "Model year" and a value
       that read "n/a".

       Land on step 4 instead. update() renames the heading and fills the value
       from the length once it is typed. */
    S.year = null;
    update(); goStep(4);
    /* Same delay as needStep() uses: focusing during a smooth scroll makes the
       browser jump to the field and abandon the animation. */
    setTimeout(focusAmo, 520);
    return;
  }
  /* A bow with one model year auto-answers step 4 and the builder jumps to 5.
     That is right for a full set and WRONG when buying one piece: the part
     chooser lives in step 4, so skipping it skipped the only question that
     matters. Most bows have a single year, so this hid the feature almost
     everywhere. */
  if(S.buy === "single" && singlesOffered()){
    if(S.year) $("v4").textContent = S.year;
    goStep(4);
    return;
  }
  if(S.year){ $("s4").classList.add("done"); $("v4").textContent = S.year; goStep(5); }
  else goStep(4);
}

/* ---- STEP 4: YEAR ---- */
function renderYears(){
  const box = $("yearTiles");
  if(!box) return;
  if(!S.bow){ box.innerHTML = '<p class="hint">Pick a model first.</p>'; $("specBox").innerHTML=""; return; }
  if(S.type === "trad"){
    /* A menu, not a text box. The length is on the riser or in the manual and
       an archer knows it — what varies is how they write it, and 62 / 62" /
       62 inches / sixty-two all reach the bench meaning the same thing.

       "Other" is always last and opens the free-text box. Odd lengths and
       fractions are real, and a list that cannot express one would turn that
       order away rather than take it. A length already typed that is not in the
       list selects Other and keeps its value, so re-opening the step never
       silently discards an answer. */
    const amo = (S.amo || "").trim();
    const listed = AMO_LENGTHS.indexOf(amo) !== -1;
    const other = S.amoOther || (amo !== "" && !listed);
    box.innerHTML =
      '<p class="hint">Not year-specific — recurve and longbow strings are built to the AMO length of your bow.</p>'
      + '<div class="amopick"><label for="amoSelect">AMO length</label>'
      + '<select id="amoSelect" onchange="RC.setAmoChoice(this.value)">'
      + '<option value=""' + (amo === "" && !other ? " selected" : "") + '>Choose your length…</option>'
      + AMO_LENGTHS.map(function (L) {
          return '<option value="' + esc(L) + '"' + (!other && L === amo ? " selected" : "") + '>' + esc(L) + '</option>';
        }).join("")
      + '<option value="__other"' + (other ? " selected" : "") + '>Other — I\'ll type it</option>'
      + '</select></div>'
      + '<input class="search" id="amoInput" placeholder="AMO length — e.g. 58&quot; or 62&quot;" value="'
      + esc(amo) + '" oninput="RC.setAmo(this.value)" onkeydown="RC.amoKey(event)"'
      + (other ? "" : " hidden") + '>';
    renderSpec(); return;
  }
  box.innerHTML = S.bow.y.slice().sort((a,b)=>b-a).map(y =>
    `<div class="tile ${S.year===y?"sel":""}" onclick="RC.pickYear(${y})">${y}</div>`
  ).join("");
  renderSpec();
}
function pickYear(y){
  S.year = y;
  $("v4").textContent = y;
  $("s4").classList.add("done");
  renderYears(); update();
  goStep(5);
}

/* Whether the Full set / One piece question is even asked. A recurve or longbow
   string is already a single item, so the question would have one answer. */
function singlesOffered(){ return S.type === "compound" || S.type === "crossbow"; }

/* The distinct parts of the selected bow, in the order its own breakdown lists
   them. "Bowstring + Buss cable + Control cable + Yoke + Yoke" offers four
   choices, not five — you buy a yoke, not the second yoke. */
function piecesFor(bow){
  if(!bow || !bow.p) return [];
  var seen = [], out = [];
  bow.p.forEach(function(part){
    if(seen.indexOf(part) < 0){ seen.push(part); out.push(part); }
  });
  return out;
}

/* Part name -> variant. The mapping is driven by the bow's architecture, which
   its breakdown already encodes:

     "Buss cable + Control cable"  is the single-cam / hybrid naming
     "Cable + Cable"               is a twin or binary cam

   so the bowstring SKU follows from which of those the bow has, rather than
   from anything the customer has to know about their own cams.

   A GENERIC "Cable" ON A COMPOUND MAPS TO THE SPLIT BUSS. On a twin-cam both
   cables are buss cables; the control cable is specific to hybrid and single-cam
   systems, so buss is the archery-correct read. It is $74.99 against $64.99, so
   the difference is real money and worth Quinn confirming. */
function singleFor(part){
  if(!part) return null;
  if(S.type === "crossbow"){
    return part === "Bowstring" ? SINGLES.xbs : SINGLES.xbc;
  }
  if(part === "Bowstring"){
    var twinCam = (S.bow && S.bow.p || []).indexOf("Control cable") < 0;
    return twinCam ? SINGLES.tcs : SINGLES.scs;
  }
  if(part === "Control cable") return SINGLES.ctl;
  if(part === "Yoke")          return SINGLES.yok;
  return SINGLES.bus;          // "Buss cable" and a generic "Cable"
}

function pkgFor(bow){
  if(!bow) return null;
  /* One piece: the variant is the part, not the set. Everything downstream —
     pricing, the summary, add-to-cart — reads this the same way either way. */
  if(S.buy === "single" && singlesOffered()){
    return S.piece ? singleFor(S.piece) : null;
  }
  if(S.type === "crossbow"){
    /* Ravin's helical-cable sets are several times the bench time of a two-piece
       Excalibur. Fall back to the standard package if the multi-cable variant
       hasn't been created yet, so a missing variant can't break pricing. */
    /* A CROSSBOW WITH NO CABLES IS SOLD ITS STRING, NOT A SET. The recurve-limbed ones
       (every Excalibur) take one string and nothing else, and were being charged the
       complete-set price, $139.99, for it. Quinn, 11 October 2026: "single excalibur
       strings are 54.99". So a parts list that is all bowstrings is the single crossbow
       string, once per string: the TwinStrike takes two. `qty` is what addToCart sends. */
    if (SINGLES.xbs && bow.p.length && bow.p.every(function (x) { return x === "Bowstring"; })) {
      return { id: SINGLES.xbs.id, name: SINGLES.xbs.name, qty: bow.p.length,
               price: SINGLES.xbs.price * bow.p.length, stringOnly: true };
    }
    return (bow.p.length >= 4 && CROSSBOW_MULTI) ? CROSSBOW_MULTI : CROSSBOW_PACKAGE;
  }
  if(S.type === "trad")     return TRAD_PACKAGE;
  const n = bow.p.length;
  return COMPOUND_PACKAGES.find(p => n >= p.min && n <= p.max) || null;
}

/* THE PIECE ROWS, shared by the catalogue bow and the not-listed bow.

   IT IS A FUNCTION BECAUSE THE TWO BRANCHES DRIFTING APART IS THE BUG IT WAS
   WRITTEN TO FIX — see the note in the custom branch below. Same reasoning as
   closeupGeom() being shared by the 2D and GPU renderers: one definition, or
   they differ and only one of them gets tested. */
function pieceRows(){
  return piecesFor(S.bow).map(part => {
    const v = singleFor(part);
    const sel = S.piece === part ? " sel" : "";
    const price = v ? ` <b>${money(v.price)}</b>` : ` <em>price on request</em>`;
    return `<div class="piecerow${sel}" onclick="RC.pickPiece('${part.replace(/'/g,"\\'")}')">
              <span>${part}</span><span class="piecep">${price}</span></div>`;
  }).join("");
}

function renderSpec(){
  if(!S.bow){ $("specBox").innerHTML = ""; return; }
  const pk = pkgFor(S.bow);
  if(S.bow.custom){
    const c = S.custom || {};
    const said = [c.year, c.make, c.model].filter(Boolean).join(" ").trim();
    const known = [
      c.string ? `String ${esc(c.string)}` : "",
      c.cable  ? `Cables ${esc(c.cable)}`  : "",
      c.serial ? `Serial ${esc(c.serial)}` : ""
    ].filter(Boolean).join(" · ");
    /* A NOT-LISTED BOW CAN BUY ONE PIECE, AND UNTIL 2026-09-29 IT COULD NOT.
       This branch returned BEFORE the single-piece chooser below, so anyone who
       chose "Individual string or cable" and then had to use the not-listed
       hatch got their parts as a bulleted DESCRIPTION — text that looks
       clickable and is not — while missingStep() went on demanding a piece.
       Steps 5, 6 and 7 stayed locked, Add to cart did nothing, and there was no
       way out of step 4 at all. Reported by a customer through Quinn: a Hoyt
       CRX 32 XTS ARC (a 2012 bow, so not in a catalogue that starts at 2015)
       wanting a bowstring only.

       THE PARTS WERE ALWAYS THERE — `S.bow.p` is built from the configuration
       he picked in step 3 — so nothing had to be looked up to fix it; the rows
       simply were not rendered. Everything downstream already worked: singleFor()
       reads `S.bow.p` for the cam system, and pkgFor() returns the single's
       variant once a piece is set.

       THE SET PRICE IS SUPPRESSED IN SINGLE MODE. `pk` is the SINGLE's variant
       here once a piece is chosen, so the old sentence would have read "a
       3-piece set at $84.99" — a set price that is really one string's, which is
       worse than saying nothing. */
    const one = S.buy === "single" && singlesOffered();
    $("specBox").innerHTML = `<div class="spec">
      <b>${esc(said) || "Your bow"}</b> — you told us it takes <b>${S.bow.cfg}</b>, which is a
      <b>${S.bow.p.length}-piece set</b>${(pk && !one)?` at <b>${money(pk.price)}</b>`:""}.
      ${one
        ? `Which piece do you need?<div class="piecelist">${pieceRows()}</div>`
        : `<ul><li>${S.bow.p.join("</li><li>")}</li></ul>`}
      ${known ? `<div style="margin-top:8px">You gave us: ${known}.</div>` : ""}
      <div style="margin-top:10px;font-size:13px;color:var(--muted)">
        This bow isn't in our catalogue yet, so we check the configuration against it
        before we cut anything. If it does not match, we contact you before we build.</div>
    </div>`;
    return;
  }
  /* One piece: the breakdown stops being a description and becomes the choice.
     Built from the bow's own parts, so nothing can be offered that this bow does
     not have. */
  if(S.buy === "single" && singlesOffered()){
    const opts = pieceRows();
    $("specBox").innerHTML = `<div class="spec">
      Your <b>${S.year||yrLabel(S.bow.y)} ${S.make} ${S.bow.m}</b> takes
      <b>${S.bow.p.length} pieces</b>. Which one do you need?
      <div class="piecelist">${opts}</div>
      <div class="piecenote">Built to the same spec and the same queue as a full set —
        colours, servings and build time are all the same from here.</div>
    </div>`;
    return;
  }
  const counts = {};
  S.bow.p.forEach(x => counts[x] = (counts[x]||0)+1);
  const parts = Object.entries(counts).map(([k,v]) => v>1 ? `${v} × ${k}` : k);
  if(S.type === "trad"){
    $("specBox").innerHTML = `<div class="spec">
      <b>${S.make}</b> — ${S.bow.m.replace(" — specify AMO length","")}, built to your AMO length at <b>${money(pk.price)}</b>.
      <ul><li>Built to the AMO length you pick above. Add your strand count or draw weight in Order notes on the cart page.</li><li>Served both ends, centre serving included</li></ul>
    </div>`;
    return;
  }
  $("specBox").innerHTML = `<div class="spec">
    Your <b>${S.year||yrLabel(S.bow.y)} ${S.make} ${S.bow.m}</b> takes ${pk && pk.stringOnly ? `<b>${S.bow.p.length === 1 ? "one string" : S.bow.p.length + " strings"} and no cables</b>` : `a <b>${S.bow.p.length}-piece set</b>`}${pk?` — the <b>${pk.name}</b> at <b>${money(pk.price)}</b>`:""}.
    <ul>${parts.map(p=>`<li>${p}</li>`).join("")}</ul>
  </div>`;
}

/* ---- STEP 5: MATERIAL ---- */
function renderMats(){
  const box = $("matList");
  if(!box) return;
  /* The shop's pick is a SECTION SETTING naming a material handle, not a field
     on the metaobject. Metaobject field keys are permanent once created, and
     which material the bench favours is exactly the sort of thing that changes —
     so it lives where it can be edited and undone. Same reasoning as
     serving_only_colours. */
  /* A LIST of handles, not one. Materials are already filtered by bow type, and
     no material is offered on two types — 452Xtra is compound-only, DynaFLIGHT
     97 crossbow-only — so listing both badges the right one on each without any
     per-type mapping. If a material is ever offered on two bow types, a badge
     on it shows up on both; that is the case to watch. */
  const picks = String(CFG.staffPick || "").split(",")
                  .map(x => x.trim().toLowerCase()).filter(Boolean);
  const pickLabel = String(CFG.staffPickLabel || "Our pick").trim();
  box.innerHTML = materialsFor(S.type).map(m => `
    <div class="matcard ${S.material===m.id?"sel":""}" onclick="RC.pickMat('${m.id}')">
      <div class="mathead"><b>${m.name}</b>${
        picks.indexOf(String(m.id).toLowerCase()) !== -1 && pickLabel
          ? `<span class="matpick">${esc(pickLabel)}</span>` : ""
      }<em>${m.spec}</em></div>
      <p>${m.blurb}</p>
      ${m.best ? `<div class="matbest">Best for: ${m.best}</div>` : ""}
      ${matLink(m)}
    </div>`).join("");
}
/* "Learn more" under each material card — Quinn, 2026-09-17. Three things it
   has to do that are not obvious from the ask.

   IT MUST NOT SELECT THE MATERIAL. The card's own onclick is the selector, so
   the link stops the event: without that, reading about X-99 would switch the
   build to X-99 on the way out, and the customer would come back to a string
   they did not choose.

   IT OPENS IN A NEW TAB. The build lives in memory and nothing persists it, so
   navigating away mid-build and coming back lands on an empty step 1 — the
   customer is several answers in by the time they reach step 5.

   AND IT DEEP-LINKS where the page has an anchor. The materials page explains
   452Xtra and X-99 under their own headings; the traditional and crossbow
   threads are not written up there yet, so those get the page itself rather
   than a #hash that scrolls nowhere. MAT_ANCHORS is the list of headings that
   actually exist — add to it when the page does, not before. */
var MAT_ANCHORS = ["452xtra", "x99"];
function matLink(m){
  var base = String(CFG.materialsUrl || "/pages/materials").trim();
  if (!base) return "";
  var id = String(m.id || "").toLowerCase();
  var href = base + (MAT_ANCHORS.indexOf(id) !== -1 ? "#" + id : "");
  return '<a class="matmore" href="' + esc(href) + '" target="_blank"'
       + ' rel="noopener" onclick="event.stopPropagation()">Learn more</a>';
}

function pickMat(id){
  S.material = id;
  /* Drop anything the new material can't carry: speckles when it is solids-only,
     and colours tied to other materials — Natural exists only as untreated
     452Xtra and X-99, so it cannot follow you to B55, Mercury 2 or DF97. */
  ["main","second","serving","centre","pinstripe"].forEach(k=>{
    const c = S.colours[k] && colourByName(S.colours[k]);
    if(c && !colourAllowed(c)) S.colours[k] = (k === "second" || k === "pinstripe") ? null : firstAllowed();
  });
  $("v5").textContent = MATERIALS.find(m=>m.id===id).name;
  $("s5").classList.add("done");
  renderMats(); update();
  goStep(6);
}

/* ============================================================
   STEP 4 — COLOURS
   ============================================================ */
const TABS = [
  {id:"main",   label:"Main colour"},
  {id:"second", label:"Second colour"},
  {id:"pinstripe", label:"Pinstripe"},
  {id:"serving",label:"End servings"},
  {id:"centre", label:"Centre serving"}
];
/* ONE LIST, ONE ROW PER COLOURABLE SURFACE.
   Replaces renderPin / renderShrink / renderTabs, which were three controls
   stacked in one step with two different ways of picking a colour between them.
   Each row shows its own chip and current value so the whole build reads at a
   glance; the open row expands in place. `S.activeTab` still names the open row,
   so everything downstream — setColour, tabTakes, the Clear gate — is unchanged.

   SURFACES is TABS plus the wrap. The wrap is a row like the others now, but it
   still draws from SHRINKS rather than COLOURS: it comes in what is stocked, not
   the 68 the string does, and offering the full palette would promise something
   the bench cannot build. */
const SURFACES = TABS.concat([{ id: "shrink", label: "Speed nock wrap" }]);

/* A FLEMISH TWIST STRING HAS NO END SERVING. Its loops are the bundle itself, twisted back
   and spliced in, where an endless-loop string's are served. Quinn, 10 October 2026:
   "selecting flemish twist needs to remove the end serve in the visualizer". It is read
   off the model's name, the same way the catalogue names the two recurve and longbow
   entries ("Flemish twist"); a not-listed bow is not asked and is drawn served. The row
   goes with the drawing, and the slip and the caption with the row: a colour chosen for
   a serving that is not built would only be an instruction the bench cannot follow. */
function endServed(){
  return !(S.bow && /flemish/i.test(S.bow.m || ""));
}

function surfaceShown(id){
  if (id === "pinstripe") return !!S.opts.pin;
  if (id === "serving")   return endServed();
  /* Compounds only — nothing else carries speed nocks, so nothing else has a
     wrap to colour. Two or more stocked colours or there is nothing to choose. */
  if (id === "shrink")    return nocksIncluded() && SHRINKS.length >= 2;
  /* A CABLE HAS NO CENTRE SERVING. Quinn, 2026-08-15, closing the last part of
     the single-piece gate: the centre serving is where the arrow is nocked, so
     it exists on the bowstring and nowhere else. END servings stay on every
     row — a cable is served at its loops like anything else. */
  /* NOR DOES A CROSSBOW STRING OFFER ONE. Quinn, 2026-09-13: "Center serve
     selection needs to be removed from crossbow strings". Set or single piece,
     so the row, the slip line, the preview chip and the Clear warning all go
     with it, because every one of them asks surfaceShown("centre"). The arrow
     nock question is left alone; he did not ask about it. */
  if (id === "centre")    return stringPiece() && S.type !== "crossbow";
  return true;
}

/* The bottom row of the list AS RENDERED, which moves: a compound with no
   pinstripe ends on Speed nock wrap, a recurve ends on Centre serving because
   it has no speed nocks to wrap. Picking a colour there is what "the last
   colour" means — there is nothing below it left to answer. */
function lastSurfaceId(){
  var shown = SURFACES.filter(function (t) { return surfaceShown(t.id); });
  return shown.length ? shown[shown.length - 1].id : null;
}

/* Picking a colour moves you on: to the next surface, or out of step 6
   altogether once the bottom row is answered.

   Quinn asked for the per-pick jump on 2026-08-14, REVERSING an earlier call of
   mine that only advanced on the last row — my reasoning had been that someone
   comparing three reds should not be thrown down the page each time. His is the
   better read of how people actually use it: nearly everyone picks once and
   moves on, and anyone who wants to compare can click the row open again.

   The end of the list is still special, and still cannot be "every colour has a
   value" — every colour has a DEFAULT, so that test is true before the customer
   has chosen anything and would fire on the first click. */
function nextSurfaceId(id){
  var shown = SURFACES.filter(function (t) { return surfaceShown(t.id); });
  for (var i = 0; i < shown.length - 1; i++) {
    if (shown[i].id === id) return shown[i + 1].id;
  }
  return null;              // it was the last one
}
function advanceAfterColour(id){
  if (!id) return;
  var next = nextSurfaceId(id);
  if (!next) { coloursDone(); return; }
  var was = S.activeTab;
  S.activeTab = next;
  renderSurfaces();
  centreFocusZoom(was);
  panelRoom();
  /* `nearest`, so it only moves the page when the next row is actually off
     screen. Jumping the viewport on every colour pick would be worse than the
     problem it solves. */
  var head = document.querySelector('#surfaceList [data-surface="' + next + '"]');
  if (head) head.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function surfaceValue(id){
  return id === "shrink" ? S.colours.shrink : S.colours[id];
}

/* The chip on a row. Uses the same swatchStyle() as the grid so a two-tone or
   speckled colour reads the same at 26px as it does at full size. */
function surfaceChip(id){
  const v = surfaceValue(id);
  if (!v) return 'background:transparent;border-style:dashed';
  const c = COLOURS.find(x => x.n === v);
  return c ? swatchStyle(c) : ('background:' + hexOf(v));
}

function setTab(id){
  /* Clicking the open row closes it, so the list can be collapsed back to a
     six-line summary of the build. */
  var was = S.activeTab;
  S.activeTab = (S.activeTab === id) ? null : id;
  renderSurfaces();
  centreFocusZoom(was);
  panelRoom();
  colourRowIntoView(S.activeTab);
}

function renderSurfaces(){
  showpieceNote();
  var box = $("surfaceList");
  if (!box) return;
  /* The pinstripe row only exists while the pinstripe is on, so the way to turn
     it ON has to live somewhere always visible. One line in the list rather
     than a checkbox back at the top.

     IT SITS DIRECTLY UNDER SECOND COLOUR, which is where the pinstripe row
     itself appears once it is on — Quinn, 2026-08-14. So the offer and the
     thing it turns into occupy the same slot, and the list does not reorder
     itself under the cursor when you accept it. It used to be appended at the
     bottom, below the servings, which put the offer three rows away from
     anything it related to. */
  var add = S.opts.pin ? ""
    : '<div class="saddrow"><button type="button" class="sadd" data-pin="1">'
    +   '<span class="splus" aria-hidden="true"></span>'
    +   '<span class="saddlab">Add a pinstripe'
    +   '<em>A single contrasting strand run through the bundle</em></span>'
    +   '<b>Included</b></button></div>';
  var placed = false;

  /* The open row can stop being rendered under the cursor — turn the pinstripe
     off, or switch from a bowstring to a yoke. Normalised here rather than at
     each of those call sites, because every one of them ends up through this
     function and any missed site leaves step 6 with nothing expanded and no way
     to tell why. */
  if (S.activeTab && !surfaceShown(S.activeTab)) S.activeTab = "main";

  var html = SURFACES.filter(function (t) { return surfaceShown(t.id); }).map(function (t) {
    var open = S.activeTab === t.id;
    var v = surfaceValue(t.id);
    /* Pinstripe carries its on/off here, beside the colour it controls, rather
       than as a separate checkbox above the list. */
    /* THE BADGE IS A LABEL AGAIN, NOT A SECRET REMOVE BUTTON.
       It used to carry data-pin and a `title` reading "click to remove", and it
       sits DEAD CENTRE of the row header - measured 2026-10-24: the header is
       294px on a phone and the badge occupies 140-241px of it, so the middle of
       the header IS the badge. Tapping the pinstripe row to open it therefore
       deleted the pinstripe and collapsed the row back to the "Add a pinstripe"
       offer, which is the opposite of what the row looks like it does. It also
       explained itself only through a tooltip, which a phone never shows.
       Saying no is the None swatch in the row now, like Second colour's. */
    var badge = t.id === "pinstripe"
      ? ' <span class="sbadge">included</span>'
      : "";
    var row = '<div class="srow' + (open ? " open" : "") + '">'
      + '<button type="button" class="shead" data-surface="' + t.id + '"'
      + ' aria-expanded="' + open + '">'
      + '<i class="schip" style="' + surfaceChip(t.id) + '"></i>'
      + '<span class="slab">' + t.label + badge + '</span>'
      + '<span class="sval">' + (v || "None") + '</span>'
      + '<span class="scar" aria-hidden="true"></span>'
      + '</button>'
      + (open ? '<div class="spane">' + paneFor(t.id) + '</div>' : "")
      + '</div>';
    if (add && t.id === "second") { placed = true; return row + add; }
    return row;
  }).join("");

  /* Back to the bottom if Second colour is ever not rendered. The offer must
     survive that: without it there is NO way to turn the pinstripe on. */
  if (add && !placed) html += add;
  box.innerHTML = html;
  renderClearWarn();
}

/* Delegated once, on the container, rather than an inline onclick per row.
   Inline handlers here would need a quoted argument inside a quoted attribute
   inside a quoted JS string, which is three levels of escaping and was already
   wrong once. Bound at boot; renderSurfaces() replaces innerHTML freely and the
   listener survives because it is on the parent. */
function bindSurfaces(){
  var box = $("surfaceList");
  if (!box || box._rcBound) return;
  box._rcBound = true;
  box.addEventListener("click", function (e) {
    var pin = e.target.closest("[data-pin]");
    if (pin) { e.stopPropagation(); toggleOpt("pin"); return; }
    var head = e.target.closest("[data-surface]");
    if (head) setTab(head.getAttribute("data-surface"));
  });
}

/* Lost and restored: the block replacement that built the surface list took
   setShrink with it, and the builder threw at boot with rcMain still hidden —
   a blank step, not a broken one, which is the harder failure to spot. */
/* The button under the colour rows: every colour has a default, so "done" here is
   the customer saying so, not a state the script can detect. */
function coloursDone(){
  $("s6").classList.add("done");
  goStep(7);
}

/* THE EXAMPLE, SAID ONCE, WHERE IT HELPS. Drawn under the colour rows while a
   showpiece is still on screen and the customer has not picked a main colour,
   and taken away by setColour() the moment they do. Quiet on purpose - it is a
   label on what they are looking at, not an error about something they did. */
function showpieceNote(){
  var host = $("coloursDone");
  if (!host) return;
  var note = $("coloursNeed");
  if (!SHOWPIECE || S.colourPicked) { if (note) note.remove(); return; }
  if (note) return;
  note = document.createElement("p");
  note.id = "coloursNeed";
  note.className = "hint";
  note.textContent = "The string shown is our example — tap a colour to make it yours.";
  host.parentNode.insertBefore(note, host);
}

function setShrink(i){
  S.colours.shrink = SHRINKS[i] || SHRINKS[0];
  $("s6").classList.add("done");
  update();
  advanceAfterColour("shrink");
}

function paneFor(id){
  if (id === "shrink"){
    return '<div class="swatchgrid">' + SHRINKS.map(function (n, i) {
      return '<div class="sw ' + (S.colours.shrink === n ? "sel" : "") + '"'
        + ' onclick="RC.setShrink(' + i + ')" title="' + n + '">'
        + '<i style="background:' + hexOf(n) + '"></i><span>' + n + '</span></div>';
    }).join("") + '</div>'
    + '<p class="spanenote">Heat shrink over the speed nocks. This is what we stock,'
    + ' not the full string palette.</p>';
  }
  return swatchPane(id);
}

function swatchStyle(c){
  if(c.h.indexOf("|") < 0) return `background:${c.h}`;
  const [a,b] = c.h.split("|");
  return c.sp
    ? `background:${a};background-image:radial-gradient(${b} 34%,transparent 35%),radial-gradient(${b} 34%,transparent 35%);`
      + `background-size:6px 6px;background-position:0 0,3px 3px`
    : `background:linear-gradient(115deg,${a} 0 48%,${b} 52% 100%)`;
}
/* Returns the swatch grid for one surface as HTML, rather than writing into
   three fixed elements. Same rules as before — only the delivery changed:
   colourAllowed() carries the speckle gate and the per-material list, and
   tabTakes() keeps Clear on the two serving rows. */
function swatchPane(id){
  const cur = S.colours[id];
  /* A "None" SWATCH ON THE PINSTRIPE TOO - Quinn, 2026-10-24: "pinstripe needs a
     'no pinstripe' option?" It did. The pinstripe is ON by default (the showpiece
     is Gunmetal / Mountain Berry / Natural), and the only way off was the little
     `included` pill, which is a <span> whose whole explanation lives in a `title`
     tooltip: no cursor and no tooltip on a phone, not keyboard reachable, and it
     reads as a label saying the pinstripe is free rather than as a control. So
     the row every visitor arrives with had no visible way to turn it off.

     The row STAYS when None is picked, exactly as Second colour's does, rather
     than collapsing back to the "Add a pinstripe" offer: changing your mind
     should not make a row appear and disappear under your thumb. Everything
     downstream already reads a null colour correctly - the slip and the preview
     both test `S.opts.pin && S.colours.pinstripe`, the header falls back to
     "None" and the chip goes dashed. The `included` pill still works and still
     removes the row outright, so both routes exist. */
  const optional = id === "second" || id === "pinstripe";
  /* AN UNAVAILABLE COLOUR IS SHOWN, NOT HIDDEN — Quinn, 2026-09-05: "rest
     should still be visible just not available". A grid that silently drops 28
     of 37 reads as a fault; the same grid with 28 dimmed reads as a range the
     shop does not stock in serving thread, which is the true thing.

     It carries NO onclick at all rather than a handler that returns early —
     nothing to mis-fire, and a .sw with no handler is inert by construction.
     aria-disabled because it is still read out, and the title says WHY rather
     than repeating the name. */
  const cell = c => {
    if (!inStock(c, id))
      return `<div class="sw na" aria-disabled="true" title="${c.n} — out of stock">
      <i style="${swatchStyle(c)}"></i><span>${c.n}</span></div>`;
    if (!centreAllows(c, id))
      return `<div class="sw na" aria-disabled="true" title="${c.n} — not stocked for centre serving">
      <i style="${swatchStyle(c)}"></i><span>${c.n}</span></div>`;
    /* Shown, dimmed, inert — Quinn's own pattern from 2026-09-05, and no onclick
       at all rather than a handler that returns early. A grid that silently drops
       most of its swatches reads as a fault; the same grid dimmed reads as a
       material the shop stocks in four colours, which is the true thing. */
    if (!materialStocks(c, id))
      return `<div class="sw na" aria-disabled="true" title="${c.n} — not stocked in ${materialName()}">
      <i style="${swatchStyle(c)}"></i><span>${c.n}</span></div>`;
    return `<div class="sw ${cur===c.n?"sel":""}" onclick="RC.setColour('${c.n.replace(/'/g,"\'")}')" title="${c.n}">
      <i style="${swatchStyle(c)}"></i><span>${c.n}</span></div>`;
  };

  /* centreAllows is NOT applied to `shown` any more — the row draws everything
     and cell() dims what cannot be chosen. The gate still exists and still runs,
     in cell() and in update(); what changed is only whether an unavailable
     colour is drawn at all. */
  const shown  = COLOURS.filter(c => colourAllowed(c) && tabTakes(c, id));
  let solids   = shown.filter(c => !c.sp);

  /* CLEAR SITS BESIDE BLACK ON THE END SERVING — Quinn, 2026-09-17. They are the
     two a customer actually picks for an end serving, and sort_order had them at
     opposite ends of a 37-swatch grid: Black first, Clear last, because Clear is
     appended to the palette rather than being part of it.

     Written against SERVING_ONLY rather than against the string "Clear", so a
     second unpigmented thread would group with Black too instead of landing at
     the end on its own. If Black is not on the row for some reason, nothing
     moves — the list is left exactly as the catalogue ordered it. */
  if (id === "serving" && SERVING_ONLY.length) {
    const blackAt = solids.findIndex(c => c.n === "Black");
    if (blackAt >= 0) {
      const moved = solids.filter(c => isServingOnly(c.n));
      const rest  = solids.filter(c => !isServingOnly(c.n));
      const at    = rest.findIndex(c => c.n === "Black");
      solids = rest.slice(0, at + 1).concat(moved, rest.slice(at + 1));
    }
  }
  const specks = shown.filter(c => c.sp);
  /* SERVING THREAD DOES NOT COME SPECKLED (Quinn, 2026-08-14). That is a
     property of the serving thread itself, not of the string material chosen in
     step 5 — so it is a flat no on these two rows however SPECKLE_OK reads. The
     mirror of tabTakes(), which keeps Clear ON the serving rows and off
     everything else. */
  const speckOK = !servingSurface(id) && SPECKLE_OK.includes(S.material);

  let grid = "";
  /* THE PINSTRIPE'S None TURNS THE PINSTRIPE OFF; the second colour's just clears
     the colour. They cannot share setColour(null), and the reason is in update():
     while the customer has not picked a main colour yet - which is every visitor,
     because the showpiece arrives with all three set - the showpiece guard REFILLS
     a null pinstripe with White. So setColour(null) here is undone before the next
     paint, and the swatch would look broken rather than absent.
     toggleOpt('pin') is the builder's existing off-path and already does the whole
     job: clears the colour, moves off the row, collapses it back to the "Add a
     pinstripe" offer - which is the visible way back on. */
  const noneCall = id === "pinstripe" ? "RC.toggleOpt('pin')" : "RC.setColour(null)";
  if(optional) grid += `<div class="sw ${!cur?"sel":""}" onclick="${noneCall}">
      <i style="background:var(--ink-3);border:1px dashed var(--muted-2)"></i><span>None</span></div>`;
  grid += solids.map(cell).join("");

  /* Name the material actually selected. The old copy said "not available in
     X-99" whatever you had chosen, which reads as a bug on Mercury 2 or DF97. */
  const matName = (MATERIALS.find(m => m.id === S.material) || {}).name || "this material";
  const head = speckOK
    ? `Speckled <em>— ${specks.length} more</em>`
    : `Speckled <em>— not available in ${matName}; switch material in step 5</em>`;

  /* The two grids are named, not just positional. The suites need to tell
     solids from speckles, and "the second .swatchgrid" breaks the moment the
     speckled block is hidden for a material that cannot carry it. */
  /* No speckled header at all on a serving row. "Not available in 452Xtra"
     would be doubly wrong there: it is not about the string material, and
     switching material in step 5 would not change it. */
  if (servingSurface(id)) {
    /* THE CENTRE SERVING DISCOLOURS AND THE END SERVINGS DO NOT GET THE SAME
       WARNING — Quinn, 2026-09-17: "a caveat needs to go on center serve that
       bright colours discolour very quickly and darker colours are recommended."
       It is the centre that the release, the fingers and the nock work against
       every shot, so the caveat belongs on that row alone. Putting it on both
       would tell a customer their end servings fade, which is not what he said
       and not what the bench sees. */
    const caveat = id === "centre"
      ? ` <b>Bright colours discolour quickly on a centre serving — a darker colour is recommended.</b>`
      : "";
    return `<div class="swatchgrid sw-solids">${grid}</div>`
      + `<p class="spanenote">Servings are a different thread from the string, and it does not come speckled.${caveat}</p>`;
  }

  return `<div class="swatchgrid sw-solids">${grid}</div>`
    + `<div class="swhead${speckOK ? "" : " off"}">${head}</div>`
    + (speckOK ? `<div class="swatchgrid sw-specks">${specks.map(cell).join("")}</div>` : "");
}

/* Kept as the name the rest of the file calls. Everything in step 6 is one
   list now, so redrawing a swatch means redrawing the list. */
function renderSwatches(){ renderSurfaces(); }

/* Clear serving lets the string colour show through it, which is the whole
   point of Clear — and on a dark string there is nothing much to show. Quinn,
   2026-08-13: it "doesn't turn out very well as the string colours don't show
   through clearly".

   A WARNING, NOT A BLOCK. It is a real combination and someone may want it; what
   they should not be is surprised by it four weeks later.

   0.18 is WCAG relative luminance, and it is a judgement rather than a
   standard. Measured against the real palette it catches Black (0.009) through
   Green (0.155) — so Black, both Black Cherries, Dark Brown, Root Beer, the
   Royal Blues, Cocobolo, Mountain Berry, Gunmetal, Olive Drab, Cedar, Patriot,
   Kiwi, Red, Camo and Purple — and stops before the tans and the whites, where
   Clear reads fine. Move it if the line lands in the wrong place; the colours
   above are what to check it against. */
var CLEAR_DARK_MAX = 0.18;

function clearServingWarning(){
  var main = colourByName(S.colours.main);
  if(!main || !main.h) return null;
  if(relLum(main.h) > CLEAR_DARK_MAX) return null;
  var on = [];
  if(surfaceShown("serving") && isServingOnly(S.colours.serving)) on.push("end servings");
  /* surfaceShown, not the stored value: S.colours.centre keeps whatever it held
     when the row was last on screen, and warning about Clear on a serving the
     customer cannot see or change would be a warning about nothing. */
  if(surfaceShown("centre") && isServingOnly(S.colours.centre)) on.push("centre serving");
  if(!on.length) return null;
  return "Clear on your " + on.join(" and ") + " over <b>" + S.colours.main +
         "</b>. Clear works by letting the string show through it, and on a colour "
         + "this dark there is little to see — it tends to read as a dull grey rather "
         + "than as your colour. It is still yours to choose; we build it either way.";
}
function renderClearWarn(){
  var box = $("clearWarn");
  if(!box) return;
  var msg = clearServingWarning();
  box.innerHTML = msg ? ("<b class=\"ttl\">Worth knowing</b>" + msg) : "";
  box.hidden = !msg;
}

function setColour(n){
  var was = S.activeTab;
  S.colours[was] = n;
  if (was === "main") {
    S.colourPicked = true;
    var need = $("coloursNeed"); if (need) need.remove();     // the prompt has been answered
  }
  $("s6").classList.add("done");
  renderSurfaces(); update();
  advanceAfterColour(was);
}

/* ============================================================
   STEP 5 — OPTIONS
   ============================================================ */
function renderOpts(){
  const inc = nocksIncluded()
    ? `<div class="optrow incl">
         <div class="inctick">✓</div>
         <label>Speed nocks<small>Fitted in the manufacturer's original locations, on every set</small></label>
         <div class="price">Included</div>
       </div>`
    : "";
  $("optList").innerHTML = inc + OPTIONS.filter(o =>
    o.type !== "auto" && (accessoriesOffered() || (o.id !== "loop" && o.id !== "peep"))
  ).map(o=>{
    if(o.type === "qty"){
      const rope = ropeOf();
      /* Rope and colour only appear once there is a loop to build, so a
         customer who wants none is never asked which kind of none. */
      /* The shop's pick on a rope, same badge and same wording as the material
         card. A SEPARATE setting from staff_pick because the two lists have
         nothing in common — materials are metaobject handles, ropes are "23"
         and "24" — so one setting could not name both. The wording is shared,
         because two badges reading differently would look like two different
         kinds of recommendation. Leading # tolerated: the buttons say "BCY #23"
         and typing it back with the hash is the obvious mistake. */
      const rpick = String(CFG.ropePick || "").trim().replace(/^#/, "");
      const rpickLabel = String(CFG.staffPickLabel || "Our pick").trim();
      const btns = (o.ropes && o.ropes.length > 1)
        ? `<div class="ropebtns">${o.ropes.map(r=>
            `<button class="ropebtn${rope && rope.k===r.k ? " sel" : ""}" onclick="RC.setRope('${r.k}')">
               <strong>${r.label}${
                 rpick && r.k === rpick && rpickLabel
                   ? `<span class="matpick">${esc(rpickLabel)}</span>` : ""
               }</strong><small>${r.note}</small>
             </button>`).join("")}</div>`
        : "";
      /* A select rather than swatches: five stocked colours is a list, and the
         string palette above it is already forty-one swatches deep. */
      const cols = loopColours().length
        ? `<div class="ropecolour">
             <label for="loopColour">Loop colour</label>
             <select id="loopColour" onchange="RC.setLoopColour(this.value)">${
               loopColours().map(c=>
                 `<option value="${c.replace(/"/g,"&quot;")}"${
                   S.opts.loopColour===c ? " selected" : ""}>${c}</option>`).join("")
             }</select>
           </div>`
        : "";
      const pick = (S.opts.loop > 0 && (btns || cols))
        ? `<div class="ropepick">${btns}${cols}</div>` : "";
      return `<div class="optrow${pick ? " nobord" : ""}">
        <div style="width:18px"></div>
        <label>${o.name}<small>${o.note}</small></label>
        <div class="qty">
          <button onclick="RC.bumpLoop(-1)">−</button><span id="loopQty">${S.opts.loop}</span><button onclick="RC.bumpLoop(1)">+</button>
        </div>
        <div class="price">${money(rope ? rope.price : o.price)}</div>
      </div>${pick}`;
    }
    /* Sold out is a fact about this week, not a broken control. Rendered as a
       flat row rather than a disabled checkbox: a greyed tickbox invites
       clicking and then says nothing back. */
    /* ANY check option can be out of stock, not just the rush. The copy travels
       with the option rather than living here, because "Not open" is true of a rush
       week and false of an empty parts bin, and a row that explains the wrong
       thing is worse than one that explains nothing. */
    if(o.type === "check" && o.available === false){
      return `<div class="optrow soldout">
        <div class="inctick">—</div>
        <label>${o.name}<small>${o.soldOut || "Out of stock just now."}</small></label>
        <div class="price">${o.soldOutPrice || "Out of stock"}</div>
      </div>`;
    }
    const pick = o.addon ? addonPick(o) : null;
    /* THE ROW DRAWS THE PRODUCT'S OWN PICTURE, not the picked variant's.
       Quinn, 2026-10-24: "this isnt the right picture for the hat add on" /
       "just use the studio image". addonPick() answers with the FIRST variant in
       stock when nobody has chosen one yet, which for the hat is Tan, whose
       picture is a workshop photograph - right on the product page, because it is
       the only accurate picture of that hat, and unreadable at the ~44px this row
       draws, where the bench behind it is most of the frame.
       The product's own picture is the clean studio shot, and the colour a
       customer picks is said in words in the dropdown beside it, which carries
       that better than 44 pixels ever could. PRICE still follows the pick -
       that one has to be the real number. */
    const pic = o.image || (pick ? pick.image : "");
    const price = pick ? pick.price : o.price;
    const chooser = (o.variants && S.opts[o.id])
      ? `<div class="addonpick"><select class="nocksel" id="optvar_${o.id}" aria-label="${esc(o.name)}: colour and size" onchange="RC.setAddonVar('${o.id}', this.value)">`
        + o.variants.map(x => `<option value="${x.id}"${x.id === pick.id ? " selected" : ""}${x.available === false ? " disabled" : ""}>${esc(x.title)}${x.available === false ? " (out of stock)" : ""}</option>`).join("")
        + `</select></div>`
      : "";
    return `<div class="optrow${chooser ? " haspick" : ""}">
      <input type="checkbox" id="opt_${o.id}" ${S.opts[o.id]?"checked":""} onchange="RC.toggleOpt('${o.id}')">
      ${pic ? `<img class="optpic" src="${esc(pic)}" alt="" width="56" height="56" loading="lazy">` : ""}
      <label for="opt_${o.id}">${o.name}<small>${o.note}</small></label>
      <div class="price"${price ? "" : ' style="color:var(--ok)"'}>${price ? "+"+money(price) : "Included"}</div>
    </div>${chooser}`;
  }).join("");
}
/* Index rather than the label in the onclick: nock names carry em-dashes,
   hashes and brackets, and one apostrophe would otherwise break the markup. */
/* THE ARROW NOCK QUESTION IS A STRING QUESTION. It exists to size the centre
   serving, and a cable has no centre serving — so on a single cable or yoke it
   is asked and answered about nothing, and the answer then prints on the slip
   as if the bench should act on it.

   Ships `hidden` in the markup and is revealed here, rather than the other way
   round: the block is inside step 7, which a customer can reach before this has
   ever run, and a question that flashes up and disappears is worse than one
   that arrives a moment late. */
/* NOT ON A CROSSBOW EITHER. Quinn, 2026-09-13, straight after the centre
   serving row came off crossbows: "Dont ask the nock question either". Hiding
   it here also clears any answer, drops the requirement in missingStep() and
   keeps "Arrow nock" off the slip, because all of them ask nockAsked(). */
function nockAsked() { return stringPiece() && S.type !== "crossbow"; }

function syncNockBlock(){
  var box = $("nockBlock");
  if (!box) return;
  var ask = nockAsked();
  box.hidden = !ask;
  /* Clear the answer when the question stops applying, or a nock chosen while
     the Bowstring was selected rides onto a yoke order — the same failure the
     peep and D-loop already had, where update() has to clear S.opts rather than
     just hide the rows. */
  if (!ask && S.nock) { S.nock = null; S.nockOther = ""; }
}

function renderNocks(){
  const box = $("nockTiles");
  if(!box) return;
  /* A dropdown, not tiles (Quinn, 2026-09-10). Rebuilt only when the list or
     the answer changes, so update() on every keystroke in the arrow box never
     re-renders a select the customer has open. The placeholder is disabled:
     "no answer" stays no answer until they pick, which is what missingStep()
     relies on. */
  const key = NOCKS.length + "|" + (S.nock || "");
  if(box.dataset.key !== key){
    box.dataset.key = key;
    box.innerHTML = '<select id="nockSelect" class="nocksel" onchange="RC.setNock(this.value)" aria-label="Which nock do you shoot?">'
      + '<option value="" disabled' + (S.nock ? "" : " selected") + '>Choose your nock…</option>'
      + NOCKS.map((n,i) => `<option value="${i}"${S.nock===n?" selected":""}>${n}</option>`).join("")
      + '</select>';
  }

  /* Create and destroy the free-text box, but never rewrite one that is already
     there — update() runs on every keystroke and replacing the input mid-type
     would drop focus and the caret. */
  const w = $("nockOther");
  if(!w) return;
  if(S.nock === NOCK_OTHER){
    if(!$("nockOtherInput")){
      w.innerHTML = '<input class="search" id="nockOtherInput" style="margin-top:10px"'
        + ' placeholder="Which arrow do you shoot? e.g. Easton Axis 340"'
        + ' value="' + String(S.nockOther||"").replace(/"/g,"&quot;") + '"'
        + ' oninput="RC.setNockOther(this.value)">';
    }
  } else if(w.innerHTML){
    w.innerHTML = "";
  }
}
function setNock(i){
  S.nock = NOCKS[i] || NOCKS[0];
  $("s7").classList.add("done");
  renderNocks(); update();
}
function setNockOther(v){
  S.nockOther = v || "";
  update();
}
function toggleOpt(id){
  S.opts[id] = !S.opts[id];
  if(id === "pin" && !S.opts.pin){ S.colours.pinstripe = null; if(S.activeTab==="pinstripe") S.activeTab="main"; }
  if(id === "pin" && S.opts.pin && !S.colours.pinstripe){ S.colours.pinstripe = "White"; }
  /* Pinstripe marks the colours step done, not options — it lives there now. */
  $(id === "pin" ? "s6" : "s7").classList.add("done");
  if(id === "pin" && S.opts.pin) S.activeTab = "pinstripe";   // straight to the swatch
  renderSurfaces(); renderOpts(); update();
  /* The offer is tapped from below a long open list, which has just collapsed:
     without this the new Pinstripe row is left far above the screen. */
  if(id === "pin" && S.opts.pin) colourRowIntoView("pinstripe");
}
function bumpLoop(d){
  S.opts.loop = Math.max(0, Math.min(9, S.opts.loop + d));
  $("s7").classList.add("done");
  renderOpts(); update();
}
function setRope(k){
  S.opts.loopRope = k;
  $("s7").classList.add("done");
  renderOpts(); update();
}
function setLoopColour(v){
  S.opts.loopColour = v;
  $("s7").classList.add("done");
  update();
}

/* ============================================================
   STRING VISUALISER — canvas renderer
   The string is a bundle of filaments swept along a helix, so
   lighting, occlusion and specular are computed from each
   point's surface normal rather than faked. End loops are the
   same sweep run round a teardrop and served at the throat.
   Four camera views, live twist.
   ============================================================ */
function hexOf(name){ const c = COLOURS.find(c=>c.n===name); return c ? c.h : "#555"; }
function rgbOf(hex){
  const s = String(hex).split("|")[0].replace("#","");
  return [parseInt(s.slice(0,2),16), parseInt(s.slice(2,4),16), parseInt(s.slice(4,6),16)];
}
/* WCAG relative luminance, 0 (black) to 1 (white). Used to decide whether a
   surface takes light or dark lettering — perceptual, so it does not call a
   saturated yellow dark the way a naive (r+g+b)/3 would. */
function relLum(hex){
  const [r,g,b] = rgbOf(hex);
  const f = function (v) {
    v = v / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126*f(r) + 0.7152*f(g) + 0.0722*f(b);
}
/* Diffuse MULTIPLIES the base colour; specular and rim are ADDED.
   That is what keeps a black string black instead of greying it
   out the moment it catches a highlight. */
function tone(hex, mul, add, cool){
  const [r,g,b] = rgbOf(hex);
  const a = add || 0, c = cool || 0;
  const t = v => Math.max(0, Math.min(255, Math.round(v)));
  return `rgb(${t(r*mul + a + c*0.55)},${t(g*mul + a + c*0.72)},${t(b*mul + a + c*1.0)})`;
}
function lit(hex, k, rim, dim){
  const [r,g,b] = rgbOf(hex);
  // Scale the additive highlight by how light the fibre already is, so a black
  // string does not get lifted off its swatch value by a specular it cannot have.
  const lum = (0.2126*r + 0.7152*g + 0.0722*b) / 255;
  const add = 0.22 + 0.78*lum;
  const d = (0.66 + 0.09*lum + 0.46*Math.max(0,(k+1)/2)) * (1 + (dim||0));
  /* Waxed, not polished. Quinn's close-ups show a soft waxy sheen rather than
     the hard specular I had pushed it to — pulled back and tightened so the
     highlight is a narrow band rather than a broad gloss. */
  const s = Math.pow(Math.max(0,k), 6) * 24 * add;
  const c = Math.pow(Math.max(0,rim), 6) * 13 * add;
  return tone(hex, Math.max(0.10, d), s, c);
}

/* ============================================================
   STRAND VARIATION
   Deterministic, seeded off the strand index, so a redraw is identical —
   nothing here may flicker between frames. Real bundles are not machine
   perfect and the uniformity was a large part of why this read as drawn
   rather than photographed.
   ============================================================ */
function jit(i, salt) {
  var x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);                       // 0..1
}

/* Real twist is 0.5–0.75 turns per inch on a ~0.105" bundle. Measured off
   Quinn's own orange/black string (2026-08-10, hand in shot for scale): about
   one colour band per inch, and a band is a half turn, so ~0.5 turns per inch
   — roughly 18 string diameters per turn. Everything before today was 2.6
   diameters, seven times too tight, which is why it read as a barber pole
   instead of a rope. 18 diameters = 36R. */
var TWIST_PER_R = 36;

/* Which colour a strand carries.

   The two colours are laid as SEPARATE BUNDLES and twisted together — they do
   not mix. An earlier version crossed a few strands at each seam on the theory
   that a real bundle would not be mathematically perfect; per Quinn that
   misrepresents the build, and the mixed strands were visible in the render.
   The split is clean, and stays clean. */
/* Speckle is dyed ALONG each filament, not across the bundle.

   BCY dye short segments of the individual fibre, so every strand alternates
   between the two colours down its own length, and at bundle scale the eye
   reads a fine tweed running with the twist. Quinn's photos are unambiguous
   about this: a single strand off the spool carries teal and gold in short
   runs, and the wound spool reads as a blend rather than as stripes.

   The old rule made every third STRAND carry the fleck colour for its entire
   length. That is a pinstripe repeated three times, which is why the render
   looked synthetic — it was drawing a thing that does not exist.

   Segment length scales off R so the texture holds at any zoom, and both the
   length and the phase are jittered per strand. Without the phase offset every
   strand would change colour at the same arc length and the bundle would band. */
/* Measured off the spool photos rather than guessed: the dyed runs are a
   fraction of the bundle diameter, not a multiple of it. At 2.2R the render
   drew long diagonal dashes — a barber pole, which is the failure the old
   per-strand rule had in a different direction. */
var SPECK_SEG  = 0.42;   // × R
var SPECK_FRAC = 0.26;   // proportion of runs carrying the fleck colour

function speckSeg(i, R) {
  return R * SPECK_SEG * (0.68 + 0.64 * jit(i, 9));
}
function isFleck(i, s, R) {
  var seg = speckSeg(i, R);
  var k = Math.floor((s + jit(i, 8) * seg * 7) / seg);
  return jit(i * 131 + k, 7) < SPECK_FRAC;
}
/* `s` is the arc length along the centreline. Omit it and you get the base
   colour, which is what the swatches and the flat chips want. */
function strandColour(i, N, opt, isPin, s) {
  if (isPin) return opt.pin;
  var col = (i < N / 2) ? opt.main : opt.second;
  if (col && col.indexOf("|") > 0) {
    var pair = col.split("|");
    if (s === undefined) return pair[0];
    col = isFleck(i, s, opt.R || 10) ? pair[1] : pair[0];
  }
  return col;
}

/* True at the two places the colour groups meet. Each half is its own bundle,
   so the join is a seam between two ropes, not a painted line down one. */
function atSeam(i, N) {
  var half = Math.round(N / 2);
  return i === 0 || i === half;
}

/* lighting rig: key from upper front-left, cool rim from below right */
const KEY = [-0.72, 0.60], RIM = [0.78, -0.50];
function keyAmt(th){ return KEY[0]*Math.sin(th) + KEY[1]*Math.cos(th); }
function rimAmt(th){ return RIM[0]*Math.sin(th) + RIM[1]*Math.cos(th); }

/* Static render — the twist animation cost more frame time than it earned. */
const VIEW = { mode: "closeup", phase: 0.85 };

/* ---- centrelines ---- */
function polyLine(x0, y0, x1, y1, n){
  const out = [], dx = x1-x0, dy = y1-y0, len = Math.hypot(dx,dy);
  const nx = -dy/len, ny = dx/len;
  for(let i = 0; i <= n; i++){
    const t = i/n;
    out.push({ x: x0+dx*t, y: y0+dy*t, nx, ny, s: len*t });
  }
  out.len = len; return out;
}

/* ---- the bundle, swept along any centreline ---- */
function sweepBundle(ctx, poly, R, opt){
  const N = opt.N, TWIST = opt.twist, ph0 = opt.phase, pinAt = opt.pinAt;
  /* pinAt is a list of strand indices now — one per colour junction. Kept
     tolerant of the old single-number form so a stale caller cannot silently
     drop the pinstripe altogether. */
  const PINS = pinAt == null ? [] : (Array.isArray(pinAt) ? pinAt : [pinAt]);
  const isPinStrand = (i) => PINS.indexOf(i) !== -1;
  const cheap = R < 8;                       // set view / small pieces
  const SLABS = cheap ? [
    { lo:-1.01, hi: 0.00, dim:-0.18, blur: 0, a:.90 },
    { lo: 0.00, hi: 1.01, dim: 0.00, blur: 0, a:1   }
  ] : [
    { lo:-1.01, hi:-0.34, dim:-0.22, blur: 2.0, a:.86 },
    { lo:-0.34, hi: 0.34, dim:-0.07, blur: 0.7, a:.96 },
    { lo: 0.34, hi: 1.01, dim: 0.00, blur: 0,   a:1   }
  ];
  const wBase = Math.max(0.8, 3.4 * R / N);

  /* Solid body, then clip everything to it, so the bundle has a clean outer
     edge and light colours do not render low through the gaps.

     KNOWN ISSUE — the sawtooth serration along the top and bottom edge. It is
     NOT the serving, and the following have each been tried and ruled out:
     round line caps on the underlay, round caps on the filament strokes, a
     dark body fill showing through, narrow filament strokes, and widening the
     strokes at the silhouette. Raising strand density (see drawString) reduced
     it a lot but did not remove it. It is somewhere in how each filament's
     sweep turns over at the silhouette; worth a proper look rather than
     another guess. */
  ctx.save();
  const body = new Path2D();
  poly.forEach((q,i)=>{ const x=q.x+q.nx*R, y=q.y+q.ny*R;
                        i ? body.lineTo(x,y) : body.moveTo(x,y); });
  for(let i=poly.length-1; i>=0; i--){ const q=poly[i];
                        body.lineTo(q.x-q.nx*R, q.y-q.ny*R); }
  body.closePath();
  /* Close to the filament value, not a deep shadow. Any gap that opens between
     filaments shows this, and a dark body turns those gaps into visible teeth. */
  ctx.fillStyle = tone(opt.main, 0.84);
  ctx.fill(body);
  ctx.clip(body);

  /* THE UNDERLAY MOVES ONLY ON A TWO-COLOUR STRING, and the reason is the
     whole diagnosis.

     The slabs draw the BACK face first, so it lands on top of this underlay and
     shows through the gaps between front filaments. On a SINGLE colour that is
     harmless — the back face is the same colour, and on a speckled string it
     adds fleck texture that reads as depth. On a TWO-COLOUR string it is the
     OTHER colour showing through, which is the dotted contamination Quinn
     reported: measured at zoom 0.60, dark bleed into the light arc is 0.0% on a
     single colour and 9.5% with a second.

     So the bleed and the extra speckle are the same phenomenon — seeing through
     the string. Occluding the back face unconditionally halves the bleed and
     also costs real speckle legibility (t_click's scanline drops 4 -> 2, and
     that test uses Red & Black, a SINGLE speckled colour). Gating on a second
     colour fixes it exactly where it is wrong and leaves every single-colour
     string byte-identical. */
  const twoTone = !!(opt.second && opt.second !== opt.main);
  const paintUnderlay = () => {
  /* Opaque underlay. A real bundle you cannot see through — without this the
       gaps between filaments show the stage and every light colour renders low. */
    ctx.save();
    /* Butt caps, not round. This underlay is wide and darker than the filaments
       that sit on it, so a round cap at the end of every run leaves a dark
       semicircle poking past the lit strand — which is what the sawtooth along
       the top and bottom edge actually was, all along. */
    ctx.lineCap = "butt"; ctx.lineJoin = "round";
    ctx.lineWidth = wBase*2.2;
    for(let i = opt.i0; i < opt.iN; i++){
      if(isPinStrand(i)) continue;
      const phi = (i/N)*Math.PI*2;
      /* The underlay follows the speckle too. It shows through wherever the
         filaments above leave a gap, and a base-coloured underlay under a fleck
         segment bleeds the wrong colour into exactly those gaps. */
      let run = [], runCol = null;
      const flush = () => {
        if(run.length > 1){
          ctx.strokeStyle = tone(runCol, 0.74);
          ctx.beginPath();
          run.forEach((q,j)=> j ? ctx.lineTo(q[0],q[1]) : ctx.moveTo(q[0],q[1]));
          ctx.stroke();
        }
        run = [];
      };
      for(const q of poly){
        const th = (q.s/TWIST)*Math.PI*2 + phi + ph0;
        if(Math.cos(th) <= 0){ flush(); runCol = null; continue; }   // front face only
        const c = strandColour(i, N, opt, false, q.s);
        const o2 = R*Math.sin(th);
        const pt = [q.x + q.nx*o2, q.y + q.ny*o2];
        if(runCol !== null && c !== runCol){
          run.push(pt);           // overlap a point so the colours butt, not gap
          flush();
        }
        runCol = c;
        run.push(pt);
      }
      flush();
    }
    ctx.restore();

  };
  if (!twoTone) paintUnderlay();

  SLABS.forEach((slab, si) => {
    /* After the back face, before anything in front of it. Index 1 is the first
       non-back slab in both the cheap and the full lists. */
    if (twoTone && si === 1) paintUnderlay();
    ctx.save();
    ctx.globalAlpha = slab.a;
    if(slab.blur) ctx.filter = `blur(${slab.blur}px)`;

    for(let i = opt.i0; i < opt.iN; i++){
      const isPin = isPinStrand(i);
      /* Per-strand variation, seeded off the index so a redraw is identical.
         A few percent is enough — the tell was that every strand was exactly
         the same width and brightness. */
      const jw = 0.94 + 0.12*jit(i, 1);         // width
      const jb = -0.04 + 0.08*jit(i, 2);        // brightness, via the dim term
      const phi = (i/N)*Math.PI*2 + (jit(i, 3) - 0.5)*0.06;

      let run = null;
      const flush = () => {
        if(!run || run.pts.length < 2){ run = null; return; }
        const col = run.col;      // per run, not per strand: speckle changes along it
        const th = run.thm, k = keyAmt(th), rm = rimAmt(th);
        /* Full width, deliberately. Narrowing these to separate the filaments
           opens gaps where each one lingers at the top and bottom of its sweep,
           and those gaps read as teeth. Separation is drawn as a seam below
           instead, which costs nothing at the silhouette. */
        /* THE PINSTRIPE IS TWO STRANDS WIDE. Quinn, 2026-08-11 — it is drawn at
           exactly twice a neighbour so it reads as a deliberate stripe rather
           than as one strand that happens to be a different colour.

           Written as PIN_STRANDS × the ordinary width, not as a magic number.
           It arrived at 0.88 against 1.04 — thinner than everything around it,
           which is why it kept getting lost — then 1.20, then 1.10 on top of
           that, and by then 1.373 said nothing about what it was for. Two
           strands is a statement of intent that survives the next adjustment.

           Widening one strand is safe. Widening all of them is not: see the
           note above about full-width strokes, where narrowing opens gaps at
           the silhouette that read as teeth. */
        const PIN_STRANDS = 2;
        const wOne = wBase * 1.04;
        const wid = (isPin ? wOne * PIN_STRANDS : wOne) * jw * (0.74 + 0.26*Math.abs(Math.cos(th)));
        ctx.lineCap = "butt"; ctx.lineJoin = "round";

        ctx.beginPath();
        run.pts.forEach((q,j)=> j ? ctx.lineTo(q.x,q.y) : ctx.moveTo(q.x,q.y));
        ctx.strokeStyle = lit(col, k, rm, slab.dim + jb);
        ctx.lineWidth = wid; ctx.stroke();

        if(!slab.blur && k > 0.22 && !isPin){          // filament down the lit face
          ctx.beginPath();
          run.pts.forEach((q,j)=>{ const x=q.x+q.nx*wid*0.22, y=q.y+q.ny*wid*0.22;
                                   j ? ctx.lineTo(x,y) : ctx.moveTo(x,y); });
          ctx.strokeStyle = lit(col, Math.min(1, k+0.28), rm, slab.dim + jb);
          ctx.lineWidth = wid*0.32; ctx.stroke();
        }
        if(!slab.blur && !isPin){                      // seam between filaments
          ctx.beginPath();
          run.pts.forEach((q,j)=>{ const x=q.x-q.nx*wid*0.46, y=q.y-q.ny*wid*0.46;
                                   j ? ctx.lineTo(x,y) : ctx.moveTo(x,y); });
          /* Light between filaments of the same colour — on a waxed string you
             read the twist as colour bands, not as individual fibres. Deeper
             where the two colour bundles meet, because that is a join between
             two ropes rather than a line down one. */
          const seam = atSeam(i, N);
          ctx.strokeStyle = seam ? "rgba(0,0,0,0.34)" : "rgba(0,0,0,0.11)";
          ctx.lineWidth = Math.max(0.5, wid*(seam ? 0.26 : 0.13)); ctx.stroke();
        }
        if(!slab.blur && rm > 0.60){                   // cool underside rim
          ctx.beginPath();
          run.pts.forEach((q,j)=>{ const x=q.x-q.nx*wid*0.20, y=q.y-q.ny*wid*0.20;
                                   j ? ctx.lineTo(x,y) : ctx.moveTo(x,y); });
          ctx.strokeStyle = `rgba(120,140,180,${(0.20*(rm-0.60)/0.40).toFixed(3)})`;
          ctx.lineWidth = wid*0.38; ctx.stroke();
        }
        run = null;
      };

      for(const q of poly){
        const th = (q.s/TWIST)*Math.PI*2 + phi + ph0;
        const z = Math.cos(th);
        if(z > slab.lo && z <= slab.hi){
          const c = strandColour(i, N, opt, isPin, q.s);
          const o = R*Math.sin(th);
          const pt = { x:q.x + q.nx*o, y:q.y + q.ny*o, nx:q.nx, ny:q.ny };
          /* A colour change ends the run and starts the next one on the same
             point, so the two segments butt against each other. Starting the new
             run one point later leaves a hole the width of a filament. */
          if(run && run.col !== c){ run.pts.push(pt); flush(); }
          if(!run) run = { pts:[], thm:th, col:c };
          run.pts.push(pt);
        } else flush();
      }
      flush();
    }
    ctx.restore();
  });
  /* ---- silhouette rim: the fix for the edge serration ----

     The serration was never geometry. Measured off the rendered canvas, the top
     edge sits on one y value for every column — the body path is a straight
     line and the clip makes the silhouette exactly straight. What varies is
     WHAT PAINTS IT. A filament only reaches the edge where its sweep peaks, so
     between those points the darker body fill shows through, and that
     alternation along a straight edge is what the eye reads as teeth. Five
     earlier attempts all moved the geometry, which was never the problem.

     So paint the edge continuously, with the colour of whichever filament is at
     the silhouette at that arc length — the twist, the two colour bundles and
     the speckle all still read correctly along the rim, because it is the same
     strandColour call the filaments use. */
  (function () {
    var wRim = Math.max(1, wBase * 0.75);
    [-Math.PI / 2, Math.PI / 2].forEach(function (thEdge) {
      var k = keyAmt(thEdge), rm = rimAmt(thEdge);
      /* Pulled in by half its width so the stroke lands inside the clip rather
         than being half cut away, which would reopen the notches it is here to
         close. */
      var off = Math.sin(thEdge) * (R - wRim * 0.5);
      ctx.lineWidth = wRim;
      ctx.lineCap = "butt";
      for (var j = 0; j < poly.length - 1; j++) {
        var q = poly[j], q2 = poly[j + 1];
        /* Solve for the filament whose sweep is at this edge here:
           (s/TWIST)*2pi + phi + ph0 = thEdge, with phi = (i/N)*2pi. */
        var i = Math.round(N * ((thEdge - ph0 - (q.s / TWIST) * Math.PI * 2) / (Math.PI * 2)));
        i = ((i % N) + N) % N;
        var col = strandColour(i, N, opt, isPinStrand(i), q.s);
        ctx.beginPath();
        ctx.moveTo(q.x + q.nx * off, q.y + q.ny * off);
        ctx.lineTo(q2.x + q2.nx * off, q2.y + q2.ny * off);
        ctx.strokeStyle = lit(col, k, rm, 0);
        ctx.stroke();
      }
    });
  })();

  /* Form shadow where the cylinder turns away, stroked while still clipped so
     only the inner half lands. */
  ctx.save();
  ctx.strokeStyle = "rgba(0,0,0,0.17)";
  ctx.lineWidth = Math.max(1.4, R*0.34);
  ctx.stroke(body);
  ctx.restore();

  ctx.restore();          // release the body clip
}

function bundleForm(ctx, xa, xb, cy, R){
  const g = ctx.createLinearGradient(0, cy-R, 0, cy+R);
  g.addColorStop(0,   "rgba(255,255,255,0.00)");
  g.addColorStop(0.22,"rgba(255,255,255,0.09)");
  g.addColorStop(0.42,"rgba(255,255,255,0.01)");
  g.addColorStop(0.74,"rgba(0,0,0,0.07)");
  g.addColorStop(1,   "rgba(0,0,0,0.20)");
  ctx.save();
  ctx.globalCompositeOperation = "overlay";
  ctx.fillStyle = g; ctx.fillRect(xa, cy-R, xb-xa, R*2);
  ctx.restore();
}

/* ---- serving, drawn one turn at a time ---- */
function drawServing(ctx, xa, xb, cy, rad, col, phase, r0, clear){
  const top = cy - rad, hgt = rad*2;
  /* Serving is thread wound onto the string, so it ramps up off the bundle at
     each shoulder rather than sitting on it as a block — the old rounded rect
     read as a rubber grommet. The ramp does a second job: it covers the
     filament run terminations that used to fringe the edge as sawtooth. */
  const rs = (r0 != null ? r0 : rad*0.80);
  const t  = Math.min(rad*0.85, (xb-xa)*0.15);   // short ramp: a long one rounds
                                                 // the whole block off again
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(xa, cy - rs);
  ctx.quadraticCurveTo(xa + t*0.5, top, xa + t, top);   // curved, not a facet:
  ctx.lineTo(xb - t, top);                              // straight ramps read as
  ctx.quadraticCurveTo(xb - t*0.5, top, xb, cy - rs);   // an octagon at this size
  ctx.lineTo(xb, cy + rs);
  ctx.quadraticCurveTo(xb - t*0.5, top + hgt, xb - t, top + hgt);
  ctx.lineTo(xa + t, top + hgt);
  ctx.quadraticCurveTo(xa + t*0.5, top + hgt, xa, cy + rs);
  ctx.closePath();
  ctx.clip();

  /* Clear serving is unpigmented thread: you see the bundle through it. So no
     base fill at all — only the turns, drawn as light and shadow rather than
     as colour. Filling first would have produced a pale grey wrap, which is
     what "clear" must not look like. */
  if(!clear){
    ctx.fillStyle = tone(col, 0.42);
    ctx.fillRect(xa-2, top-2, xb-xa+4, hgt+4);
  }

  /* Finer and flatter than before: serving thread is thin and wound near
     perpendicular. The old pitch and lean read as a barber pole rather than
     a wrap, which is the main thing that made ours look drawn and theirs
     look photographed. */
  const pitch = Math.max(1.5, rad*0.18), lean = rad*0.40;
  const off = ((phase/(Math.PI*2)) * pitch) % pitch;
  /* Each turn drawn as its own small cylinder: a dark base, a bright crown
     offset toward the key light, and a valley on the far side. Previously this
     was one gradient stroke per turn plus a dark line, which reads as hatching
     over a tube rather than as thread you could count. */
  for(let x = xa - lean*2 - off; x < xb + lean*2; x += pitch){
    const A = [x + lean, top - 1], B = [x - lean, top + hgt + 1];
    const g = ctx.createLinearGradient(0, top, 0, top+hgt);
    if(clear){
      g.addColorStop(0,    "rgba(0,0,0,0.20)");
      g.addColorStop(0.30, "rgba(255,255,255,0.10)");
      g.addColorStop(0.72, "rgba(0,0,0,0.10)");
      g.addColorStop(1,    "rgba(0,0,0,0.28)");
    } else {
      g.addColorStop(0,    tone(col, 0.46));
      g.addColorStop(0.30, tone(col, 0.82,  4));
      g.addColorStop(0.72, tone(col, 0.46));
      g.addColorStop(1,    tone(col, 0.26, 0, 10));
    }
    ctx.lineCap = "butt";
    ctx.strokeStyle = g; ctx.lineWidth = pitch*0.96;
    ctx.beginPath(); ctx.moveTo(A[0], A[1]); ctx.lineTo(B[0], B[1]); ctx.stroke();

    // crown: narrow, brighter, sitting just above centre where the key falls
    ctx.strokeStyle = clear ? "rgba(255,255,255,0.42)" : tone(col, 1.00, 34);
    ctx.lineWidth = Math.max(0.6, pitch*0.30);
    ctx.beginPath();
    ctx.moveTo(A[0] - pitch*0.10, A[1] + hgt*0.30);
    ctx.lineTo(B[0] - pitch*0.10, B[1] - hgt*0.52); ctx.stroke();

    // valley where this turn meets the next
    ctx.strokeStyle = clear ? "rgba(0,0,0,0.30)" : "rgba(0,0,0,0.50)";
    ctx.lineWidth = Math.max(0.6, pitch*0.20);
    ctx.beginPath();
    ctx.moveTo(A[0] + pitch*0.50, A[1]); ctx.lineTo(B[0] + pitch*0.50, B[1]); ctx.stroke();
  }

  // form shading over the whole serving — lighter when clear, or the bundle
  // showing through it gets muddied by the very overlay meant to shape it
  const k = clear ? 0.45 : 1;
  const cg = ctx.createLinearGradient(0, top, 0, top+hgt);
  cg.addColorStop(0,   `rgba(255,255,255,${0.22*k})`);
  cg.addColorStop(0.3, `rgba(255,255,255,${0.02*k})`);
  cg.addColorStop(0.75,`rgba(0,0,0,${0.16*k})`);
  cg.addColorStop(1,   `rgba(0,0,0,${0.42*k})`);
  ctx.fillStyle = cg; ctx.fillRect(xa-2, top-2, xb-xa+4, hgt+4);
  ctx.fillStyle = "rgba(150,178,220,0.10)";
  ctx.fillRect(xa-2, top+hgt*0.86, xb-xa+4, hgt*0.14);
  ctx.restore();

  ctx.fillStyle = "rgba(0,0,0,0.42)";
  ctx.fillRect(xa, top, 1.8, hgt);
  ctx.fillRect(xb-1.8, top, 1.8, hgt);
}

/* ---- hardware ---- */
/* Speed nocks as they actually ship: several nocks grouped on the end serving
   and covered with branded heat-shrink. So it reads as one dark glossy sleeve
   with slight swelling where each nock sits under it, not as separate brass
   beads — which is what the old drawNock produced, and it was drawn on bare
   string besides. River City brand their wrap, so the render carries it. */
/* SUPERSAMPLE — the single biggest quality lever in this renderer.

   The backing store used to be sized at devicePixelRatio, so a 1x display got
   one sample per CSS pixel. At that rate the helical filaments — the entire
   point of the drawing — alias into a crosshatch moire and the twist stops
   reading at all. Side by side at the same display size, the 1x render is not
   a slightly softer version of the 3x one; it is a different and much worse
   picture, and it is what every standard 1080p monitor at 100% scaling was
   getting.

   Measured against the 3x render as the target, on a 1x display:
       no supersample   RMS 11.85
       2x               RMS 10.79
       3x               RMS  9.65
   and the repaint stays around 5-6 ms either way, because this is a static
   drawing painted once per change rather than per frame. So it is a flat 3.

   A CONSTANT, not min(3, max(3, dpr)), which is the same number written to
   look like a calculation. The old code capped at 3 already; this only raises
   the floor to meet it.

   ONE FUNCTION, TWO CALLERS, and that matters. The wrap-logo legibility guard
   reasons in DEVICE pixels; if it kept reading devicePixelRatio while the
   canvas was supersampled it would suppress the mark on exactly the displays
   where the extra samples had just made it readable. */
function renderScale() {
  return 3;
}


/* HEAT SHRINK OVER THE SPEED NOCKS — this IS drawSpeedNock, live since 2026-09-17.

   Quinn, 2026-09-17: "the heat shrink needs more detail, look at the home page
   hero, you can see the ribs of the speed nocks under the heat shrink", then on
   the first two passes: "the width of each nock is remarkably too narrow. That
   current width would show max of two speed nocks. Look closer at the taper on
   either end in the hero."

   Measured off that photograph (photos/hero-bow.jpg, the string's wrap at
   2745-2832 across, grooves at y 472 / 525 / 572 / 627 / 680 / 730):

     - the tube's outer diameter is about 87px and the grooves are about 51px
       apart, so **one nock is 0.58 of the tube's DIAMETER**. Every earlier pass
       had it at 0.21-0.34, which is why they looked like a bellows.
     - **the taper closes in about ONE nock's length**, not over half the wrap.
       The old drawing spent 52% of its length on two cones, which is the second
       thing Quinn was pointing at.
     - **the ribs run THROUGH the taper.** The last segments are smaller in
       diameter but they are still segments — the tube keeps its shape as it
       closes down. So the ribs are not a texture on a flat middle between two
       smooth cones, which is how the first pass built them.

   THE WRAP'S LENGTH IS NOW DERIVED, NOT SET. A nock is a real object with a
   width, and a wrap covers a number of them plus the two closings, so `pitch`
   and `nocks` decide how long the tube is. Setting the length directly and
   dividing it up was what made "wider nocks" and "more nocks" pull against each
   other in the first two passes.

   Segment count is zoom-invariant by construction: every length here is a
   multiple of R, so zooming in draws the same nocks bigger and cannot conjure
   more of them. */
var SHRINK_DEFAULT = {
  nocks:  5,      // how many nocks the wrap covers
  pitch:  0.58,   // ONE NOCK as a fraction of the tube's outer diameter
  cone:   0.28,   // each end closing, same units. SHORT: Quinn, third pass — "the
                  // ends need to taper sharply to the string, following the
                  // contour of the speed nock".
  skirt:  0.5,    // how far the tubing carries ON the string past the closing,
                  // in NOCK WIDTHS. Quinn, fourth pass: "shrink wrap material
                  // needs to carry onto the string for half a speed nock width".
                  // The tube is cut longer than the hardware it covers, so it
                  // grips bare string at each end — the end of the wrap is a cut
                  // edge lying on the string, not the point where it lands.
  wall:   0.12,   // how far that collar stands off the string, as a fraction of
                  // the string radius: it is the tubing's own wall thickness.
  lip:    0.16,   // the round-over at the cut edge, as a fraction of the collar
  shape:  0,      // how the closing falls. 1 = straight cone. Above 1 puts the
                  // drop at the STRING end and leaves the top flat, so the tube
                  // carries the last nock's crown out to the edge of the
                  // hardware and then falls away off its face. 0 = the old
                  // smoothstep, flat at both ends, kept so it can be compared.
  bulge:  0.075,  // how far each segment swells, as a fraction of rad
  groove: 0.040,  // groove width, also a fraction of the outer diameter: the gap
                  // between two nocks does not widen when the nocks do
  gdark:  0.78,   // how dark a groove goes
  gtone:  0.26,   // and what colour it goes: the wrap's own colour times this.
                  // Ignored on a near-black wrap, which stays black.
  /* A CREASE IN A LIGHT MATERIAL IS READ OFF ITS SHOULDERS, not off its shadow.
     Quinn, ninth pass: "the render of the gaps looks 10x worse than black." On
     the black wrap a groove cuts into a bright top edge and reads as a crisp
     line; on white and red there is nothing bright for it to cut into, so the
     shadow alone came out as a grey smudge. Real creases in white plastic read
     because the lip either side of the valley catches the light. So each groove
     gets a pair of thin highlights on its shoulders, scaled by the wrap's value
     — full on white, nothing on black, which needs none. -1 derives it. */
  shoulder: -1,
  gcore:  0.30,   // how much of the groove's width is its dark core. Lower is a
                  // crease; higher is the soft channel the first version drew.
  cant:   0.05,   // how far a groove leans off square, as a fraction of height
  plateau: 1.9,   // how flat a nock's top is. 1 = a rounded sine hump; higher
                  // clips it into a plateau with the dip pushed into the joints,
                  // which is what a row of small cylinders under tubing does.
  crown:  0.19,   // sheen alpha
  print:  0.45,   // how much of the groove shading crosses the print
  /* WHAT IS PRINTED ON THE WRAP. "text" is the two lines of type that shipped;
     "logo" is the bow mark alone; "logo+word" is the mark with BOWSTRINGS under
     it. Quinn, 2026-09-17: "demo the bow logo on the shrink wrap instead of
     lettering."

     The mark is an IMAGE, not canvas paths, and that is forced: it is potrace
     output and its first path alone is 191KB of coordinates against a 213KB
     rc-builder.js. tools/make-wrap-mark.py rasterises the bow and its string
     lines out of the stacked logo — dropping the wordmark, which is already on
     the wrap as type — into assets/rc-wrap-mark.png, white on transparent so one
     file tints to any wrap colour. */
  mark:   "text", // overridden by the section's wrap_print setting. It defaults
                  // to TEXT here on purpose: the JS and the section are two
                  // pushes, and a JS that wanted the mark before the section
                  // could give it a URL would draw an empty wrap in between.
  markScale: 0.80, // the mark's width as a fraction of the hardware's
  /* HOW FAR THE SHADING FALLS OFF, and it cannot be one number for every wrap.
     The stops below were tuned on the black wrap and applied blind to the other
     two: measured, the white wrap's shaded side came out (83,83,82) — a third
     grey, so a white wrap rendered as grey plastic — and the red one's (67,7,10),
     near-black maroon. Both lit sides clipped to pure white as well, flattening
     the top quarter of the tube.

     High albedo is the physical reason: a light or saturated material bounces a
     lot of light into its own shadow and picks up ambient off the stage, while a
     near-black one has nowhere to go but black. So the falloff is compressed by
     the wrap's VALUE — its largest channel — rather than by luminance, which
     reads a saturated red as nearly dark. -1 derives it; 0 is the old stops for
     every colour and 1 is fully compressed. */
  falloff: -1,
  marginK: 0.45, // serving left showing each side of the wrap, as a fraction of
                 // the tube's diameter. The rule is Quinn's, 2026-09-17: the
                 // serving always shows on both sides, even if only a little.
  marginRightK: 2.6, // and MORE of it on the right. Quinn, 2026-09-18: "More
                 // serving should show on the right side of the speed nocks."
                 // A multiple of marginK, not its own measurement, so the two
                 // sides cannot drift apart as the wrap is retuned.
                 //
                 // It has to be a SEPARATE number because marginK does double
                 // duty: closeupGeom derives the wrap's own position from it too
                 // (nockX = margin + span), so simply raising marginK slides the
                 // whole wrap right and adds the same thread on the LEFT, which
                 // is not what was asked for. Measured at rest: 15px of serving
                 // showed past the wrap before, 41px at 2.6. 1.8 still read as a
                 // seam, 3.4 as a black sleeve.
  /* A hairline along the silhouette, alpha derived the same way. A white wrap on
     this near-white stage has no top edge at all without it. */
  rim:     -1
};

/* Deterministic, so two renders of the same string are identical — a Math.random
   lean would make the wrap flicker on every repaint. */
function shrinkJitter(i){
  var s = Math.sin((i + 1) * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/* ONE FUNCTION OWNS THE WRAP'S SIZE, because two things need it and they must
   not disagree: the wrap itself, and the end serving it sits on.

   THE SERVING HAS TO SHOW ON BOTH SIDES OF THE NOCKS — Quinn's rule, 2026-09-17:
   "as a rule the serving always needs to show on both sides of the speed nocks,
   even if its just a small amount." The serving used to end at a flat 50% of the
   frame while the wrap's length came from the hardware it covers, so at high
   zoom the wrap could outgrow its serving and reach the edge of the picture —
   leaving it looking like it was shrunk round nothing. The caller now reads the
   span from here and sizes the serving to it. */
/* What the SECTION says goes on the wrap. Only the two things a shop owner would
   ever move — which print, and where the mark comes from — so the rest of the
   numbers stay in one place in this file rather than becoming forty settings.
   The mark is only asked for when there is a URL to load it from. */
function shrinkFromCfg(){
  var o = {};
  if (typeof CFG === "undefined" || !CFG) return o;
  if (CFG.wrapPrint === "logo" && CFG.wrapMarkUrl) o.mark = "logo";
  if (CFG.wrapPrint === "text") o.mark = "text";
  return o;
}

/* The wrap's VALUE: its largest channel, 0..1. Black .10, red .77, white .96 —
   which is the ordering the shading needs and the one relLum() does not give. */
function shrinkValue(hex){
  const c = rgbOf(hex);
  return Math.max(c[0], c[1], c[2]) / 255;
}

function shrinkShade(hex, V){
  const v = shrinkValue(hex);
  const auto = Math.max(0, Math.min(1, (v - 0.18) / 0.55));
  const t = V.falloff >= 0 ? V.falloff : auto;
  const rim = V.rim >= 0 ? V.rim : auto * 0.22;
  const mix = (a, b) => a + (b - a) * t;
  const sh = V.shoulder >= 0 ? V.shoulder : auto * 0.55;
  return { topMul: mix(1.55, 1.00), topAdd: mix(22, 0),
           midMul: mix(0.62, 0.86), botMul: mix(0.34, 0.66), rim: rim,
           shoulder: sh };
}

function shrinkGeom(R){
  /* MERGED over the defaults, not swapped for them. An override object that
     simply replaced SHRINK_DEFAULT meant every key a caller did not mention
     arrived as undefined — and undefined in a gradient stop is NaN, which
     throws IndexSizeError inside the renderer. The symptom is the builder
     appearing not to work several steps later, with nothing pointing here. */
  var V = Object.assign({}, SHRINK_DEFAULT, shrinkFromCfg(), window.RC_SHRINK || {});
  const rad = R*1.55;                  // the tube at full diameter
  const od = rad*2;
  const seg = od * V.pitch;            // one nock
  const n = Math.max(1, Math.round(V.nocks));
  const cone = od * V.cone;            // one closing
  const skirt = seg * V.skirt;         // the collar of tubing on the string
  const wallR = R * (1 + V.wall);      // the collar's outer radius
  /* THE NOCKS ARE LAID OUT FROM THEIR CROWNS, and the hardware's edge falls
     where the END NOCK'S FLAT TOP ENDS.

     This is the third attempt at the end nocks and the first one that was
     measured rather than eyeballed. Holding the flat top out to the nock's outer
     FACE — the obvious reading of "the tube leaves at the end of the hardware" —
     overshoots, and the render says so: end plateaus came out 166px against
     139px for the interior ones, 20% wider. An interior nock's top is bounded by
     a dip on BOTH sides; an end nock's has a dip on one side and a cliff on the
     other, so a top that runs to the face is half a plateau too long.

     So: crowns a nock apart, the plateau the same width on every one of them,
     and the descent starting exactly where the end nock's plateau stops. */
  const lastCrown = (n - 1) * 0.5 * seg;
  /* How much of a nock the flat top covers, read back out of the plateau
     constant rather than guessed: min(1, sin*k) is flat wherever sin >= 1/k. */
  const plat = 1 - 2 * Math.asin(Math.min(1, 1/V.plateau)) / Math.PI;
  const half = seg*0.5;
  const hard = lastCrown + seg*plat*0.5;
  const ribbed = hard * 2;             // the hardware's whole footprint
  return { V: V, rad: rad, od: od, seg: seg, n: n, ribbed: ribbed, cone: cone,
           skirt: skirt, wallR: wallR, half: half, hard: hard,
           lastCrown: lastCrown, plat: plat,
           w: hard + cone + skirt,     // half the whole wrap, cut edge to cut edge
           bulge: rad * V.bulge };
}

/* How much serving to leave showing each side of the wrap. Just over half a tube
   diameter: enough to read as a band of thread rather than as a seam, and it
   scales with the render so it holds at every zoom level. */
function shrinkServingMargin(R){
  const g = shrinkGeom(R);
  const k = g.V.marginK || 0.45;
  return Math.max(g.od*k, R*1.2);
}

/* The RIGHT-hand margin, which is the deeper one. On the real string the serving
   runs on past the speed nocks toward the cam, so a thin seam either side of the
   wrap reads as a sleeve dropped onto bare thread. Left is unchanged: the loop
   end is close by on that side and there is no room to give.

   Derived from the left margin rather than measured separately, so retuning the
   wrap moves both together. */
function shrinkServingMarginRight(R){
  const g = shrinkGeom(R);
  return shrinkServingMargin(R) * (g.V.marginRightK || 1);
}

/* THE MARK LOADS ASYNCHRONOUSLY AND THE RENDERER IS SYNCHRONOUS, so the first
   paint can happen before the image is there. It is skipped when not ready and
   ONE repaint is asked for when it arrives — not a repaint per draw, which would
   loop. Same shape as the logo size guard: a wrap with no mark on it is honest,
   a half-drawn one is not. */
var MARK = null, MARK_TINT = {};
function markImage(){
  if (MARK) return MARK;
  if (typeof Image === "undefined") return null;
  MARK = new Image();
  MARK.onload = function () {
    MARK_TINT = {};
    if (typeof renderPreview === "function") renderPreview();
  };
  /* FROM LIQUID, not a hardcoded path. On the storefront the asset lives under
     /cdn/shop/t/3/assets/ and that `t/3` moves if the theme is ever swapped, so
     the section hands over `asset_url`. The literal below is the local test
     harness's path and is the fallback, not the answer. */
  MARK.src = ((typeof CFG !== "undefined" && CFG && CFG.wrapMarkUrl)
              || window.RC_MARK_URL || "/assets/rc-wrap-mark.png");
  return MARK;
}

/* Tinted through the alpha rather than shipped once per colour: draw the mark,
   then composite the ink over it with source-in, which keeps the artwork's own
   antialiasing. Cached per ink because this runs on every repaint. */
function markTinted(img, ink){
  if (MARK_TINT[ink]) return MARK_TINT[ink];
  var c = document.createElement("canvas");
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  var g = c.getContext("2d");
  g.drawImage(img, 0, 0);
  g.globalCompositeOperation = "source-in";
  g.fillStyle = ink;
  g.fillRect(0, 0, c.width, c.height);
  MARK_TINT[ink] = c;
  return c;
}

/* ============================================================
   THE WRAP, PER PIXEL  (see tools/demo/hd-wrap-patch.py for why)
   ============================================================
   A solid of revolution about the string: at distance d along it the radius is
   edge(d), so a pixel at height y sits at angle phi with sin(phi) = y / r and
   the surface normal there is (-dr/dd, sin phi, cos phi), normalised. Everything
   below is that normal, lit. Worked in linear light and converted at the end,
   which is what stops the white wrap going grey and the red one going brown.

   The print is drawn flat into a mask whose vertical axis is ARC LENGTH round the
   tube, and sampled through asin(), so lettering near the top and bottom of the
   tube is foreshortened the way print on a cylinder is.

   One entry of cache: the wrap only changes with zoom, colour or the mark
   loading, and a repaint for a string colour should not redo it. */
var HD_WRAP = { key: "", cv: null };
function hdWrap(ctx, x, cy, R, shrinkHex, G, envOf, edge){
  if (typeof document === "undefined" || !ctx.getTransform) return false;
  var sc = ctx.getTransform().a;
  if (!(sc > 0)) return false;
  var V = G.V, rad = G.rad, w = G.w, bulge = G.bulge, hard = G.hard, ribbed = G.ribbed;
  var sh = shrinkHex || "#141419";
  var pad = 2, top = rad + bulge;
  var Wc = 2*w + 2*pad, Hc = 2*top + 2*pad;
  var pw = Math.ceil(Wc*sc), ph = Math.ceil(Hc*sc);
  if (pw < 4 || ph < 4 || pw*ph > 6e6) return false;
  var img0 = V.mark !== "text" ? markImage() : null;
  var ready = !!(img0 && img0.complete && img0.naturalWidth);
  var key = [pw, ph, sc.toFixed(3), R.toFixed(3), sh, V.mark, ready, V.nocks, V.pitch].join("|");
  if (HD_WRAP.key !== key) {
    var cv = document.createElement("canvas"); cv.width = pw; cv.height = ph;
    var c2 = cv.getContext("2d");
    if (!c2 || !c2.createImageData) return false;

    /* --- the print, flat, in (distance along the tube, arc length round it) --- */
    var arc = Math.PI * rad;                       // top of the tube to the bottom, round the front
    var mh2 = Math.ceil((arc + 2*pad) * sc);
    var mk = document.createElement("canvas"); mk.width = pw; mk.height = mh2;
    var m = mk.getContext("2d");
    m.setTransform(sc, 0, 0, sc, (pad + w)*sc, (pad + arc/2)*sc);
    m.fillStyle = "#fff";
    var printed = false;
    if (V.mark !== "text") {
      if (ready) {
        var word = V.mark === "logo+word";
        var mw = ribbed * V.markScale * (word ? 0.86 : 1);
        var mhh = mw * (img0.naturalHeight / img0.naturalWidth);
        if (mhh * renderScale() >= 26) {
          m.drawImage(markTinted(img0, "#ffffff"), -mw*0.5, -mhh*0.5 - (word ? rad*0.16 : 0), mw, mhh);
          printed = true;
        }
      }
    } else {
      var face = "800 %spx ui-sans-serif, system-ui, Arial, sans-serif";
      m.font = face.replace("%s", (rad*0.46).toFixed(1));
      var mw0 = m.measureText("BOWSTRINGS").width, usable = ribbed*0.94;
      var big = mw0 > usable ? rad*0.46*(usable/mw0) : rad*0.46, small = big*0.68;
      if (big*renderScale() >= 7.5 && small*renderScale() >= 5.0) {
        m.textAlign = "center"; m.textBaseline = "middle";
        m.font = face.replace("%s", small.toFixed(1)); m.fillText("RIVER CITY", 0, -big*0.56);
        m.font = face.replace("%s", big.toFixed(1));   m.fillText("BOWSTRINGS", 0,  big*0.42);
        printed = true;
      }
    }
    var mask = printed ? m.getImageData(0, 0, pw, mh2).data : null;

    /* --- the material --- */
    var lin = function (c) { return Math.pow(c/255, 2.2); };
    var rgb = rgbOf(sh);
    var aR = lin(rgb[0]), aG = lin(rgb[1]), aB = lin(rgb[2]);
    var darkWrap = relLum(sh) < 0.05;
    var iR = darkWrap ? 0.76 : 0.012, iG = darkWrap ? 0.76 : 0.012, iB = darkWrap ? 0.80 : 0.014;
    /* Key light: above, a little toward the viewer's left, well in front. Screen y runs DOWN, so "above" is negative. */
    var Lx = -0.28, Ly = -0.66, Lz = 0.70, Ll = Math.sqrt(Lx*Lx + Ly*Ly + Lz*Lz); Lx /= Ll; Ly /= Ll; Lz /= Ll;
    var Hx = Lx, Hy = Ly, Hz = Lz + 1, Hl = Math.sqrt(Hx*Hx + Hy*Hy + Hz*Hz); Hx /= Hl; Hy /= Hl; Hz /= Hl;
    var out = c2.createImageData(pw, ph), D = out.data;
    var GAM = 1/2.2, e = 0.75/sc;
    for (var px = 0; px < pw; px++) {
      var d = (px + 0.5)/sc - pad - w;
      if (d < -w || d > w) continue;
      var r = edge(d);
      var dl = Math.max(-w, d - e), dr = Math.min(w, d + e);
      var slope = (edge(dr) - edge(dl)) / (dr - dl || 1);
      /* how far down into a joint between two nocks this column is: 0 on a crown, 1 at the bottom */
      var occ = Math.abs(d) <= hard ? Math.max(0, Math.min(1, (rad + bulge - r) / bulge)) : 0;
      var ao = 1 - 0.55 * occ * occ;
      for (var py = 0; py < ph; py++) {
        var y = (py + 0.5)/sc - pad - top;
        var cov = (r - Math.abs(y)) * sc + 0.5;
        if (cov <= 0) continue;
        if (cov > 1) cov = 1;
        var sy = y / r; if (sy > 1) sy = 1; else if (sy < -1) sy = -1;
        var nz = Math.sqrt(1 - sy*sy);
        var nx = -slope, ny = sy, nl = Math.sqrt(nx*nx + ny*ny + nz*nz);
        nx /= nl; ny /= nl; nz /= nl;
        var ndl = nx*Lx + ny*Ly + nz*Lz; if (ndl < 0) ndl = 0;
        var sky = 0.5 - 0.5*ny;                    // 1 facing straight up, 0 straight down
        var bounce = ny > 0 ? ny : 0;              // the pale stage, lighting the underside
        var light = (0.16 + 0.86*ndl + 0.20*sky + 0.26*bounce*bounce) * ao;
        light *= 0.80 + 0.20*Math.sqrt(nz);        // the surface turning away at the silhouette
        var ndh = nx*Hx + ny*Hy + nz*Hz; if (ndh < 0) ndh = 0;
        var spec = (0.46*Math.pow(ndh, 80) + 0.040*Math.pow(ndh, 12)) * ao;
        var cr = aR, cg = aG, cb = aB;
        if (mask) {
          var s = rad * Math.asin(sy);             // arc length from the middle of the tube
          var my = Math.floor((s + pad + arc/2) * sc);
          if (my >= 0 && my < mh2) {
            var ia = mask[(my*pw + px)*4 + 3] / 255;
            if (ia > 0) { cr += (iR - cr)*ia; cg += (iG - cg)*ia; cb += (iB - cb)*ia; spec *= 1 - 0.80*ia; }
          }
        }
        var vr = cr*light + spec, vg = cg*light + spec, vb = cb*light + spec;
        var o = (py*pw + px)*4;
        D[o]   = 255 * Math.pow(vr > 1 ? 1 : vr, GAM);
        D[o+1] = 255 * Math.pow(vg > 1 ? 1 : vg, GAM);
        D[o+2] = 255 * Math.pow(vb > 1 ? 1 : vb, GAM);
        D[o+3] = 255 * cov;
      }
    }
    c2.putImageData(out, 0, 0);
    HD_WRAP.key = key; HD_WRAP.cv = cv;
  }
  /* Laid on whole device pixels, so the image is not resampled on the way down. */
  var ox = Math.round((x - w - pad) * sc) / sc, oy = Math.round((cy - top - pad) * sc) / sc;
  ctx.drawImage(HD_WRAP.cv, ox, oy, pw / sc, ph / sc);
  return true;
}

function drawSpeedNock(ctx, x, cy, R, shrinkHex){
  const G = shrinkGeom(R);
  const V = G.V, rad = G.rad, od = G.od, seg = G.seg, n = G.n;
  const ribbed = G.ribbed, cone = G.cone, skirt = G.skirt, wallR = G.wallR;
  /* THE DESCENT STARTS AT THE END NOCK'S OUTER FACE (see G.hard) and leaves from
     the plateau, so the end nocks are the same width and shape as the rest. Quinn, fifth pass: "do you see how the last speed nock on either
     side is larger than the rest?" — it was, and this is why. Pass four held the
     end crowns out to the hardware edge to stop a false groove being drawn
     there, which made those two segments half a nock wider than every other one.

     A nock's crown is at its centre. Inboard of it the tube pinches into the gap
     before the next nock; outboard there is no next nock, so the tube simply
     falls away. So the surface descends from the last CROWN — every segment the
     same width, no groove at the ends, and the end segments read slightly
     smaller than the interior ones because they sit on the falling envelope,
     which is what the photograph shows. */
  const half = G.half, hard = G.hard, w = G.w, bulge = G.bulge;
  const lastCrown = G.lastCrown;
  const top = cy - rad, bot = cy + rad;

  /* The envelope: full diameter over the hardware, then the closing.

     THE CLOSING FOLLOWS THE END NOCK'S CONTOUR, which is what makes it read as
     tubing shrunk over hardware rather than a moulded cone. `1 - (1-u)^shape`
     leaves the hardware edge with ZERO slope — so the tube carries the last
     nock's crown right out to the end of it — and arrives at the string with
     its steepest slope, falling off the nock's outer face. Smoothstep, which is
     what the previous pass used, is flat at BOTH ends: it eased away from the
     nock as gently as it landed, which is exactly the long soft cone Quinn
     rejected. shape 0 keeps the old curve for comparison. */
  const envOf = function (d) {          // d = distance from the wrap's centre
    const a = Math.abs(d);
    if (a <= hard) return { r: rad, s: 1 };
    if (a <= hard + cone) {
      /* The closing, from the last crown's full height down to the COLLAR rather
         than to the string — the tubing carries on past this. */
      const u = 1 - (a - hard) / cone;          // 1 at the nock's face, 0 at the collar
      const s = V.shape > 0 ? 1 - Math.pow(1 - u, V.shape) : u*u*(3 - 2*u);
      return { r: wallR + (rad + bulge - wallR)*s, s: s };
    }
    /* The collar: tubing lying on the string at its own wall thickness, ending
       in a cut edge. The edge gets a quarter-circle round-over rather than a
       vertical drop — a cut in shrink is not a knife edge, it has the wall's own
       thickness, and a vertical silhouette aliases besides. */
    const past = (a - hard - cone) / skirt;     // 0 at the closing, 1 at the cut
    const lipAt = 1 - V.lip;
    if (past <= lipAt) return { r: wallR, s: 0 };
    const q = Math.min(1, (past - lipAt) / V.lip);
    return { r: R + (wallR - R) * Math.sqrt(Math.max(0, 1 - q*q)), s: 0 };
  };
  /* The ribs, as one phase running the whole length. Grooves fall at the nock
     boundaries and the swell fades out with the envelope, so the segments get
     smaller as the tube closes instead of stopping dead at the shoulder. */
  /* A NOCK HAS A FLAT TOP, and that is what fixes the end ones.

     Quinn, three passes running, on the end nocks: "larger than the rest", then
     "strange dimensions still". Both were the same underlying thing. A nock is a
     little cylinder: its top is FLAT across its width and the tube dips only at
     the joints between nocks. A sine crown is a rounded hump, which is close
     enough in the middle of a row but wrong at the ends, where the row stops:

       - pass four held the end crown out to the hardware edge to avoid a false
         pinch there, which against rounded neighbours read as a wider nock;
       - pass six started the fall at the end CROWN instead, which made the end
         nock half a nock wide with a pointed top — "strange dimensions".

     So: the crown is a PLATEAU (sin, scaled and clipped), every nock gets the
     same flat top, the phase is held at that plateau past the outer crowns so
     the end nocks keep their tops out to their outer FACE, and only then does
     the tube fall away. All five nocks now have the same width and the same
     profile, and the difference at the ends is that the tube leaves rather than
     dipping into another joint — which is the only thing that should differ. */
  const ribOf = function (d) {
    /* Clamped to the outer crowns, so the end nocks hold their plateau outward
       instead of dipping into a joint that has no next nock on the other side of
       it. The clamp is why `hard` is measured from the crown and not the face. */
    let dd = d;
    if (dd < -lastCrown) dd = -lastCrown;
    if (dd >  lastCrown) dd =  lastCrown;
    const frac = ((dd + lastCrown) / seg + 0.5) % 1;   // peaks ON each crown
    const f = frac < 0 ? frac + 1 : frac;
    return bulge * Math.min(1, Math.sin(Math.PI * f) * V.plateau);
  };
  const edge = function (d) {
    /* Ribs over the hardware only. Past the last crown the envelope already
       carries the bulge down with it, so adding the rib profile there would
       either double it or neck the tube in at the very edge of the nock. */
    const e = envOf(d);
    return Math.abs(d) <= hard ? e.r + ribOf(d) : e.r;
  };

  /* Sampled rather than drawn as beziers: the outline is an envelope times a rib
     profile, and there is no control-point arrangement that is that curve. 30
     samples per nock, which at the 3x supersample is well under a device pixel
     apart. */
  /* Per pixel when it can be; the drawing below is what it replaces and what it falls back to. */
  if (V.hd !== false && hdWrap(ctx, x, cy, R, shrinkHex, G, envOf, edge)) return;

  const steps = Math.max(120, n * 30);
  const outline = function () {
    ctx.beginPath();
    ctx.moveTo(x - w, cy - envOf(-w).r);
    for (let i = 0; i <= steps; i++){
      const d = -w + (2*w) * (i/steps);
      ctx.lineTo(x + d, cy - edge(d));
    }
    ctx.lineTo(x + w, cy + envOf(w).r);
    for (let i = steps; i >= 0; i--){
      const d = -w + (2*w) * (i/steps);
      ctx.lineTo(x + d, cy + edge(d));
    }
    ctx.closePath();
  };
  ctx.save();
  outline();
  ctx.clip();

  /* Heat shrink takes a colour like anything else on the string. Defaults to
     black when nothing is chosen, which is what most sets ship as. */
  const sh = shrinkHex || "#141419";
  const SH = shrinkShade(sh, V);
  const g = ctx.createLinearGradient(0, top, 0, bot);
  g.addColorStop(0,    tone(sh, SH.topMul, SH.topAdd));
  g.addColorStop(0.24, tone(sh, 1.00));
  g.addColorStop(0.64, tone(sh, SH.midMul));
  g.addColorStop(1,    tone(sh, SH.botMul));
  ctx.fillStyle = g; ctx.fillRect(x - w - 2, top - 2, w*2 + 4, rad*2 + 4);

  /* THE COLLAR GETS ITS OWN GRADIENT, over its own radius. Filled by the body
     gradient it took a thin slice out of the middle of that ramp and came out
     flat and dark — a small tube is not the middle of a big one, it is lit the
     same way over a smaller span, which is exactly how the serving beside it is
     drawn. This is also what puts a highlight on the cut edge, which is the part
     that says "the tubing ends here" rather than "the tube gets thinner". */
  if (skirt > od * 0.02) {
    const cg = ctx.createLinearGradient(0, cy - wallR, 0, cy + wallR);
    cg.addColorStop(0,    tone(sh, SH.topMul, SH.topAdd));
    cg.addColorStop(0.24, tone(sh, 1.00));
    cg.addColorStop(0.64, tone(sh, SH.midMul));
    cg.addColorStop(1,    tone(sh, SH.botMul));
    ctx.fillStyle = cg;
    const from = hard + cone;
    ctx.fillRect(x + from, cy - wallR - 1, (w - from) + 2, wallR*2 + 2);
    ctx.fillRect(x - w - 2, cy - wallR - 1, (w - from) + 2, wallR*2 + 2);
  }

  /* THE SHEEN IS ONE STRIP THAT THE GROOVES CUT, not one blob per segment.
     Drawn as separate patches it read as painted dots — which is what the first
     cut of this did, and it looked worse than the slab it replaced. In the
     photograph there is a single lit strip running the length of the tube,
     brightest where the tube is fattest and interrupted at every pinch.

     Faded before it reaches the closings so it is not clipped into a hard
     horizontal edge, which read as a square shoulder. */
  const litHalf = hard + cone*0.5;
  const hl = ctx.createLinearGradient(x - litHalf, 0, x + litHalf, 0);
  const lit = (V.crown*0.85).toFixed(3);
  hl.addColorStop(0,    "rgba(255,255,255,0)");
  hl.addColorStop(0.16, "rgba(255,255,255," + lit + ")");
  hl.addColorStop(0.84, "rgba(255,255,255," + lit + ")");
  hl.addColorStop(1,    "rgba(255,255,255,0)");
  ctx.fillStyle = hl;
  ctx.fillRect(x - litHalf, cy - rad*0.74, litHalf*2, rad*0.22);

  /* and a broad soft swell on each crown, so the strip is brightest where the
     tube is fattest. One extra segment either side of the hardware, because the
     ribs carry on into the closings and so does their shading. */
  for (let i = 0; i < n; i++){
    const d = -lastCrown + i*seg;
    const s = envOf(d).s;
    if (s <= 0.02) continue;
    ctx.save();
    ctx.translate(x + d, cy - rad*0.58);
    ctx.scale(seg*0.46, rad*0.32);
    const rg = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    rg.addColorStop(0,    "rgba(255,255,255," + (V.crown*0.55*s).toFixed(3) + ")");
    rg.addColorStop(0.60, "rgba(255,255,255," + (V.crown*0.22*s).toFixed(3) + ")");
    rg.addColorStop(1,    "rgba(255,255,255,0)");
    ctx.fillStyle = rg;
    ctx.beginPath(); ctx.arc(0, 0, 1, 0, Math.PI*2); ctx.fill();
    ctx.restore();
  }

  /* The grooves. Drawn as shadow rather than as a colour, so they are right on a
     white wrap and a red one without a second set of numbers: a valley is dark
     because it is shaded, not because the tube changes colour. */
  /* A VALLEY IS SHADED, NOT PAINTED BLACK. Quinn, fifth pass: "black is too dark
     for both red and white for segmentations". Pure black multiplies the hue out
     of the tubing, so a groove in the white wrap came out as an inked line and
     the red one went almost to black. A real valley keeps its material's colour:
     it is the wrap darkened, with whatever light reaches the bottom of it.

     Keyed off the wrap's LUMINANCE rather than its name — the same test the
     print ink uses — because a near-black wrap has nowhere to go but black, and
     a groove drawn as dark grey on it would not read at all. So black keeps
     black; everything lighter gets its own colour back. */
  const gRGB = rgbOf(sh);
  const gDarkWrap = relLum(sh) < 0.05;
  const grooveInk = function (alpha) {
    if (gDarkWrap) return "rgba(0,0,0," + alpha.toFixed(3) + ")";
    const k = V.gtone;
    return "rgba(" + Math.round(gRGB[0]*k) + "," + Math.round(gRGB[1]*k) + ","
                   + Math.round(gRGB[2]*k) + "," + alpha.toFixed(3) + ")";
  };

  const grooves = function (alpha) {
    const gw = rad * V.groove;          // half-width: od * groove / 2
    /* Interior boundaries ONLY. A groove is the gap between two nocks; the ends
       of the hardware are not gaps, and shading them there drew a pinch at the
       top of each taper. */
    for (let i = 0; i <= n - 2; i++){
      const d = -lastCrown + (i + 0.5)*seg;
      const e = envOf(d);
      if (e.s <= 0.02) continue;        // past the closing: nothing left to shade
      const lean = (shrinkJitter(i + 2) - 0.5) * 2 * V.cant;
      ctx.save();
      ctx.translate(x + d, cy);
      ctx.transform(1, 0, lean, 1, 0, 0);
      /* A tighter core than a plain three-stop ramp: the dark part is a crease
         at the bottom of the valley, and the walls climbing out of it are only
         slightly shaded. A single ramp across the whole width is the soft
         channel this used to draw. */
      const c = V.gcore;
      const gg = ctx.createLinearGradient(-gw, 0, gw, 0);
      gg.addColorStop(0,             grooveInk(0));
      gg.addColorStop(0.5 - c*0.5,   grooveInk(alpha * e.s * 0.35));
      gg.addColorStop(0.5,           grooveInk(alpha * e.s));
      gg.addColorStop(0.5 + c*0.5,   grooveInk(alpha * e.s * 0.35));
      gg.addColorStop(1,             grooveInk(0));
      ctx.fillStyle = gg;
      ctx.fillRect(-gw, -rad - 2, gw*2, rad*2 + 4);

      /* The shoulders. Two thin highlights just outside the valley, on the lit
         half of the tube only — a lip on the shaded underside catches nothing.
         This is what makes the gap read on white and red. */
      if (SH.shoulder > 0.01) {
        const a2 = SH.shoulder * e.s;
        const sg = ctx.createLinearGradient(0, -rad, 0, rad);
        sg.addColorStop(0,    "rgba(255,255,255," + (a2*0.30).toFixed(3) + ")");
        sg.addColorStop(0.22, "rgba(255,255,255," + a2.toFixed(3) + ")");
        sg.addColorStop(0.62, "rgba(255,255,255," + (a2*0.25).toFixed(3) + ")");
        sg.addColorStop(1,    "rgba(255,255,255,0)");
        ctx.fillStyle = sg;
        const lw = Math.max(0.6, gw*0.42);
        ctx.fillRect(-gw - lw*0.6, -rad - 2, lw, rad*2 + 4);
        ctx.fillRect( gw - lw*0.4, -rad - 2, lw, rad*2 + 4);
      }
      ctx.restore();
    }
  };
  grooves(V.gdark);

  /* The brand on the wrap, the way GAS put theirs there.

     Two short lines, not the full lockup. This was tried once with the whole
     "RIVER CITY CUSTOM BOWSTRINGS" mark and removed because at wrap scale it was
     mush. It is drawn to fit and then checked: below about 5.5px the type stops
     being readable and turns into grey noise, so at that size nothing is drawn
     at all. A blank wrap is honest; a smudge that looks like a rendering fault is
     not. Zooming in with the wheel brings it back.

     Measured across the HARDWARE rather than the whole wrap: the closings are
     turning away from the viewer, and type run across them would be stretched
     over a surface the eye reads as curving. */
  /* --- the bow mark, in place of the type --- */
  if (V.mark !== "text") {
    const img = markImage();
    if (img && img.complete && img.naturalWidth) {
      const lum2 = relLum(sh);
      const dark2 = lum2 < 0.05;
      const ink2   = dark2 ? "rgba(228,228,234,0.94)" : "rgba(16,16,18,0.94)";
      const shade2 = dark2 ? "rgba(0,0,0,0.55)"       : "rgba(255,255,255,0.42)";
      const word = V.mark === "logo+word";
      const mw = ribbed * V.markScale * (word ? 0.86 : 1);
      const mh = mw * (img.naturalHeight / img.naturalWidth);
      /* THE SAME GUARD THE TYPE HAS, in device pixels for the same reason. The
         mark is fine line art — trees, a river, cams — and below about 26 device
         pixels tall those stop being anything and become a smudge that reads as
         a rendering fault. A blank wrap is honest; on a phone at the minimum
         string radius the mark works out around 20px, so it is skipped there and
         comes back on zoom, exactly as the lettering does. */
      if (mh * renderScale() < 26) {
        ctx.restore();
        return;
      }
      const my = cy - mh*0.5 - (word ? rad*0.16 : 0);
      const off = Math.max(0.5, mh*0.045);
      ctx.drawImage(markTinted(img, shade2), x - mw*0.5, my + off, mw, mh);
      ctx.drawImage(markTinted(img, ink2),   x - mw*0.5, my,       mw, mh);
      if (word) {
        const wface = "800 %spx ui-sans-serif, system-ui, Arial, sans-serif";
        let size = rad * 0.30;
        ctx.font = wface.replace("%s", size.toFixed(1));
        const wid = ctx.measureText("BOWSTRINGS").width;
        if (wid > ribbed*0.80) size = size * (ribbed*0.80 / wid);
        if (size * renderScale() >= 5.0) {
          ctx.font = wface.replace("%s", size.toFixed(1));
          ctx.textAlign = "center"; ctx.textBaseline = "middle";
          const wy = my + mh + size*0.70;
          ctx.fillStyle = shade2;
          ctx.fillText("BOWSTRINGS", x, wy + Math.max(0.5, size*0.09));
          ctx.fillStyle = ink2;
          ctx.fillText("BOWSTRINGS", x, wy);
        }
      }
      grooves(V.gdark * V.print);
    }
  }

  /* --- or the two lines of type, which is what shipped --- */
  if (V.mark === "text") {
  const usable = ribbed * 0.94;
  const face = "800 %spx ui-sans-serif, system-ui, Arial, sans-serif";
  const fit = function (text, maxW, cap) {
    ctx.font = face.replace("%s", cap.toFixed(1));
    const measured = ctx.measureText(text).width;
    return measured > maxW ? cap * (maxW / measured) : cap;
  };
  const big = fit("BOWSTRINGS", usable, rad * 0.46);
  const small = big * 0.68;
  /* THE LEGIBILITY TEST IS IN DEVICE PIXELS, NOT CSS PIXELS — see renderScale().
     7.5 device pixels for the large line, 5.0 for the small one. */
  const dpr = renderScale();
  if (big * dpr >= 7.5 && small * dpr >= 5.0) {
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const rows = [
      { t: "RIVER CITY", size: small, y: cy - big * 0.56 },
      { t: "BOWSTRINGS", size: big,   y: cy + big * 0.42 }
    ];
    /* The mark takes its ink from the wrap. Light lettering on the black wrap;
       BLACK on the white and the red, Quinn 2026-08-11. Keyed off the wrap's
       luminance rather than its name, so a fourth stocked colour cannot break
       it. The offset copy flips with it, which is what makes the lettering read
       as pressed into the shrink rather than printed on top of it. */
    const lum = relLum(sh);
    const dark = lum < 0.05;
    const ink   = dark ? "rgba(228,228,234,0.92)" : "rgba(16,16,18,0.92)";
    const shade = dark ? "rgba(0,0,0,0.55)"       : "rgba(255,255,255,0.42)";
    for (const r of rows) {
      ctx.font = face.replace("%s", r.size.toFixed(1));
      ctx.fillStyle = shade;
      ctx.fillText(r.t, x, r.y + Math.max(0.5, r.size * 0.09));
      ctx.fillStyle = ink;
      ctx.fillText(r.t, x, r.y);
    }
    /* The print is ON the tube, so the valleys shade it too. Without this pass
       the lettering floats over the ribs and undoes them. */
    grooves(V.gdark * V.print);
  }
  }

  /* The rim goes last, inside the clip, so it is a half-width hairline hugging
     the silhouette rather than a border sitting outside it.

     Two things stop it reading as an inked outline, which is what a flat grey
     stroke looked like on the white wrap. It is the WRAP'S OWN COLOUR darkened,
     for the same reason the grooves are — an edge turning away from the light is
     still made of the material. And its alpha FADES DOWNWARD, gone by two thirds
     of the way: the top edge is where a white tube disappears into a near-white
     stage and needs the help, while the bottom is already separated by the
     contact shadow under it. A rim of even weight all the way round is a
     drawing convention, not a lighting one. */
  if (SH.rim > 0.004) {
    const rc = rgbOf(sh);
    const rk = 0.45;
    const rimCol = function (a) {
      return "rgba(" + Math.round(rc[0]*rk) + "," + Math.round(rc[1]*rk) + ","
                     + Math.round(rc[2]*rk) + "," + a.toFixed(3) + ")";
    };
    const rg2 = ctx.createLinearGradient(0, top, 0, bot);
    rg2.addColorStop(0,    rimCol(SH.rim));
    rg2.addColorStop(0.45, rimCol(SH.rim * 0.55));
    rg2.addColorStop(0.68, rimCol(0));
    rg2.addColorStop(1,    rimCol(0));
    outline();
    ctx.strokeStyle = rg2;
    ctx.lineWidth = Math.max(1, rad*0.04) * 2;
    ctx.stroke();
  }

  ctx.restore();
}

/* ------------------------------------------------------------
   CLOSE-UP
   A finished string is ~450:1 length to diameter, so drawing the
   whole thing in a card forces a ~25:1 lie and every fitting on
   it reads fat. The way out is not to draw the whole thing: show
   a magnified section, bled off both edges so it is plainly a
   crop. Same thing the sample photos in this category are.
   ------------------------------------------------------------ */
/* ZOOM-OUT STOPS WHERE THE STRING STOPS RENDERING PROPERLY — 2026-09-12.
   Quinn, on a fully zoomed-out Black / Sand / Mountain Berry string: "zoomed out
   it starts looking funny", then "Stop the Zoom mode at the point you think is
   best". ZOOM_MIN 0.6 took the radius to about 6.8px on a desktop, and below 8
   sweepBundle() switches to its low-detail path (meant for tiny pieces), so the
   weave went flat and stripy, the wrap logo drew about 3px tall and garbled, and
   a one-strand pinstripe broke into dots. Rendered at 0.70 / 0.75 / 0.80 / 0.90:
   at 0.70 (radius 8.0) the pinstripe was already breaking; from 0.75 up all
   three held.

   So zoom-out stopped at MIN_RADIUS, the same 8.5px the close-up already uses as
   its floor on narrow screens (see the note below).

   THE FLOOR CAME DOWN TO 6.0 ON 2026-09-18 — Quinn: "Should be able to zoom the
   visualizer out further too", right after pinch started working on phones,
   where this bit hardest: baseRadius is already clamped to 8.5 at phone widths,
   so the floor evaluated to zoom 1.0 and a phone could zoom IN but not out at
   all. Pinching inward did nothing, which reads like the pinch fix half-landing.

   The sweepBundle reasoning above turns out not to apply to this view. Rendering
   the same string at R 7.0 with the cheap path forced on and forced off gives
   PIXEL-IDENTICAL output (getbbox() None, max channel diff 0) — the close-up
   never calls sweepBundle, so the R<8 switch that the 0.70 test was blamed on
   was never reachable from here. What that test actually measured was the
   radius, and 8.5 was simply where the search stopped.

   Re-measured with the pinstripe and second colour that make it hard (Red /
   Sand / Mountain Berry, the build Quinn judged on). At 6.0 the pinstripe still
   runs as one strand and the wrap mark is still readable; at 5.5 the mark goes
   to a smudge. So 6.0, and tools/demo/_zoomout.py regenerates the comparison.

   BOTH RENDERERS WERE CHECKED, because headless Chromium reports gpu TRUE and
   so renders the shader by default — the path most machines take, but not the
   only one. Forcing the 2D fallback shows the known edge sawtooth (see the
   KNOWN ISSUE in sweepBundle) a little stronger at 6.0 than at 8.5, but it is
   already visible at 8.5: pre-existing, not introduced here, and not a reason
   to hold the floor up for the shader path everyone else is on.

   TWO FLOORS, NOT ONE, and the distinction is the whole reason this is safe:
   MIN_RADIUS is the DEFAULT look (baseRadius never renders a string thinner
   than this at rest), MIN_ZOOM_RADIUS is how far the user may pull it back from
   there. Lowering MIN_RADIUS instead would have thinned the string on every
   phone at rest, which is not what was asked for. ZOOM_MIN drops to 0.5 only so
   it stops binding before MIN_ZOOM_RADIUS does on a wide stage (6.0/11.4 =
   0.526); it stays as an absolute limit. */
var MIN_RADIUS = 8.5;
var MIN_ZOOM_RADIUS = 6.0;
/* HOW FAR ALONG THE STRING THE CENTRE SERVING SITS, in string radii from the end
   serving — Quinn, 2026-09-22: "center serving should show when zoomed out all
   the way on the right side in the visualizier". A customer picks a centre
   serving colour and never saw it: the close-up drew the end serving, the wrap,
   and then bare bundle to the edge of the frame.

   It is a DISTANCE ALONG THE STRING, not a fraction of the stage, so it behaves
   the way zooming out should — pull back, more string fits the frame, and the
   centre serving comes into it from the right. Measured on the harness, the bare
   span right of the wrap is 35 radii at rest on a desktop and 75 zoomed out, so
   48 sits off the frame at rest and about a third of the way in at the floor.

   ON A PHONE IT NEVER ARRIVES, and that is arithmetic rather than a decision:
   the phone starts at MIN_RADIUS and can only pull back to 6.0, which leaves 35
   radii — the same view a desktop has AT REST. Showing it there means showing it
   on the desktop at rest too. Lower this number if that is ever wanted. */
var CENTRE_GAP_R = 48;
function baseRadius(w){ return Math.max(MIN_RADIUS, Math.min(11.4, w*0.0192)); }
function stageWidth(){
  var c = $("pvCanvas"), host = c && c.parentElement;
  return host ? Math.max(240, host.clientWidth) : 614;
}
function zoomFloor(){ return Math.max(ZOOM_MIN, MIN_ZOOM_RADIUS / baseRadius(stageWidth())); }

/* THE SHADOW UNDER THE STRING IS BLURRED HERE, NOT BY ctx.filter — 2026-09-13.
   It used to be an ellipse drawn with ctx.filter = "blur(10px)". Safari has never
   shipped canvas filters (caniuse: every Safari and every iOS version, and every
   iPhone browser is Safari underneath), so on Apple devices the filter was ignored
   and the shadow drew as a hard grey bar under the string. Found by rendering the
   live page in Chromium with the filter setter disabled.

   So the same ellipse is blurred in JavaScript: coverage on a 1px grid, then a
   separable Gaussian, cached per size, and drawn as an image, which every browser
   scales the same way. The width matches what Chrome actually drew: a canvas
   filter's blur is in the canvas's OWN pixels, ignoring setTransform, so on the
   3x offscreen canvas "blur(10px)" was 10/3 CSS px, not 10. */
var SHADOW_CACHE = { key: "", canvas: null, W: 0, H: 0 };
/* HOW FAR ACROSS THE STAGE THE CENTRE SERVING LANDS when its row is open.
   Quinn sent the view he wanted with "probably only needs to be that far": the
   band just arriving at the right-hand edge, not a third of the picture. 0.86 is
   also the cap servX already uses, so the two ends of the frame agree. */
var CENTRE_VIEW_AT = 0.86;

/* The zoom that puts the centre serving CENTRE_VIEW_AT across the stage, or null when there
   is no canvas to measure. Uses the FULL gap rather than closeupGeom's capped one
   — the cap is what covers the phone once this has pulled back as far as it can,
   so reading it here would compute a zoom against a distance it had just moved. */
function centreZoomTarget(){
  var c = $("pvCanvas"), host = c && c.parentElement;
  if (!host) return null;
  var w = Math.max(240, host.clientWidth);
  var G = closeupGeom(w, 100, currentCols(), VIEW.phase);
  var k = G.servX / G.R + CENTRE_GAP_R;          // where the band starts, in radii
  var want = (w * CENTRE_VIEW_AT) / k / baseRadius(w);
  return Math.max(zoomFloor(), Math.min(1, want));
}

/* Called wherever the open colour row changes. Pulls back on the way in, and puts
   the zoom back on the way out — but only if the customer has not touched it
   meanwhile, which the wheel, the pinch and the double-click all record by
   clearing S.zoomAuto. Taking back a zoom somebody set themselves would be worse
   than leaving them pulled out. */
function centreFocusZoom(was){
  if (S.activeTab === "centre" && was !== "centre") {
    var t = centreZoomTarget();
    if (t != null && t < (S.zoom || 1) - 0.001) {
      S.zoomWas = S.zoom || 1;
      S.zoomAuto = t;
      S.zoom = t;
    }
  } else if (was === "centre" && S.activeTab !== "centre" && S.zoomAuto != null) {
    if (Math.abs((S.zoom || 1) - S.zoomAuto) < 0.001) S.zoom = S.zoomWas;
    S.zoomAuto = null; S.zoomWas = null;
  }
  renderPreview();
}

function drawContactShadow(ctx, w, sy, R, renderDpr){
  var scale = renderDpr || (ctx.getTransform ? ctx.getTransform().a : 3) || 3;
  var rx = w * 0.44, ry = R * 0.30, sig = 10 / scale, pad = Math.ceil(sig * 3);
  var W = Math.ceil(2 * rx + 2 * pad), H = Math.ceil(2 * ry + 2 * pad);
  var key = W + "x" + H + ":" + ry.toFixed(3) + ":" + sig.toFixed(3);
  if (SHADOW_CACHE.key !== key) {
    var cov = new Float32Array(W * H), tmp = new Float32Array(W * H);
    var cx = W / 2, cyy = H / 2, x, y, k;
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
      var dx = (x + 0.5 - cx) / rx, n = 0;
      for (k = 0; k < 4; k++) {                        // four rows per pixel: the ellipse is only ~7px tall
        var dy = (y + (k + 0.5) / 4 - cyy) / ry;
        if (dx * dx + dy * dy <= 1) n++;
      }
      cov[y * W + x] = n / 4;
    }
    var r = Math.ceil(sig * 3), ker = [], ks = 0;
    for (k = -r; k <= r; k++) { var g = Math.exp(-0.5 * (k / sig) * (k / sig)); ker.push(g); ks += g; }
    for (k = 0; k < ker.length; k++) ker[k] /= ks;
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {   // horizontal
      var acc = 0;
      for (k = -r; k <= r; k++) { var xx = x + k; if (xx >= 0 && xx < W) acc += cov[y * W + xx] * ker[k + r]; }
      tmp[y * W + x] = acc;
    }
    var cvs = document.createElement("canvas"); cvs.width = W; cvs.height = H;
    var sctx = cvs.getContext("2d"), img = sctx.createImageData(W, H), d = img.data;
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {   // vertical, straight into the image
      var a2 = 0;
      for (k = -r; k <= r; k++) { var yy = y + k; if (yy >= 0 && yy < H) a2 += tmp[yy * W + x] * ker[k + r]; }
      var i = (y * W + x) * 4;
      d[i] = 40; d[i + 1] = 36; d[i + 2] = 60; d[i + 3] = Math.round(a2 * 0.20 * 255);
    }
    sctx.putImageData(img, 0, 0);
    SHADOW_CACHE = { key: key, canvas: cvs, W: W, H: H };
  }
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(SHADOW_CACHE.canvas, w / 2 - SHADOW_CACHE.W / 2, sy - SHADOW_CACHE.H / 2);
  ctx.restore();
}

/* ============================================================
   GPU STRING RENDERER — 2026-09-13
   Quinn, after the "even higher quality visualizer" deep dive: "do 1 and 2".

   The 2D renderer below draws the bundle as thousands of stroked polylines,
   one shade per run. At close zoom that shows: strands read as flat ribbons,
   the colour junction and the pinstripe break into blocks where runs end, and
   the serving's leaning hatch strokes alias into a carbon-fibre crosshatch.

   This draws the SAME geometry per pixel on the graphics card instead: the
   same radius, 36R twist, strand count, phase, colour split, pinstripe seams,
   speckle hash and serving pitch as drawCloseup(). For every pixel it works out
   which strand is there and where across that strand, gives each strand a round
   cross-section with a waxed sheen, shades it in linear light, and averages a
   grid of samples inside the pixel. The serving is real wound turns. The
   background, shadow, speed nock, logo, edge fade and caption are still 2D and
   are drawn around it by paint().

   It is only used when it can be trusted: WebGL with high-precision fragment
   floats (the speckle hash needs them), the "gpu_visualiser" section setting on,
   and no ?viz=2d in the URL. Anything else, a shader that fails to compile, or
   a lost context, and paint() uses the 2D renderer exactly as before. */
var GPU = { tried: false, gl: null, canvas: null, progs: {}, dead: false, error: null };

var GPU_FS = [
  "precision highp float;",
  "uniform vec2 uRes; uniform float uDpr;",
  "uniform float uR, uCy, uTwist, uN, uPhase, uServX, uPitch, uPinA, uPinB, uHasPin, uServClear;",
  "uniform float uCentreX, uCentreOn, uCentreClear;",
  "uniform vec3 uMainA, uMainB, uSecA, uSecB, uPin, uServ, uCentre;",
  "uniform float uMainSpeck, uSecSpeck;",
  "const float TAU = 6.28318530718;",
  "float jit(float i, float salt){ return fract(sin(i*12.9898 + salt*78.233)*43758.5453); }",
  "const vec3 L = vec3(-0.34983, -0.71965, 0.59971);",
  "const vec3 V = vec3(0.0, 0.0, 1.0);",
  "vec3 light(vec3 albedo, vec3 Nc, vec3 Nm, float ao, float gloss){",
  "  vec3 H = normalize(L + V);",
  "  float ndl = dot(Nm, L);",
  "  float diff = pow(clamp(ndl*0.5 + 0.5, 0.0, 1.0), 2.2);",
  "  float amb = 0.10 + 0.10*Nc.z;",
  "  float vis = smoothstep(-0.15, 0.35, ndl);",
  "  float nh = max(0.0, dot(Nm, H));",
  "  float spec = pow(nh, 46.0) * 0.30 * gloss + pow(nh, 7.0) * 0.035;",
  "  float rim = pow(1.0 - Nc.z, 3.0) * clamp(Nc.y, 0.0, 1.0);",
  "  float fill = max(0.0, dot(Nm, vec3(0.24938, 0.54863, 0.79800))) * 0.22;",
  "  vec3 c = albedo * (amb + diff*1.15 + fill) * ao * 1.28 + vec3(spec) * vis * ao + vec3(0.55, 0.65, 0.85) * rim * 0.10;",
  "  return c * mix(0.62, 1.0, pow(Nc.z, 0.6));",
  "}",
  // the bundle at a point on the front face: which strand, where across it, what colour
  "vec3 bundle(vec2 p, float th, vec3 Nc){",
  "  float s = p.x + 30.0;",
  "  float phi = th - TAU * s / uTwist - uPhase;",
  "  float f = phi / TAU * uN;",
  "  float fi = floor(f + 0.5);",
  "  float t = f + 0.5 - fi;",
  "  float idx = mod(fi, uN);",
  // the pinstripe is two strands wide CENTRED on its seam strand, as drawCloseup() strokes it:
  // inside it, re-grid so it is two whole strands from f = seam-1 to seam+1
  "  float dA = mod(f - uPinA + uN * 0.5, uN) - uN * 0.5;",
  "  float dB = mod(f - uPinB + uN * 0.5, uN) - uN * 0.5;",
  "  bool isPin = uHasPin > 0.5 && (abs(dA) < 1.0 || abs(dB) < 1.0);",
  "  if (isPin) { t = fract((abs(dA) < 1.0 ? dA : dB) + 1.0); }",
  "  float cr = (t - 0.5) * 2.0;",
  "  float k = TAU * uR / uTwist;",
  "  vec3 T = normalize(vec3(1.0, k * cos(th), -k * sin(th)));",
  "  vec3 B = normalize(cross(T, Nc));",
  "  float jw = 0.90 + 0.10 * jit(idx, 1.0);",
  "  float jb = 0.94 + 0.10 * jit(idx, 2.0);",
  "  float b = sqrt(max(0.0, 1.0 - cr*cr));",
  "  vec3 Nm = normalize(Nc * b + B * cr * 0.9);",
  "  float ao = mix(0.58, 1.0, pow(b, 0.45 * jw));",
  "  float half_ = floor(uN / 2.0 + 0.5);",
  "  vec3 albedo;",
  "  if (isPin) albedo = uPin;",
  "  else {",
  "    bool isMain = idx < half_;",
  "    vec3 A = isMain ? uMainA : uSecA; vec3 C = isMain ? uMainB : uSecB; float sp = isMain ? uMainSpeck : uSecSpeck;",
  "    albedo = A;",
  "    if (sp > 0.5) {",
  "      float seg = uR * 0.42 * (0.68 + 0.64 * jit(idx, 9.0));",
  "      float kk = floor((s + jit(idx, 8.0) * seg * 7.0) / seg);",
  "      if (jit(idx * 131.0 + kk, 7.0) < 0.26) albedo = C;",
  "    }",
  "  }",
  "  bool seam = (idx == 0.0 && t < 0.5) || (idx == half_ && t < 0.5) || (idx == mod(half_ - 1.0, uN) && t > 0.5) || (idx == uN - 1.0 && t > 0.5);",
  "  if (seam) ao *= mix(0.55, 1.0, abs(cr));",
  "  return light(albedo * jb, Nc, Nm, ao, 1.0);",
  "}",
  "vec4 shade(vec2 p){",
  "  float v = (p.y - uCy) / uR;",
  "  if (abs(v) >= 1.0) return vec4(0.0);",
  "  float th = asin(v);",
  "  vec3 Nc = vec3(0.0, sin(th), cos(th));",
  // the end serving on the left, and the centre serving running off the right
  "  bool inEnd = p.x < uServX;",
  "  bool inCentre = uCentreOn > 0.5 && p.x > uCentreX;",
  "  if (inEnd || inCentre) {",
  // serving: thread wound round the string; across the front face a turn advances half a pitch
  "    float u = (p.x + 30.0 - uPitch * th / TAU) / uPitch;",
  "    float cr = (fract(u) - 0.5) * 2.0;",
  "    float b = sqrt(max(0.0, 1.0 - cr*cr));",
  "    vec3 Nm = normalize(Nc * b + vec3(1.0, 0.0, 0.0) * cr * 0.85);",
  "    float ao = mix(0.45, 1.0, pow(b, 0.5));",
  "    vec3 col = inEnd ? uServ : uCentre;",
  "    float clearThread = inEnd ? uServClear : uCentreClear;",
  "    vec3 c;",
  "    if (clearThread > 0.5) {",
  // clear thread: the bundle shows through, the turns only add light and shadow
  "      vec3 under = bundle(p, th, Nc);",
  "      float nh = max(0.0, dot(Nm, normalize(L + V)));",
  "      c = under * mix(0.62, 1.05, b) + vec3(pow(nh, 30.0) * 0.35);",
  "    } else {",
  "      c = light(col, Nc, Nm, ao, 0.8);",
  "    }",
  // the thread beds down where a band ends: the left band on its right edge, the right band on its left
  "    float edge = inEnd ? uServX - p.x : p.x - uCentreX;",
  "    if (edge < 1.8) c *= 0.58;",
  "    return vec4(c, 1.0);",
  "  }",
  "  return vec4(bundle(p, th, Nc), 1.0);",
  "}",
  "void main(){",
  "  vec2 frag = vec2(gl_FragCoord.x, uRes.y - gl_FragCoord.y);",
  "  vec4 acc = vec4(0.0);",
  "  for (int i = 0; i < SAMPLES; i++) for (int j = 0; j < SAMPLES; j++) {",
  "    vec2 o = (vec2(float(i), float(j)) + 0.5) / float(SAMPLES);",
  "    vec4 c = shade((frag - 0.5 + o) / uDpr);",
  "    acc += vec4(c.rgb * c.a, c.a);",
  "  }",
  "  acc /= float(SAMPLES * SAMPLES);",
  "  vec3 rgb = acc.a > 0.0 ? acc.rgb / acc.a : vec3(0.0);",
  "  rgb = rgb / (1.0 + 0.06 * rgb);",
  "  gl_FragColor = vec4(pow(clamp(rgb, 0.0, 1.0), vec3(1.0/2.2)), acc.a);",
  "}"
].join("\n");

function gpuWanted() {
  if (CFG.gpuVisualiser === false || CFG.gpuVisualiser === "false") return false;
  if (/[?&]viz=2d\b/.test(location.search)) return false;
  return !GPU.dead;
}
function gpuContext() {
  if (GPU.tried) return GPU.gl;
  GPU.tried = true;
  try {
    var c = document.createElement("canvas");
    var gl = c.getContext("webgl", { premultipliedAlpha: false, antialias: false, preserveDrawingBuffer: true, alpha: true });
    if (!gl) { GPU.error = "no webgl"; return null; }
    var hp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    if (!hp || hp.precision < 16) { GPU.error = "no highp"; return null; }
    c.addEventListener("webglcontextlost", function (e) {
      e.preventDefault(); GPU.dead = true; GPU.gl = null; GPU.error = "context lost";
      try { renderPreview(); } catch (err) {}
    });
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    GPU.canvas = c; GPU.gl = gl;
    return gl;
  } catch (err) {
    GPU.error = String(err && err.message || err); return null;
  }
}
function gpuProgram(gl, samples) {
  if (GPU.progs[samples]) return GPU.progs[samples];
  var sh = function (type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader compile failed");
    return s;
  };
  var pr = gl.createProgram();
  gl.attachShader(pr, sh(gl.VERTEX_SHADER, "attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }"));
  gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, "#define SAMPLES " + samples + "\n" + GPU_FS));
  gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(pr) || "link failed");
  var u = {};
  ["uRes", "uDpr", "uR", "uCy", "uTwist", "uN", "uPhase", "uServX", "uPitch", "uPinA", "uPinB", "uHasPin", "uServClear",
   "uCentreX", "uCentreOn", "uCentreClear",
   "uMainA", "uMainB", "uSecA", "uSecB", "uPin", "uServ", "uCentre", "uMainSpeck", "uSecSpeck"].forEach(function (n) {
    u[n] = gl.getUniformLocation(pr, n);
  });
  GPU.progs[samples] = { pr: pr, u: u, a: gl.getAttribLocation(pr, "a") };
  return GPU.progs[samples];
}
function linRGB(hex) {
  var c = rgbOf(hex);
  return [Math.pow(c[0] / 255, 2.2), Math.pow(c[1] / 255, 2.2), Math.pow(c[2] / 255, 2.2)];
}
/* Draws the string body and serving for the close-up into GPU.canvas at device
   pixels and returns that canvas, or null when the 2D renderer should be used. */
function gpuRenderString(w, h, dev, g, cols) {
  if (!gpuWanted()) return null;
  var gl = gpuContext();
  if (!gl) return null;
  try {
    var samples = dev >= 2 ? 3 : 4;
    var P = gpuProgram(gl, samples), u = P.u;
    var c = GPU.canvas, bw = Math.round(w * dev), bh = Math.round(h * dev);
    if (c.width !== bw || c.height !== bh) { c.width = bw; c.height = bh; }
    gl.viewport(0, 0, bw, bh);
    gl.useProgram(P.pr);
    gl.enableVertexAttribArray(P.a);
    gl.vertexAttribPointer(P.a, 2, gl.FLOAT, false, 0, 0);
    var pair = function (hex) {
      var parts = String(hex).split("|");
      return { a: linRGB(parts[0]), b: linRGB(parts[1] || parts[0]), speck: parts.length > 1 ? 1 : 0 };
    };
    var m = pair(cols.main), s2 = pair(cols.second || cols.main);
    gl.uniform2f(u.uRes, bw, bh); gl.uniform1f(u.uDpr, dev);
    gl.uniform1f(u.uR, g.R); gl.uniform1f(u.uCy, g.cy); gl.uniform1f(u.uTwist, g.twist);
    gl.uniform1f(u.uN, g.N); gl.uniform1f(u.uPhase, g.phase);
    gl.uniform1f(u.uServX, cols.servingShown === false ? -1e5 : g.servX);   // no end serving: the band starts nowhere
    gl.uniform1f(u.uPitch, Math.max(1.5, g.R * 0.18));
    gl.uniform1f(u.uPinA, g.seams[0]); gl.uniform1f(u.uPinB, g.seams.length > 1 ? g.seams[1] : g.seams[0]);
    gl.uniform1f(u.uHasPin, cols.pin ? 1 : 0);
    gl.uniform1f(u.uServClear, cols.servingClear ? 1 : 0);
    gl.uniform1f(u.uCentreX, g.centreX);
    gl.uniform1f(u.uCentreOn, (cols.centreShown && g.centreX < w) ? 1 : 0);
    gl.uniform1f(u.uCentreClear, cols.centreClear ? 1 : 0);
    gl.uniform3fv(u.uMainA, m.a); gl.uniform3fv(u.uMainB, m.b); gl.uniform1f(u.uMainSpeck, m.speck);
    gl.uniform3fv(u.uSecA, s2.a); gl.uniform3fv(u.uSecB, s2.b); gl.uniform1f(u.uSecSpeck, s2.speck);
    gl.uniform3fv(u.uPin, cols.pin ? linRGB(cols.pin) : [0, 0, 0]);
    gl.uniform3fv(u.uServ, linRGB(cols.serving));
    gl.uniform3fv(u.uCentre, linRGB(cols.centre));
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    /* Only the band the string occupies is shaded: the stage is several times
       taller than the string, and every pixel outside it would run the sample
       loop for nothing. GL counts rows from the bottom. */
    var y0 = Math.max(0, Math.floor((g.cy - g.R - 2) * dev)), y1 = Math.min(bh, Math.ceil((g.cy + g.R + 2) * dev));
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(0, bh - y1, bw, y1 - y0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.disable(gl.SCISSOR_TEST);
    if (gl.isContextLost()) { GPU.dead = true; return null; }
    return c;
  } catch (err) {
    GPU.dead = true; GPU.error = String(err && err.message || err);
    if (CFG.debug && window.console) console.warn("[rc-builder] GPU renderer off:", GPU.error);
    return null;
  }
}

/* The close-up's geometry, shared by the 2D and GPU renderers so they cannot
   drift apart. See drawCloseup() for why each number is what it is. */
function closeupGeom(w, h, cols, phase){
  const R = baseRadius(w) * Math.max(S.zoom || 1, MIN_ZOOM_RADIUS / baseRadius(w));
  const dens = 1.5;
  const N = Math.round(26 * Math.pow(dens, 0.62));
  const seams = cols.second ? [0, Math.round(N / 2)] : [0];
  /* THE SERVING SHOWS ON BOTH SIDES OF THE SPEED NOCKS — Quinn, 2026-09-17:
     "even if its just a small amount". A flat 50% of the frame could not promise
     that: the wrap is as long as the hardware it covers, so at high zoom it
     outgrew its serving and ran to the edge of the picture, shrunk round
     nothing. The serving is sized from the wrap instead, with a margin of bare
     thread each side, and the wrap's position comes out of the same sum so the
     two cannot disagree.

     IT LIVES HERE because closeupGeom is shared by the 2D renderer and the GPU
     shader (uServX) so the two cannot drift. Changing the 2D call site alone
     would have left the serving at 50% wherever the GPU path runs, which is
     most machines. A crossbow or a recurve has no speed nocks and keeps the
     old 50%. */
  const rcNock = nocksIncluded();
  const rcMargin = rcNock ? shrinkServingMargin(R) : 0;
  const rcSpan = rcNock ? shrinkGeom(R).w : 0;
  const rcNockX = rcMargin + rcSpan;
  /* The right margin is its own number (see shrinkServingMarginRight); rcMargin
     still sets where the wrap SITS, so widening the right side does not move it. */
  const rcMarginR = rcNock ? shrinkServingMarginRight(R) : 0;
  const rcServX = rcNock ? Math.min(w*0.86, rcNockX + rcSpan + rcMarginR) : w*0.50;
  /* Scales with R for the same reason servX does: both are lengths along the
     string, so the whole picture zooms as one. */
  /* WHILE THE CENTRE SERVING ROW IS OPEN IT IS BROUGHT INTO THE FRAME — Quinn,
     2026-09-26: "it should zoom out to show center serve automatically when
     someone selects their center serve colours". Opening the row pulls the zoom
     back (see centreFocusZoom), which is enough on a desktop. It is NOT enough
     on a phone: there the string starts at MIN_RADIUS and only pulls back to
     6.0, which still leaves the band off the stage — so while that row is open
     the gap is also capped at whatever puts it CENTRE_VIEW_AT across. Nothing else moves,
     and the moment the customer leaves the row the full 48 radii are back. */
  var gapR = CENTRE_GAP_R;
  if (S.activeTab === "centre") {
    gapR = Math.min(CENTRE_GAP_R, Math.max(10, (w * CENTRE_VIEW_AT - rcServX) / R));
  }
  const rcCentreX = rcServX + gapR * R;
  return { R: R, N: N, dens: dens, seams: seams, cy: h*0.50, servX: rcServX,
           centreX: rcCentreX,
           nockX: rcNockX, nockHalf: rcSpan, twist: R*TWIST_PER_R, phase: phase };
}

/* layer: undefined draws everything (the 2D renderer); "under" is only what sits
   beneath the string (the shadow); "over" is only what sits on top of it (speed
   nock, edge fade, caption). The GPU path draws under, the string, then over. */
function drawCloseup(ctx, w, h, cols, phase, layer, renderDpr){
  /* This is the close-up: let it fill its frame. Capped at 11px it read as a
     thin cord in a lot of empty stage, which is most of why it looked like a
     diagram rather than a photograph. */
  /* Pulled back 20% from the 19px it filled before, then a further 25% once the
     stage grew: the canvas now sizes off its container, so a wider column made
     the string thicker rather than longer and it read as punched in. Radius sets
     the zoom — smaller R is the same string seen from further back, so more of
     its length fits the frame and the twist has room to read. */
  /* THE FLOOR IS 8.5, NOT 6. On a phone the canvas is about 322px wide, so
     w*0.0192 gives 6.2 against a desktop's 10.6 — the same drawing seen from
     much further back, with the twist and the filaments too small to resolve.
     That is the "less refined on mobile" Quinn reported, and it was never a
     rendering-quality problem, just zoom.

     ONLY THE FLOOR MOVED, deliberately. Rewriting this to follow the stage
     HEIGHT instead read better in principle — height is what the thickness
     has to sit inside — but it shifted desktop from 10.6 to 11.3, and a
     tighter crop slid the two-tone string's second colour under t_click's
     speckle scanline. Half an hour went into re-deriving that test before the
     obvious point landed: the desktop render was never the thing being
     complained about. Raising the floor fixes the one case that was wrong and
     leaves every wider canvas byte-identical. */
  /* Clamped here as well as in the handlers: a window narrowed after zooming
     out raises the floor, and the drawing must never fall below it. */
  const G = closeupGeom(w, h, cols, phase);
  const R = G.R;
  const dens = G.dens;   // fewer, slightly coarser strands so the twist still resolves at this width
  const N = G.N;
  /* The pinstripe rides the seam between the two colour bundles, which is where
     it actually sits on the jig — index 4 put it buried inside the main bundle
     looking like a stray strand.

     There are two seams, at 0 and N/2, and index 0 is the one that faces the
     camera across the visible crop. N/2 is on the back face for the whole
     length shown and the pinstripe simply disappears — the twist is lazy enough
     now that a single strand does not come round within the frame. */
  /* THE PINSTRIPE GOES AT EVERY COLOUR JUNCTION, not just one of them.
     Quinn, 2026-08-11: on a two-tone string the pinstripe has to show where the
     two colours meet, and it was only showing at one of the two meetings.

     strandColour splits the bundle in half by index, so a two-colour string has
     TWO seams — at strand 0 and at strand N/2. Pinning only strand 0 meant the
     colour junction came round twice per revolution while the pinstripe came
     round once. That reads as the pinstripe twisting at half the rate of
     everything around it, which is exactly how it was reported, even though its
     helix was always identical to its neighbours'. Nothing was wrong with the
     twist; the stripe was in one of the two places it belonged. */
  const seams = G.seams;
  const opt = { main:cols.main, second:cols.second, pin:cols.pin, phase,
                N, twist: R*TWIST_PER_R, i0:0, iN:N, dens, R,
                pinAt: cols.pin ? seams : null, servCol: cols.serving };

  const cy = h*0.50;
  const poly = polyLine(-30, cy, w+30, cy, Math.max(120, Math.round(w/2)));

  if (layer !== "over") drawContactShadow(ctx, w, cy + R*1.6, R, renderDpr);
  if (layer === "under") return;

  if (!layer) {
  sweepBundle(ctx, poly, R, opt);
  bundleForm(ctx, -30, w+30, cy, R);

  /* Bare bundle on the left, running into the end serving on the right with a
     speed nock sitting on it. A crop of just bare string shows the colours but
     none of the build; this way the same frame carries the twist, the serving
     and the nock without pretending to show a whole string. */
  /* Serving and nock on the left, bare bundle running off to the right. The
     colours are what the customer is choosing, so they get the open end of the
     frame and the last thing the eye lands on. */
  const servX = G.servX;
  // serving sits flush with the bundle, not proud of it
  if (cols.servingShown !== false) drawServing(ctx, -30, servX, cy, R, cols.serving, phase, R, cols.servingClear);
  /* The centre serving, running off the right edge — see CENTRE_GAP_R. Skipped
     when the row is not offered (a crossbow, or a single piece that is not the
     bowstring) so the frame never shows a surface the customer cannot choose,
     and skipped when it lands off the stage, which is where it sits at rest. */
  if (cols.centreShown && G.centreX < w) {
    drawServing(ctx, G.centreX, w+30, cy, R, cols.centre, phase, R, cols.centreClear);
  }
  }
  /* Compounds only. A crossbow or a recurve never carries a speed nock, and
     drawing one showed the customer a part that is not on the string they are
     buying. Recorded at the call site so a test can read what was drawn
     rather than re-deriving the rule and agreeing with itself. */
  RENDER_INFO.speedNock = nocksIncluded();
  RENDER_INFO.nockAt = RENDER_INFO.speedNock ? G.nockX / w : null;
  RENDER_INFO.nockHalf = RENDER_INFO.speedNock ? G.nockHalf / w : null;
  /* Where the serving ends, as a fraction of the stage like the two above.
     Recorded so how much thread shows past the wrap can be MEASURED rather
     than eyeballed off a screenshot — it is the difference of two of these. */
  RENDER_INFO.servAt = G.servX / w;
  /* Where the centre serving starts, on the same scale. 1 or more means it is
     off the right of the stage, which is what rest looks like; null means the
     row is not offered at all. */
  RENDER_INFO.centreAt = cols.centreShown ? G.centreX / w : null;
  if (RENDER_INFO.speedNock) drawSpeedNock(ctx, G.nockX, cy, R, cols.shrink);

  // depth of field toward the crop edges
  const fade = ctx.createLinearGradient(0,0,w,0);
  fade.addColorStop(0,    "rgba(246,246,250,0.62)");
  fade.addColorStop(0.14, "rgba(244,244,248,0)");
  fade.addColorStop(0.86, "rgba(244,244,248,0)");
  fade.addColorStop(1,    "rgba(246,246,250,0.62)");
  ctx.fillStyle = fade; ctx.fillRect(0,0,w,h);

  const bits = [S.colours.main];
  if(S.colours.second) bits.push(S.colours.second);
  if(S.opts.pin && S.colours.pinstripe) bits.push(S.colours.pinstripe + " pinstripe");
  ctx.fillStyle = "rgba(28,25,45,0.42)";
  const mat = (MATERIALS.find(m=>m.id===S.material)||{}).name || "BCY";
  const cap = (mat + " · " + bits.join(" / ")).toUpperCase();

  /* THE CAPTION HAS TO FIT THE CANVAS IT IS DRAWN ON. At a fixed 8.5px it ran
     straight off the right edge of a phone-width stage — "…ELECTRIC RED P" and
     then nothing, which reads as a bug rather than a caption. Canvas text does
     not wrap and has no overflow, so it has to be measured.

     Shrink to fit first, down to 6.5px; only truncate if it still will not go,
     and then with a real ellipsis so it is obviously deliberate. */
  const room = w - 20;
  let size = 8.5;
  ctx.font = "600 " + size + "px ui-sans-serif,system-ui,sans-serif";
  while (ctx.measureText(cap).width > room && size > 6.5) {
    size -= 0.25;
    ctx.font = "600 " + size.toFixed(2) + "px ui-sans-serif,system-ui,sans-serif";
  }
  let shown = cap;
  if (ctx.measureText(shown).width > room) {
    while (shown.length > 4 && ctx.measureText(shown + "…").width > room) {
      shown = shown.slice(0, -1);
    }
    shown = shown.replace(/[\s·\/]+$/, "") + "…";
  }
  ctx.fillText(shown, 10, 14);
}

/* ---- scene ---- */
function currentCols(){
  return {
    main:    hexOf(S.colours.main),
    second:  S.colours.second ? hexOf(S.colours.second) : hexOf(S.colours.main),
    serving: hexOf(S.colours.serving),
    centre:  hexOf(S.colours.centre),
    /* Clear is not a colour to paint with — it is the absence of one. The
       renderer needs to know so it can let the bundle show through instead of
       laying a pale grey wrap over it. */
    servingClear: isServingOnly(S.colours.serving),
    centreClear:  isServingOnly(S.colours.centre),
    /* The renderers must not draw a surface the customer was never offered —
       same rule as the slip and the caption chips, and the same one function. */
    centreShown:  surfaceShown("centre"),
    servingShown: surfaceShown("serving"),
    shrink:  S.colours.shrink ? hexOf(S.colours.shrink) : null,
    pin:     (S.opts.pin && S.colours.pinstripe) ? hexOf(S.colours.pinstripe) : null
  };
}

function paint(canvas){
  if(!canvas) return;
  const host = canvas.parentElement;
  const w = Math.max(240, host.clientWidth);
  /* Height follows width, but stays a letterbox. A string is a long thin thing:
     at w*0.60 the stage came out nearly square and the string floated in an
     ocean of empty grey, which looked far worse than the fixed 84 it replaced.
     w*0.26 keeps the crop tight while still growing with the column.
     `data-h` still wins, for any caller that wants an exact size. */
  /* Floor raised from 96 to 118. A phone-width stage hits this floor — 322 wide
     would want 84 — and at 96 the string sat in a letterbox too shallow to show
     the twist, which is now what sets the radius as well. 22px of extra height
     buys a visibly better render for the one layout that needed it; every wider
     stage computes above the floor and is untouched. */
  const h = canvas.dataset.h ? +canvas.dataset.h
          : Math.round(Math.max(118, Math.min(210, w * 0.26)));
  /* DRAWN AT 3x OFF SCREEN, DOWNSCALED BY US — 2026-09-12 (Quinn: "deploy b
     with a rollback ready"; the evidence is the "String Render Quality" demo).

     The drawing is unchanged and still supersampled at renderScale(). What
     changed is who shrinks it. The visible canvas used to BE the 3x bitmap and
     the page scaled it down in CSS, which the browser does with its cheapest
     filter: on a 1x monitor that discards two pixels in three and the
     filaments alias into a checkerboard. Measured against a high-quality
     downscale of the identical bitmap: 97.3 error before, 53.5 after, on a
     single colour. 2x and 3x screens were already fine and stay the same.

     So the scene is drawn into an off-screen canvas at 3x, and the visible
     canvas is sized to the screen's REAL pixels (CSS size x devicePixelRatio)
     and filled with one high-quality drawImage. It costs about 3% per repaint.
     A screen above 3x draws off screen at its own ratio so nothing is ever
     upscaled.

     Rollback: rollback/rc-builder.pre-hq-downscale.js, git tag pre-hq-downscale. */
  const dev = Math.max(1, window.devicePixelRatio || 1);
  const dpr = Math.max(renderScale(), Math.ceil(dev));
  const bw = Math.round(w*dev), bh = Math.round(h*dev);
  if(canvas.width !== bw || canvas.height !== bh){
    canvas.width = bw; canvas.height = bh;
  }
  canvas.style.width = w+"px"; canvas.style.height = h+"px";
  const off = canvas._rcOff || (canvas._rcOff = document.createElement("canvas"));
  if(off.width !== Math.round(w*dpr) || off.height !== Math.round(h*dpr)){
    off.width = Math.round(w*dpr); off.height = Math.round(h*dpr);
  }
  const ctx = off.getContext("2d");
  ctx.setTransform(dpr,0,0,dpr,0,0);
  ctx.clearRect(0,0,w,h);
  const out = canvas.getContext("2d");
  const cols = currentCols();
  const stageBg = (c) => {
    const bg = c.createRadialGradient(w*0.5, h*0.42, 0, w*0.5, h*0.42, w*0.62);
    bg.addColorStop(0, "rgba(255,255,255,0.55)");
    bg.addColorStop(1, "rgba(0,0,0,0)");
    c.fillStyle = bg; c.fillRect(0,0,w,h);
  };

  /* GPU renderer when it is available (see GPU STRING RENDERER): background and
     shadow straight onto the visible canvas, the string at device pixels on top,
     then the speed nock, fade and caption drawn at 3x and downscaled as before. */
  const glc = gpuRenderString(w, h, dev, closeupGeom(w, h, cols, VIEW.phase), cols);
  RENDER_INFO.gpu = !!glc;
  RENDER_INFO.gpuError = GPU.error;
  if (glc) {
    out.setTransform(dev,0,0,dev,0,0);
    out.clearRect(0,0,w,h);
    stageBg(out);
    drawCloseup(out, w, h, cols, VIEW.phase, "under", dpr);
    out.setTransform(1,0,0,1,0,0);
    out.drawImage(glc, 0, 0, bw, bh);
    drawCloseup(ctx, w, h, cols, VIEW.phase, "over", dpr);
    out.imageSmoothingEnabled = true;
    out.imageSmoothingQuality = "high";
    out.drawImage(off, 0, 0, off.width, off.height, 0, 0, bw, bh);
    return;
  }

  stageBg(ctx);
  drawCloseup(ctx, w, h, cols, VIEW.phase);

  out.setTransform(1,0,0,1,0,0);
  out.clearRect(0,0,bw,bh);
  out.imageSmoothingEnabled = true;
  out.imageSmoothingQuality = "high";
  out.drawImage(off, 0, 0, off.width, off.height, 0, 0, bw, bh);
}

/* ---- factory specs for the bows we have them for ---- */
const BOW_SPECS = {
  "Mathews|ARC 30":            { ata:'30"',     bh:'6"',   ibo:'348 fps' },
  "Mathews|ARC 34":            { ata:'34"',     bh:'6.5"', ibo:'343 fps' },
  "Hoyt|Alpha AX-3 29":        { ata:'29½"',    bh:'6⅜"',  ibo:'340 fps' },
  "Hoyt|Alpha AX-3 33":        { ata:'33⁹⁄₁₆"', bh:'6⅜"',  ibo:'340 fps' },
  "Hoyt|Carbon RX-10":         { ata:'30½"',    bh:'6⅛"',  ibo:'342 fps' },
  "Hoyt|Carbon RX-10 Ultra":   { ata:'33½"',    bh:'6⅜"',  ibo:'340 fps' }
};

/* Wheel zoom on the preview.

   The rule that matters: preventDefault ONLY when the zoom actually changed.
   Swallowing every wheel event over the canvas turns the preview into a trap —
   you reach it scrolling down the page and cannot get past it. At either limit
   the event is left alone and the page scrolls on as normal.

   The listener has to be { passive: false } or preventDefault is ignored:
   browsers assume wheel listeners are passive precisely to stop pages doing
   this badly. */
function bindZoom(){
  var stage = $("pvStage");
  if(!stage || stage.dataset.zoomBound) return;
  stage.dataset.zoomBound = "1";

  stage.addEventListener("wheel", function (e) {
    /* Not while the preview is pinned to the bottom of the screen (Colours open,
       960px and under): there it sits under a resting pointer for the whole step,
       and a wheel turn meant for the page zoomed the string instead. A trackpad
       pinch arrives as ctrl+wheel and still zooms. */
    var pinned = stage.closest(".preview");
    if (pinned && getComputedStyle(pinned).position === "sticky" && !e.ctrlKey) return;
    var floor = zoomFloor();
    var before = Math.max(S.zoom || 1, floor);
    /* Trackpads report small deltas and mice report ~100 a notch, so scale by
       direction rather than by magnitude — otherwise a mouse jumps the whole
       range in one flick. */
    var step = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    var next = Math.min(ZOOM_MAX, Math.max(floor, before * step));
    if (Math.abs(next - (S.zoom || 1)) < 0.0005) return;   // at a limit: let the page scroll
    e.preventDefault();
    S.zoom = next;
    S.zoomAuto = null;
    renderPreview();
  }, { passive: false });

  stage.addEventListener("dblclick", function () {
    if (S.zoom === 1) return;
    S.zoom = 1;
    S.zoomAuto = null;
    renderPreview();
  });

  /* PINCH AND DOUBLE-TAP, because a touch device has no wheel and no dblclick.
     Until now the zoom simply did not exist on a phone — which is where the
     visualiser now sits under step 6, so it is the screen that needed it most.

     Repainting on every touchmove is far too much: the canvas redraws the whole
     bundle. Coalesced into one frame with requestAnimationFrame instead, so a
     fast pinch paints once per frame rather than once per event. */
  var pinch = 0, pinchZoom = 1, frame = 0;
  function dist(t) {
    var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () { frame = 0; renderPreview(); });
  }

  stage.addEventListener("touchstart", function (e) {
    if (e.touches.length !== 2) return;
    pinch = dist(e.touches);
    pinchZoom = S.zoom || 1;
  }, { passive: true });

  stage.addEventListener("touchmove", function (e) {
    if (e.touches.length !== 2 || !pinch) return;
    /* Only swallow the gesture once it is unmistakably a pinch. Below that the
       page must keep scrolling, or the stage becomes a dead patch you cannot
       scroll past — the same trap the wheel handler avoids at its limits. */
    var ratio = dist(e.touches) / pinch;
    if (Math.abs(ratio - 1) < 0.02) return;
    e.preventDefault();
    S.zoom = Math.min(ZOOM_MAX, Math.max(zoomFloor(), pinchZoom * ratio));
    S.zoomAuto = null;
    schedule();
  }, { passive: false });

  stage.addEventListener("touchend", function (e) {
    if (e.touches.length < 2) pinch = 0;
  }, { passive: true });

  var lastTap = 0;
  stage.addEventListener("touchend", function (e) {
    if (e.touches.length || pinch) return;
    var now = Date.now();
    if (now - lastTap < 320 && (S.zoom || 1) !== 1) {
      e.preventDefault();
      S.zoom = 1;
      S.zoomAuto = null;
      renderPreview();
    }
    lastTap = now;
  }, { passive: false });
}

/* THE VISUALISER MOVES UNDER COLOURS ON A NARROW SCREEN. Quinn, 2026-08-14.

   On desktop the preview sits in the sticky summary column beside the steps, so
   it is in view the whole way down. At 960px the grid collapses to one column
   and the summary drops below every step — which put the picture of the string
   at the very bottom, under the nock question, where nobody choosing a colour
   can see it. The one step where watching it matters is step 6.

   THIS HAS TO BE A DOM MOVE, NOT CSS. The preview lives inside `aside.summary`
   and step 6 is inside `.steps`; they are in different branches of the tree, so
   `order` and `flex-direction` cannot get one between the other's children.

   A comment node marks where it came from, so putting it back is exact and does
   not depend on the summary's markup staying in the same order.

   960px matches the existing `@media(max-width:960px)` that collapses the grid.
   Two places that must agree — if the breakpoint moves in the CSS, move it here
   too or the preview lands in the sticky column while the layout is stacked. */
var PV_HOME = null;
function placePreview(){
  var pv = document.querySelector(".rc-builder .preview");
  var s6 = $("s6");
  if (!pv || !s6) return;

  if (!PV_HOME) {
    PV_HOME = document.createComment("rc-preview-home");
    pv.parentNode.insertBefore(PV_HOME, pv);
  }

  var narrow = window.matchMedia("(max-width: 960px)").matches;
  var wanted = narrow ? s6 : PV_HOME.parentNode;
  /* Already where it should be — do nothing. Re-inserting a canvas resets its
     backing store, so a resize listener that moved it every time would clear
     the drawing on every pixel of a window drag. */
  if (narrow  && pv.previousElementSibling === s6) return;
  if (!narrow && pv.previousSibling === PV_HOME) return;

  if (narrow) { s6.after(pv); pv.classList.add("preview--inline"); }
  else        { PV_HOME.after(pv); pv.classList.remove("preview--inline"); }
  renderPreview();
}

function renderPreview(){
  paint($("pvCanvas"));
  paint($("heroCanvas"));
  var hint = $("pvHint");
  /* Touch has no wheel and no double-click, so the wording follows the device
     rather than telling half the customers to do something they cannot. */
  if (hint) {
    hint.textContent = window.matchMedia("(hover: none)").matches
      ? "Pinch to zoom · double-tap to reset"
      : "Scroll to zoom · double-click to reset";
  }
  if(hint) hint.classList.toggle("zoomed", (S.zoom || 1) !== 1);

  const sp = $("bowSpecs");
  if(sp){
    const rec = S.bow ? BOW_SPECS[S.make + "|" + S.bow.m] : null;
    sp.innerHTML = rec
      ? `<span><b>${rec.ata}</b>ATA</span><span><b>${rec.bh}</b>Brace</span><span><b>${rec.ibo}</b>IBO</span>`
      : "";
  }

  const cap = $("prevCap");
  if(!cap) return;
  const chip = n => `<i style="background:${hexOf(n)}"></i>`;
  let s = chip(S.colours.main);
  if(S.colours.second) s += chip(S.colours.second);
  if(S.opts.pin && S.colours.pinstripe) s += chip(S.colours.pinstripe);
  if(surfaceShown("serving")) s += chip(S.colours.serving);
  if(surfaceShown("centre")) s += chip(S.colours.centre);
  const names = [S.colours.main];
  if(S.colours.second) names.push(S.colours.second);
  cap.innerHTML = s + `<span>${names.join(" / ")}` + (surfaceShown("serving") ? ` · ${S.colours.serving} servings` : (surfaceShown("centre") ? ` · ${S.colours.centre} centre serving` : "")) + `</span>`;
}

function calc(){
  const lines = [];
  let total = 0;
  if(!S.bow) return {lines, total, n:0};

  const n = S.bow.p.length;
  const pk = pkgFor(S.bow);
  if(pk){
    const label = S.type === "trad"
      ? pk.name
      : S.bow.custom
        ? `${pk.name} — ${esc(customBowName())}`
        : `${pk.name} — ${S.year||""} ${S.make} ${S.bow.m}`.replace("  "," ");
    lines.push([label, pk.price]); total += pk.price;
  }

  if(S.colours.second) lines.push(["Second colour", 0]);
  if(S.opts.pin) lines.push(["Pinstripe", 0]);
  if(nocksIncluded()) lines.push(["Speed nocks", 0]);
  if(S.opts.rush){ const c = priceOf("rush"); lines.push(["Rush build", c]); total += c; }
  if(S.opts.peep){ const c = priceOf("peep"); lines.push(["Peep sight", c]); total += c; }
  addonsOn().forEach(function (o) { var k = addonPick(o); lines.push([o.name + (k.title ? " (" + k.title + ")" : ""), k.price]); total += k.price; });
  if(S.opts.loop > 0){
    const r = ropeOf();
    const c = (r ? r.price : priceOf("loop")) * S.opts.loop;
    const spec = [r ? r.label : null, S.opts.loopColour].filter(Boolean).join(", ");
    lines.push([`D‑loop 2 pack × ${S.opts.loop}${spec ? ` — ${spec}` : ""}`, c]);
    total += c;
  }

  return {lines, total, n};
}

function update(){
  if (PENDING_SPEC && S.bow) { var ps = PENDING_SPEC; PENDING_SPEC = null; applySpec(ps); return; }
  if (SHOWPIECE && COLOURS.length) {
    ["main", "second", "pinstripe"].forEach(function (k) {
      if (S.colours[k] && !colourByName(S.colours[k])) S.colours[k] = (k === "main") ? firstAllowed() : null;
    });
    /* The showpiece's pinstripe on a material that does not come in that colour (Natural is
       452Xtra and X-99 only, so every recurve): keep the pinstripe and make it White, which
       everything stocks. Quinn: "recurve should have a pin". Only while it is still the
       showpiece; once the customer has chosen, their colours are theirs. */
    /* "Not allowed" arrives two ways: still holding the colour, or already blanked by the
       type and material guards (which clear the colour and leave the pinstripe switched on). */
    if (!S.colourPicked && S.opts.pin) {
      var sp = S.colours.pinstripe ? colourByName(S.colours.pinstripe) : null;
      if (!sp || !colourAllowed(sp)) {
        var w = colourByName("White");
        S.colours.pinstripe = (w && colourAllowed(w)) ? "White" : null;
      }
    }
    if (S.opts.pin && !S.colours.pinstripe && S.activeTab !== "pinstripe") S.opts.pin = false;
  }
  /* Drop any accessory that this bow type cannot take, BEFORE anything prices
     or renders. Hiding the rows is not enough on its own: someone can tick a
     peep on a compound, change their mind about the bow, and otherwise carry a
     $12.99 line and a part they cannot fit into the cart with nothing on screen
     admitting it. Enforced here rather than in pickType() so it holds however
     the type came to change. */
  if (!accessoriesOffered() && (S.opts.loop || S.opts.peep)) {
    S.opts.loop = 0;
    S.opts.peep = false;
  }
  /* AND DROP ANY FITTING THAT HAS GONE OUT OF STOCK, for the same reason and in
     the same place. Hiding the row is half of it: the box can be ticked before the
     stock runs out — another tab, a browser left open over a week's rush slots, a
     count changed at the counter mid-build — and the tick is what reaches the cart.
     /cart/add.js rejects the whole items array, so one stale $14.99 fitting takes
     the entire order down with it. Clearing it here means the worst case is a
     fitting quietly falling off a build, which the row on screen then explains. */
  OPTIONS.forEach(function (o) {
    if (o.type === "check" && o.available === false && S.opts[o.id]) S.opts[o.id] = false;
  });
  /* Switching to a rope that does not come in the chosen colour falls back to
     that rope's first colour, so the D-loop line can never carry a colour the
     dropdown no longer offers (e.g. Flo Purple chosen on #24, then #23). */
  var lc = loopColours();
  if (lc.length && lc.indexOf(S.opts.loopColour) < 0) S.opts.loopColour = lc[0];
  /* A centre serving colour that the row no longer offers must not survive.
     Hiding it from the grid is only half the job — `S.colours.centre` keeps
     whatever it last held, and the slip reads the STORED value, so a colour
     nobody can pick would print for the bench to serve. Same failure the
     accessories above are guarded against, and the same reason it lives in
     update(): every reset path runs through here. */
  /* A COLOUR THAT WENT OUT OF STOCK MID-BUILD, or that a preselect or a stale
     tab is still holding. The slip reads the STORED value, so dimming the
     swatch without clearing this would put an unbuildable colour in front of
     the bench — the identical failure the centre serving is guarded against
     below, and it lives here for the identical reason: every reset path runs
     through update(). */
  /* A COLOUR THE CHOSEN MATERIAL IS NOT STOCKED IN goes the same way, and it
     matters more than the out-of-stock list because SWITCHING MATERIAL is a
     normal thing to do mid-build: pick Red on 452Xtra, change to DynaFLIGHT 97,
     and without this the slip still says Red for thread the bench does not have
     in Red. pickMat() runs update(), so this is the one place it needs to be. */
  if (OUT_OF_STOCK.length || Object.keys(MATERIAL_COLOURS).length
      || Object.keys(SPECKLED_COLOURS).length) {
    ["main", "second", "pinstripe"].forEach(function (k) {
      var n = S.colours[k];
      if (!n) return;                       // second and pinstripe may be unset
      var c = colourByName(n);
      if (!c || !inStock(c, k) || !materialStocks(c, k)) {
        S.colours[k] = (k === "main") ? firstAllowed() : null;
      }
    });
  }

  if (CENTRE_COLOURS.length && S.colours.centre) {
    var cc = colourByName(S.colours.centre);
    if (!cc || !centreAllows(cc, "centre")) S.colours.centre = firstCentreColour();
  }
  /* A SERVING COLOUR THAT HAS GONE OUT MOVES THE STORED VALUE, and this is the
     half that matters. `S.colours.serving` reaches the bench as "End servings";
     nothing has ever moved it, because until now nothing could restrict a
     serving row at all. Dimming the swatch while the old name sits in state
     sends the builder to a spool that is empty. Same place and same reason as
     the three guards above: every reset path runs through update(). */
  if (SERVING_OUT.length) {
    ["serving", "centre"].forEach(function (k) {
      if (!S.colours[k]) return;
      var sc = colourByName(S.colours[k]);
      if (!sc || !inStock(sc, k)) S.colours[k] = firstServingColour(k);
    });
  }
  /* Same guard for the rush, and it earns its place: the slots can sell out
     while somebody is mid-build with the box already ticked. Without this they
     would reach the cart holding a slot that no longer exists, and the add
     would fail on stock at the worst possible moment. */
  var rushOpt = OPTIONS.find(function (x) { return x.id === "rush"; });
  if (S.opts.rush && (!rushOpt || rushOpt.available === false)) S.opts.rush = false;
  /* And for a hat or sticker that sold out, or is no longer there, mid-build. */
  ADDONS.forEach(function (a) {
    var o = OPTIONS.find(function (x) { return x.id === a.id; });
    if (S.opts[a.id] && (!o || o.available === false)) S.opts[a.id] = false;
  });
  /* The build-time notice under Add to cart is Liquid, rendered before the box
     could be ticked, so it emits both sentences and this picks one. Leaving it
     alone would have a rush order reading "about 4 weeks" on the same screen as
     the rush line it just paid for. */
  var bt = document.querySelector(".rcs-buildtime--builder");
  if (bt) {
    var std = bt.querySelector(".rcs-buildtime__std");
    var rsh = bt.querySelector(".rcs-buildtime__rush");
    if (std && rsh) { std.hidden = !!S.opts.rush; rsh.hidden = !S.opts.rush; }
  }
  /* The Full set / One piece question, and the mode behind it. Owned here for
     the same reason as the accessories above: every reset path runs through
     update(), so this is the only place it cannot be left showing a question
     that does not apply, or a `single` mode on a bow type that has no singles. */
  var buyBox = $("buyMode");
  if (buyBox) buyBox.hidden = !(S.type && singlesOffered());
  if (!singlesOffered() && S.buy !== "set") { S.buy = "set"; S.piece = null; }
  renderBuy();
  renderSurfaces(); renderOpts(); renderNocks(); syncNockBlock(); renderMats(); renderPreview(); renderSpec();

  const {lines, total} = calc();

  /* Step 4 is two different questions depending on the bow, so it is titled
     here rather than in the markup — every reset path runs through update(),
     which is the only way the heading cannot be left saying the wrong thing.

     For a recurve or longbow the step is done when a length has been typed, and
     it shows that length. Nothing is marked done on their behalf: the AMO is the
     one thing nobody else can supply, and a step that looks answered is a step
     nobody goes back to. */
  const h4 = $("h4");
  /* Step 4 now has THREE identities, not two: model year, AMO length, and —
     when one piece is being bought — which piece. Retitled here for the same
     reason as before: every reset path runs through update(), so this is the
     only place the heading cannot be left saying the wrong thing. */
  if (h4) {
    h4.textContent = (S.buy === "single" && singlesOffered()) ? "Which piece?"
                   : (S.type === "trad" ? "AMO length" : "Model year");
  }
  if (S.type === "trad") {
    const amo = (S.amo || "").trim();
    $("s4").classList.toggle("done", !!amo);
    $("v4").textContent = amo;
  }

  // step value labels
  $("v5").textContent = (MATERIALS.find(m=>m.id===S.material)||{}).name || "";
  $("v6").textContent = S.colours.second ? `${S.colours.main} / ${S.colours.second}` : S.colours.main;
  const on = [];
  if(S.opts.rush) on.push("Rush");
  if(S.opts.peep) on.push("Peep");
  addonsOn().forEach(function (o) { on.push(o.short || o.name); });
  if(S.opts.loop){ const r = ropeOf(); on.push(`${S.opts.loop}× D‑loop${r ? ` ${r.label}` : ""}`); }
  /* Only surface the nock in the step label once it is not the default —
     "Not sure" adds nothing at a glance, a named nock confirms it registered. */
  /* Show it whatever it is. It used to be hidden when it matched the default,
     on the grounds that "Not sure" said nothing at a glance — but now that the
     answer has to be made deliberately, seeing it echoed back is how someone
     confirms it registered. */
  if(S.nock) on.push(S.nock);
  $("v7").textContent = on.length ? on.join(", ") : "None";

  var shareBtn = $("shareBtn");
  if (shareBtn) {
    var can = shareable();
    shareBtn.hidden = !can;
    /* A build that changed since the last copy must not leave "Link copied"
       sitting under it — that would promise a link to a string they have just
       stopped choosing. */
    if (shareBtn.classList.contains("ok")) {
      clearTimeout(shareBtn._rcT);
      shareBtn.classList.remove("ok");
      shareBtn.textContent = SHARE_LABEL;
    }
    var so = $("shareOut");
    if (so && !so.hidden) so.hidden = true;
  }

  $("sumSub").textContent = S.bow
    ? (S.bow.custom ? customBowName()
      : S.type === "trad" ? `${S.make} — ${S.bow.m.replace(" — specify AMO length","")}`
                          : `${S.year||yrLabel(S.bow.y)} ${S.make} ${S.bow.m}`)
    : "Nothing selected yet";

  $("sumLines").innerHTML = lines.length
    ? lines.map(([l,v]) => `<div class="sumline"><span>${l}</span><b>${v ? money(v) : "Included"}</b></div>`).join("")
    : `<div class="sumempty">Pick your bow to see pricing.</div>`;

  /* A dash until there is a price. "$0.00" was the only figure on the builder's
     first screen and it is not a price anybody is being quoted; the line above
     it already says what to do. One piece has no price until the piece is
     picked, so the dash stays through the bow and the year there. From the
     first real total this is the total exactly as before. The same dash, with
     the same `none` class that greys it, is in the section's markup. */
  const priced = !!S.bow && total > 0;
  $("sumTotal").textContent = priced ? money(total) : "–";
  $("sumTotal").classList.toggle("none", !priced);
  /* Never disabled. A dead button explains nothing and cannot be reached by
     keyboard; clicking it now sends you to whatever is missing. `notready` is
     appearance only. */
  const miss = missingStep();
  $("addBtn").disabled = false;
  $("addBtn").classList.toggle("notready", !!miss);

  /* Steps past the first unanswered one are shown locked. Appearance only —
     toggleStep() is what actually refuses, so this cannot disagree with it. */
  const maxOpen = maxOpenStep();
  for (let i = 1; i <= NSTEPS; i++) {
    $("s" + i).classList.toggle("locked", i > maxOpen);
  }

  /* Clear a prompt once its step is satisfied, or once the blocker moves on. */
  document.querySelectorAll(".rc-need").forEach(function (n) {
    const step = n.closest(".step");
    if (!miss || !step || step.id !== "s" + miss.n) n.remove();
    /* Still the blocking step, but for a different reason (the year is picked
       and the piece is not): the prompt says what the line under the button says. */
    else if (n.textContent !== miss.msg) n.textContent = miss.msg;
  });

  /* What is left, in words, directly under Add to cart. The button no longer
     fades when the build is unfinished, so this line is the only thing saying
     so. It is a button whose click is addToCart(), which in this state is
     needStep(): the same jump, from the same `miss`. Emptied as well as hidden
     when nothing is missing, so a stale reason can never be read out. Guarded,
     because the script and the section are two pushes and the script must not
     throw against markup that has no such line yet. */
  const left = $("addLeft");
  if (left) {
    left.hidden = !miss;
    left.textContent = miss ? "One thing left: " + leftReason(miss) : "";
  }

  /* Three states, because a flat "free shipping" line is a promise the store
     does not keep under $50. Since the threshold came down from $75 on
     2026-08-12 every set we build clears it, so this is now mostly the
     accessories-only case — but the shortfall line stays, because it costs
     nothing and it is the honest thing to show when a cart really is short. */
  /* The thresholds are whole dollars and read as whole dollars — money() would
     set them as $50.00 and $10.00, which is the register talking. The shortfall
     is a real amount and keeps its cents. */
  const ship = $("shipNote");
  /* OUTSIDE CANADA the three lines below are about somebody else. A US visitor
     was told shipping is free in Canada, then met about $15 of shipping and 13%
     duty for the first time at checkout ($129.00 became $160.51, read on the
     live checkout 9 October 2026; 100 US visits and 5 checkouts that month, no
     order). Shopify.country is the market the storefront is serving. The figure
     is the $20 CAD rate at the storefront's own exchange rate, rounded up the
     way the checkout rounds it. */
  const shopNow = window.Shopify || {};
  if (shopNow.country && shopNow.country !== "CA") {
    const rate = (shopNow.currency && parseFloat(shopNow.currency.rate)) || 0;
    const cur  = (shopNow.currency && shopNow.currency.active) || "";
    const flat = rate ? "$" + Math.ceil(SHIP_FLAT_US * rate) + (cur ? " " + cur : "") : "Flat-rate";
    ship.innerHTML = `<b>${flat} shipping</b> to the lower 48`
      + ` — US import duty is added at checkout, so there is nothing to pay on delivery`;
  } else if (!total) {
    ship.innerHTML = `<b>Free shipping</b> in Canada over $${FREE_SHIP_OVER}`
      + ` — $${SHIP_FLAT_CA} flat under it, $${SHIP_FLAT_US} to the lower 48`;
  } else if (total >= FREE_SHIP_OVER) {
    ship.innerHTML = `<b>Free shipping</b> — tracked &amp; insured, anywhere in Canada`;
  } else {
    ship.innerHTML = `<b>$${SHIP_FLAT_CA} shipping</b> in Canada`
      + ` — add ${money(FREE_SHIP_OVER - total)} to make it free`;
  }
  /* last, because every line above can change the panel's height */
  panelRoom();
  syncSetPrices();
}
/* ============================================================
   CART
   Every choice goes through as a line item property so it prints
   on the packing slip — a builder at the bench reads the bow,
   material, colours and options off the slip without opening a
   screen. That is the whole reason for doing it this way rather
   than exploding the catalogue into variants.
   ============================================================ */
function setAmo(v) {
  S.amo = v || "";
  update();
}

/* The dropdown. "Other" keeps whatever has been typed rather than wiping it,
   so switching to it to correct a length does not cost the length. Choosing a
   listed size clears the free-text box, or a stale value would sit behind the
   menu and reappear the next time the step was re-rendered. */
function setAmoChoice(v) {
  if (v === "__other") {
    S.amoOther = true;
  } else {
    S.amoOther = false;
    S.amo = v || "";
  }
  renderYears();
  update();
  if (S.amoOther) {
    const i = $("amoInput");
    if (i) i.focus();
    return;
  }
  /* Picking a length is answering the step, so it moves on like every other
     step does — pickYear() has always done this and the menu that replaced the
     year tiles was the odd one out. Not on the blank prompt, which is someone
     backing out of an answer rather than giving one. */
  if (S.amo.trim()) goStep(5);
}

/* Enter in the free-text box does the same thing the menu does. Advancing on
   input would be wrong — update() runs on every keystroke, and "6" is not an
   answer on the way to "64.5". */
function amoKey(e) {
  if (e.key !== "Enter") return;
  e.preventDefault();
  if ((S.amo || "").trim()) goStep(5);
}

function cartProperties() {
  var mat = MATERIALS.find(function (m) { return m.id === S.material; }) || {};
  var props = {};
  /* A not-listed bow: their words, flagged so the bench checks the
     configuration against the actual bow before anything is cut. This is the
     safeguard for someone who picked the wrong set. */
  if (S.bow && S.bow.custom) {
    /* A not-listed bow: their words, and a flag so the bench checks the
       configuration against the actual bow before cutting. That check is the
       safeguard for someone who picked the wrong set — cheaper to confirm than
       to rebuild. Everything after this runs unchanged, because a synthetic bow
       carries the same `p` array a catalogue one does. */
    var cb = S.custom || {};
    var said = [cb.year, cb.make, cb.model].filter(Boolean).join(" ").trim();
    props["Bow"] = said || "Not given";
    props["NOT IN CATALOGUE"] = "Customer chose “" + (S.bow.cfg || "") +
      "” — CHECK against the bow before cutting";
    /* What the customer knows about the bow, in their words and marked as
       theirs. Lengths off a limb sticker or an old string are a starting point
       for the check, not a spec — the flag above still stands over all of it. */
    if (cb.string) props["String length (customer's figure)"] = cb.string;
    if (cb.cable)  props["Cable length (customer's figure)"]  = cb.cable;
    if (cb.serial) props["Serial number"]                     = cb.serial;
    if (cb.notes)  props["Notes on the bow"]                  = cb.notes;
    if (S.type === "trad") props["AMO length"] = S.amo.trim();
  } else if (S.type === "trad") {
    props["Bow"] = S.make + " — " + S.bow.m.replace(" — specify AMO length", "");
    props["AMO length"] = S.amo.trim();
  } else {
    props["Bow"] = (S.year || yrLabel(S.bow.y)) + " " + S.make + " " + S.bow.m;
  }
  /* "Set" is what the cart build card keys off, along with "Bow", so a single
     piece has to fill it too or the line falls back to a plain property list.
     It says what is actually being built, and names the full set it came from so
     the bench can see the context without opening anything. */
  if (S.buy === "single" && singlesOffered() && S.piece) {
    var sv = singleFor(S.piece);
    /* The variant name is only added when it says something the part name does
       not. "Control cable (Control cable)" is noise; "Bowstring (Single cam
       string)" tells the bench which of the two string SKUs this is. */
    var svName = (sv && sv.name && sv.name !== S.piece) ? " (" + sv.name + ")" : "";
    props["Set"] = "SINGLE PIECE — " + S.piece + svName;
    props["From set"] = S.bow.p.length + " piece — " + S.bow.p.join(" + ");
  } else {
    props["Set"] = S.bow.p.length + " piece — " + S.bow.p.join(" + ");
  }
  props["Material"]       = mat.name || S.material;
  props["Main colour"]    = S.colours.main;
  if (S.colours.second)                    props["Second colour"] = S.colours.second;
  if (S.opts.pin && S.colours.pinstripe)   props["Pinstripe"]     = S.colours.pinstripe;
  if (surfaceShown("serving")) props["End servings"] = S.colours.serving;
  else props["End servings"] = "None \u2014 Flemish twist";
  /* END servings are on every piece; the CENTRE serving is only on the string.
     Keyed off surfaceShown so the slip can only ever carry what step 6 actually
     put in front of the customer — the two drifting apart is how the bench ends
     up reading a choice nobody made. */
  if (surfaceShown("centre")) props["Centre serving"] = S.colours.centre;
  /* "Arrow nock", not "Nock": the slip already carries "Speed nocks", which are
     a different thing entirely, and two similar labels at the bench is how the
     wrong serving diameter gets used. Sits next to the centre serving because
     that is the decision it feeds. */
  if (S.nock === NOCK_OTHER) {
    const arrow = String(S.nockOther||"").trim();
    props["Arrow nock"] = arrow ? "Not listed — shoots " + arrow
                                : "Not listed — arrow not given, confirm before serving";
  } else if (S.nock) {
    props["Arrow nock"] = S.nock;
  }
  if (nocksIncluded()){
    props["Speed nocks"] = "Fitted, manufacturer's original locations";
    if (S.colours.shrink) props["Speed nock wrap"] = S.colours.shrink;
  }
  /* Says NOT INSTALLED in as many words: the bench used to serve these in, and
     the slip is the only thing the builder reads. */
  /* On the SET line as well as being its own paid line, so it prints on the
     build card the bench actually reads. A separate accessory line at the foot
     of a packing slip is exactly the thing that gets missed. */
  if (S.opts.rush)        props["Rush build"]  = "YES — " + (parseInt(CFG.rushDays,10) || 5) + " business days";
  if (S.opts.peep)        props["Peep sight"]  = "Included — ships loose, NOT installed";
  return props;
}

function addToCart() {
  /* Before anything else: if the build is not finished, take them to the part
     that is not finished. This runs first because pkgFor() below cannot resolve
     a package without a bow, and "that bow doesn't map to a package" is a
     confusing thing to say to someone who has not chosen a bow. */
  var miss = missingStep();
  if (miss) { needStep(miss); return; }

  var pk = pkgFor(S.bow);
  if (!pk || !pk.id) {
    showError("That bow doesn't map to a package yet. Give us a call and we'll quote it.");
    return;
  }
  var btn = $("addBtn");
  btn.disabled = true;
  btn.textContent = "Adding…";

  var items = [{ id: pk.id, quantity: pk.qty || 1, properties: cartProperties() }];

  var rushId = variantOf("rush");
  if (S.opts.rush && rushId) items.push({ id: rushId, quantity: 1 });
  var peepId = variantOf("peep");
  if (S.opts.peep && peepId) items.push({ id: peepId, quantity: 1 });
  addonsOn().forEach(function (o) { items.push({ id: addonPick(o).id, quantity: 1 }); });
  /* The colour rides on the D‑loop line, not the set's, so the slip shows it
     against the thing it describes. The rope is already the variant title. */
  var rope = ropeOf();
  if (S.opts.loop > 0 && rope && rope.variantId) {
    var loopLine = { id: rope.variantId, quantity: S.opts.loop };
    if (S.opts.loopColour) loopLine.properties = { "Loop colour": S.opts.loopColour };
    items.push(loopLine);
  }

  fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body: JSON.stringify({ items: items })
  })
  .then(function (r) {
    return r.json().then(function (j) {
      if (!r.ok) throw new Error(j.description || j.message || ("Cart returned " + r.status));
      return j;
    });
  })
  .then(function () { window.location.href = CFG.cartUrl || "/cart"; })
  .catch(function (e) {
    btn.disabled = false;
    btn.textContent = "Add to cart";
    showError("Couldn't add that to the cart. " + e.message);
  });
}

function showError(msg) {
  var box = $("rcError");
  if (!box) { alert(msg); return; }
  box.innerHTML = "<b>Something went wrong</b>" + msg;
  box.hidden = false;
}

/* ============================================================
   BOOT
   ============================================================ */
var booted = false;
/* Open the builder already answered.

   Two sources, URL first: `?type=compound|crossbow|trad` and `?buy=set|single`,
   then the per-product map in the section settings. The URL wins so a link can
   preselect from anywhere — a homepage card, an email, an ad — while the map
   handles the ordinary case of a customer landing on the crossbow product and
   expecting a crossbow builder.

   ANYTHING UNRECOGNISED IS IGNORED RATHER THAN ERRORED. A stale bookmark or a
   handle that has been renamed should degrade to the normal builder, not to a
   broken one. */
function parsePreselect(){
  var out = { type: null, buy: null, make: null, model: null, year: null,
              cmake: null, cmodel: null, cyear: null };
  var VALID = { compound: 1, crossbow: 1, trad: 1 };

  var q = {};
  try {
    (window.location.search || "").replace(/^\?/, "").split("&").forEach(function (kv) {
      var i = kv.indexOf("=");
      /* "+" IS A SPACE HERE. A visitor's first request to the store is answered
         with a redirect to the same address re-encoded, and "ARC%2034" comes
         back as "ARC+34" (read off the live site 2026-10-08: 302 on the first
         visit, none once the cookies are set). decodeURIComponent leaves a plus
         alone, so the model matched nothing and the link stopped at the model
         list: every per-bow link and every shared build opened by somebody new,
         which is who a shared build is for. A real plus in a name ("RVX+ 32")
         travels as %2B and still decodes to a plus. */
      if (i > 0) q[decodeURIComponent(kv.slice(0, i))] = decodeURIComponent(kv.slice(i + 1).replace(/\+/g, " "));
    });
  } catch (e) { /* a malformed query string is not worth failing over */ }

  if (q.type && VALID[q.type]) out.type = q.type;
  if (q.buy === "single" || q.buy === "set") out.buy = q.buy;

  /* `?make=Mathews&model=ARC 34&year=2026` — what the per-bow pages link with.
     Held as raw strings and resolved against the real catalogue in
     applyPreselect(), because BOWS does not exist until the Storefront API has
     answered. Anything that does not match is dropped there, not here. */
  if (q.make)  out.make  = String(q.make).trim();
  if (q.model) out.model = String(q.model).trim();
  if (q.year && /^\d{4}$/.test(q.year)) out.year = parseInt(q.year, 10);

  /* A not-listed bow travels as its typed name. Kept separate from make/model
     so a shared custom build can never be mistaken for a catalogue match. */
  if (q.cmake)  out.cmake  = String(q.cmake).trim();
  if (q.cmodel) out.cmodel = String(q.cmodel).trim();
  if (q.cyear)  out.cyear  = String(q.cyear).trim();
  if (q.ccfg)   out.ccfg   = String(q.ccfg).trim();

  /* The spec itself — every value is held as a raw string and resolved against
     the real catalogue in applySpec(), for the same reason make and model are:
     none of these lists exist until the Storefront API has answered. */
  ["piece", "amo", "mat", "main", "second", "pin", "serv", "centre", "wrap",
   "nock", "nockx", "rope", "lcol"].forEach(function (k) {
    if (q[k] !== undefined && q[k] !== "") out[k] = String(q[k]).trim();
  });
  if (q.loop && /^\d{1,2}$/.test(q.loop)) out.loop = parseInt(q.loop, 10);
  out.peep = q.peep === "1";
  out.rush = q.rush === "1";
  out.hasSpec = ["piece", "mat", "main", "second", "pin", "serv", "centre",
                 "wrap", "nock"].some(function (k) { return out[k]; });

  if (!out.type) {
    var handle = String(CFG.productHandle || "").trim();
    String(CFG.preselectMap || "").split(/[\r\n,]+/).forEach(function (line) {
      var i = line.indexOf(":");
      if (i < 0) return;
      if (line.slice(0, i).trim() !== handle) return;
      var parts = line.slice(i + 1).trim().split(/\s+/);
      if (VALID[parts[0]]) out.type = parts[0];
      if (parts.indexOf("single") >= 0) out.buy = "single";
    });
  }
  return out;
}

/* ============================================================
   SHARE THIS BUILD
   ============================================================
   Quinn, 2026-09-26: a red "Share this build" button top right of the summary
   that gives "a copyable link for people to send to their buddies, to their pro
   shop". So the link has to carry the WHOLE spec, not just the bow — a buddy who
   opens it and sees a default red string has been shown somebody else's build.

   IT IS BUILT ON THE PRESELECT PLUMBING THE PER-BOW PAGES ALREADY USE, and the
   rules there hold here: readable parameters rather than an opaque blob (a pro
   shop can read one, and so can anyone debugging a link a customer pasted into
   an email), and ANYTHING UNRECOGNISED IS IGNORED RATHER THAN ERRORED. That
   matters more for a share link than for a marketing one: these get pasted into
   messages, wrapped by mail clients, and opened months later against a catalogue
   that has moved. Every part of the spec degrades on its own, so a discontinued
   colour costs that one row and not the link.

   WHAT IS DELIBERATELY LEFT OUT: the serial number, the customer's measured
   string and cable lengths, and their notes. Those ride on the ORDER, where
   they belong to one person. A share link is handed around — to a buddy, to a
   shop, into a forum thread — and none of those three describe the build. The
   make, model and year of a not-listed bow do, so those travel. */
function shareUrl(){
  var q = [];
  function add(k, v){
    if (v === null || v === undefined || v === "" || v === false) return;
    q.push(encodeURIComponent(k) + "=" + encodeURIComponent(String(v)));
  }
  add("type", S.type);
  if (singlesOffered()) add("buy", S.buy);
  if (S.bow && S.bow.custom) {
    var c = S.custom || {};
    add("cmake", c.make); add("cmodel", c.model); add("cyear", c.year);
    /* Which "What does your bow take?" choice. Without it the link could not
       rebuild a not-listed bow at all: the name says nothing about the set. */
    add("ccfg", S.bow.cfg);
  } else if (S.bow) {
    add("make", S.make); add("model", S.bow.m); add("year", S.year);
  }
  if (S.buy === "single" && S.piece) add("piece", S.piece);
  if (S.type === "trad" && S.amo) add("amo", S.amo);
  add("mat", S.material);
  add("main", S.colours.main);
  add("second", S.colours.second);
  /* The pinstripe is a flag AND a colour — "1" carries the flag alone, so a
     pinstripe turned on before a colour was picked still survives the trip. */
  if (S.opts.pin) add("pin", S.colours.pinstripe || "1");
  add("serv", S.colours.serving);
  if (surfaceShown("centre")) add("centre", S.colours.centre);
  if (nocksIncluded()) add("wrap", S.colours.shrink);
  if (nockAsked() && S.nock) {
    add("nock", S.nock);
    if (S.nockOther) add("nockx", S.nockOther);
  }
  if (S.opts.loop > 0) {
    add("loop", S.opts.loop); add("rope", S.opts.loopRope); add("lcol", S.opts.loopColour);
  }
  if (S.opts.peep) add("peep", 1);
  if (S.opts.rush) add("rush", 1);
  /* The CURRENT page, so a link shared off the crossbow product stays on it.
     Any builder page can rebuild any spec — `type` overrides the product map. */
  var base = location.origin + location.pathname;
  return q.length ? base + "?" + q.join("&") : base;
}

/* There is no state worth sharing until there is a bow: a link carrying nothing
   but "compound" rebuilds an empty builder, which is the page they already have.
   Hidden rather than disabled — a dead control in a four-word header is noise,
   and this one has no "you are nearly there" story to tell the way Add to cart
   does. */
function shareable(){ return !!(S.type && S.bow); }

var SHARE_LABEL = "Share this build";

function shareFlash(msg, ok){
  var btn = $("shareBtn");
  if (!btn) return;
  btn.classList.toggle("ok", !!ok);
  btn.textContent = msg;
  clearTimeout(btn._rcT);
  btn._rcT = setTimeout(function () {
    btn.classList.remove("ok");
    btn.textContent = SHARE_LABEL;
  }, 2600);
}

/* The clipboard is not guaranteed and its failures are invisible: the API needs
   a secure context and a user gesture, the execCommand fallback is deprecated
   and refused outright in some browsers, and an embedded webview may allow
   neither. A button that silently copies nothing is worse than no button, so the
   last resort SHOWS the link in a readonly box the customer can select by hand —
   nobody leaves without a way to get the URL. */
function shareBuild(){
  var url = shareUrl();
  var box = $("shareOut");
  if (box) { box.value = url; box.hidden = true; }

  function reveal(){
    if (!box) { window.prompt("Copy this link", url); return; }
    box.hidden = false;
    box.focus(); box.select();
    shareFlash("Copy the link below", false);
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url)
      .then(function () { shareFlash("Link copied", true); })
      .catch(function () { if (!legacyCopy(url)) reveal(); else shareFlash("Link copied", true); });
    return;
  }
  if (legacyCopy(url)) shareFlash("Link copied", true);
  else reveal();
}

function legacyCopy(text){
  try {
    var t = document.createElement("textarea");
    t.value = text;
    /* Off screen but focusable. `display:none` cannot be selected, and a
       visible one makes the page jump as it takes focus. */
    t.setAttribute("readonly", "");
    t.style.position = "fixed";
    t.style.top = "-1000px";
    t.style.opacity = "0";
    document.body.appendChild(t);
    t.select();
    var ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(t);
    return !!ok;
  } catch (e) { return false; }
}

/* Case- and space-insensitive, because these arrive off a URL somebody may have
   retyped, and "arc 34" should not be a different bow from "ARC 34". */
function looseFind(list, want, key){
  var w = String(want || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!w) return null;
  for (var i = 0; i < list.length; i++) {
    var v = key ? list[i][key] : list[i];
    if (String(v).trim().toLowerCase().replace(/\s+/g, " ") === w) return list[i];
  }
  return null;
}

var PENDING_SPEC = null;
function applyPreselect(){
  var pre = parsePreselect();
  if (!pre.type) return;
  pickType(pre.type);
  /* A single only exists on compound and crossbow, and pickType() has just
     reset the mode — so this has to come after it, not before. */
  if (pre.buy === "single" && singlesOffered()) pickBuy("single");
  else if (singlesOffered()) pickBuy("set");

  /* THE BOW ITSELF. The per-bow pages exist to save someone the search they
     have already done by landing on a page about their exact bow; dropping them
     back at a list of makes would waste the whole point.

     Resolved here rather than in parsePreselect() because BOWS is empty until
     the Storefront API has answered, and this runs after that. Every step is
     guarded: an unknown make, a model that make does not carry, or a year the
     bow does not list all degrade to the ordinary builder rather than erroring —
     the same rule ?type= has always followed, and it matters more here because
     a renamed model would otherwise break a page that ranks. */
  if (!pre.make) {
    /* A shared NOT-LISTED build has no catalogue make to resolve. */
    if (pre.ccfg && (pre.cmake || pre.cmodel)) applyCustomBow(pre);
    /* A RECIPE WITH NO BOW: the "Shoot what we shoot" buttons on About (11 October 2026)
       carry a material and every colour and leave the bow to the customer. The colours go
       on now, so the preview shows the string they asked for while they find their bow;
       the material waits, because pickMat() opens step 6 and that is locked until there is
       a bow. update() applies the whole recipe once one is picked. */
    else if (pre.hasSpec) {
      applySpec(Object.assign({}, pre, { mat: null, piece: null }));
      PENDING_SPEC = pre;
    }
    return;
  }
  var makes = Object.keys(BOWS[S.type] || {});
  var make = looseFind(makes, pre.make);
  if (!make) return;
  pickMake(make);
  if (!pre.model) return;

  var bow = looseFind(BOWS[S.type][make] || [], pre.model, "m");
  if (!bow) return;
  pickBow(bow.m);

  /* pickBow already sets the year when the bow lists exactly one, and lands on
     step 4 for a recurve to ask the AMO length — neither of which should be
     overridden. Only fill a year the customer would otherwise have to pick. */
  if (pre.year && !S.year && bow.y.indexOf(pre.year) >= 0) pickYear(pre.year);
  applySpec(pre);
}

/* A shared not-listed build: the bow as its owner typed it, and the choice
   they made for what it takes. The same calls the panel's own controls make,
   in the order a customer makes them. A choice this bow type does not offer
   (a link retyped, or the list reworded since) leaves the ordinary builder. */
function applyCustomBow(pre){
  var list = CONFIGS[S.type] || CONFIGS.compound, idx = -1;
  for (var i = 0; i < list.length; i++) {
    if (looseFind([list[i].label], pre.ccfg)) { idx = i; break; }
  }
  if (idx < 0) return;
  S.custom = { year: pre.cyear || "", make: pre.cmake || "", model: pre.cmodel || "",
               string: "", cable: "", serial: "", notes: "" };
  notListed();
  notListedPath(true);
  chooseConfig(idx);
  applySpec(pre);
}

/* Is this colour actually offerable on this row? The same four gates the swatch
   grid draws with, in one place, because a share link is exactly where a colour
   that is out of stock, wrong for the material, serving-only or off the centre
   serving's short list will arrive months after it stopped being valid. The
   fifth is the serving rule from CLAUDE.md: serving thread does not come
   speckled at all, so no speckle is legal on those two rows whatever the
   material says. */
function shareColourOk(c, id){
  if (!c) return false;
  if (servingSurface(id) && c.sp) return false;
  return colourAllowed(c) && tabTakes(c, id) && inStock(c, id) && centreAllows(c, id)
      && materialStocks(c, id);
}

/* Rebuild the rest of the spec from a shared link.

   THE ORDER IS LOAD-BEARING. The material has to land before the colours,
   because pickMat() drops every colour the new material cannot carry — set them
   first and they are wiped on the way past. The piece comes before the material
   for the same reason in reverse: it decides which rows exist at all.

   NOTHING HERE IS ASSIGNED WITHOUT BEING CHECKED against the live catalogue, and
   update() at the end is the second net: it clears an out-of-stock colour, a
   centre serving the row no longer offers, a rush whose slots have gone and a
   fitting that is out of stock. So the worst a stale link can do is arrive with
   a row or two back at their defaults. */
function applySpec(pre){
  if (!pre.hasSpec && !pre.loop && !pre.peep && !pre.rush) return;

  if (pre.piece && S.buy === "single") {
    var part = looseFind(piecesFor(S.bow), pre.piece);
    if (part) pickPiece(part);
  }
  if (pre.amo && S.type === "trad" && !S.amo) setAmo(pre.amo);

  if (pre.mat) {
    var hit = looseFind(materialsFor(S.type), pre.mat, "id");
    if (hit) pickMat(hit.id);
  }

  /* Colours are assigned rather than pushed through setColour(): that setter
     opens the next row, scrolls to it and now pulls the zoom back for the centre
     serving — a chain of six of them would walk the page about and land the
     recipient somewhere arbitrary. The values are validated first and update()
     re-checks them, which is the same protection the grid gives. */
  if (pre.main && SHOWPIECE) {
    S.colourPicked = true;
    S.colours.second = null; S.colours.pinstripe = null; S.opts.pin = false;
  }
  [["main", "main"], ["second", "second"], ["serv", "serving"],
   ["centre", "centre"]].forEach(function (pair) {
    var want = pre[pair[0]];
    if (!want || !surfaceShown(pair[1])) return;
    var c = colourByName(looseName(want));
    if (shareColourOk(c, pair[1])) S.colours[pair[1]] = c.n;
  });

  if (pre.pin) {
    /* Mirrors toggleOpt("pin"): turning it on with no colour lands on White, so
       a link carrying the flag alone rebuilds the same state a click would. */
    S.opts.pin = true;
    var pc = pre.pin === "1" ? null : colourByName(looseName(pre.pin));
    S.colours.pinstripe = shareColourOk(pc, "pinstripe") ? pc.n
                        : (S.colours.pinstripe || "White");
  }
  if (pre.wrap && nocksIncluded()) {
    var w = looseFind(SHRINKS, pre.wrap);
    if (w) S.colours.shrink = w;
  }
  if (pre.nock && nockAsked()) {
    var n = looseFind(NOCKS, pre.nock);
    if (n) {
      S.nock = n;
      if (pre.nockx && n === NOCK_OTHER) S.nockOther = pre.nockx;
      var sd = $("s7"); if (sd) sd.classList.add("done");
    }
  }

  if (pre.loop && accessoriesOffered()) {
    S.opts.loop = Math.max(0, Math.min(20, pre.loop));
    if (pre.rope === "23" || pre.rope === "24") S.opts.loopRope = pre.rope;
    if (pre.lcol && loopColours().indexOf(pre.lcol) >= 0) S.opts.loopColour = pre.lcol;
  }
  /* The peep and the rush are ticked only if they can still be BOUGHT. update()
     would clear them a moment later anyway; refusing here means the recipient
     never sees a tick appear and vanish. */
  if (pre.peep && accessoriesOffered() && optionSellable("peep")) S.opts.peep = true;
  if (pre.rush && optionSellable("rush")) S.opts.rush = true;

  var s6 = $("s6"); if (s6) s6.classList.add("done");
  renderSurfaces();
  renderOpts();
  renderNocks();
  update();
  /* Land on the options rather than the top: the recipient arrives at a build
     that is already answered, so the useful screen is the last one — what it
     costs, what is still missing, and the button. */
  if (!missingStep()) goStep(7);
}

/* Normalised the way looseFind() normalises, so a colour typed into a link with
   odd spacing still resolves. */
function looseName(want){
  var w = String(want || "").trim().toLowerCase().replace(/\s+/g, " ");
  var c = COLOURS.find(function (x) {
    return String(x.n).trim().toLowerCase().replace(/\s+/g, " ") === w;
  });
  return c ? c.n : want;
}

function optionSellable(id){
  var o = OPTIONS.find(function (x) { return x.id === id; });
  return !!o && o.available !== false;
}

/* ============================================================
   COLOURS ON A PHONE, AND "MY BOW IS NOT LISTED"
   ============================================================
   The script half of two changes from the 8 October 2026 conversion audit; the
   other half is the last block of rc-builder.css. Both were first shipped
   behind a ?demo=1 address so Quinn could look before any customer saw them.
   He approved them that same 8 October ("demos are good") and they now run for
   every visitor: there is no flag, and no class on the builder's root.

   THREE ONE-LINE HOOKS reach this code: update() ends with syncSetPrices(),
   setTab() ends with colourRowIntoView(), and toggleOpt() calls
   colourRowIntoView() when a pinstripe is added.

   NOTHING READS demo=1 ANY MORE, and links carrying it are still about. It is
   now an unrecognised parameter like any other: parsePreselect() ignores it
   and shareUrl() never writes it, so such a link opens exactly the builder
   the same link without it does. */
/* Tapping a colour row's NAME brings it into view the way picking a colour
   already does (advanceAfterColour). Without this, opening "End servings" while
   the 67-swatch Main colour list above it is open leaves the tapped row some
   1,300px above the screen: the list above collapses and nothing follows it.
   `nearest`, as there, so a row that is already in view does not move. The
   clearance from the site header and from the pinned preview is scroll-margin
   on .shead in the stylesheet, not an offset here. */
function colourRowIntoView(id){
  if (!id) return;
  var head = document.querySelector('#surfaceList [data-surface="' + id + '"]');
  if (head) head.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

/* The set price on each "What does your bow take?" choice, and again on step
   3's closed line. Run from update(), so it follows every state change.

   READ FROM THE VARIANTS, NEVER TYPED IN: pkgFor() resolves a parts list to the
   same package a catalogue bow with those parts would get, so a price moved in
   Shopify moves here with no push.

   ONLY WHEN A FULL SET IS BEING BOUGHT. Someone buying one piece must not be
   shown set prices beside a choice that only describes their bow; their price
   is on the piece rows in step 4. Switching between the two modes after the
   panel is on screen is why this adds AND removes.

   The price is appended to the tile rather than written into its template
   because notListedPath() draws the tiles when "Yes" is answered and a later
   change between a full set and one piece does not redraw them, so the price
   has to be put on and taken off from here (the paragraph above); and so that
   one function owns the figure in both places: an edit to the template or to
   step 3's wording cannot drift from this. */
function syncSetPrices(){
  var setMode = !(S.buy === "single" && singlesOffered());
  var list = CONFIGS[S.type] || CONFIGS.compound;
  var tiles = document.querySelectorAll("#nlPanel .tiles .tile");
  for (var i = 0; i < tiles.length; i++) {
    var pk  = (setMode && list[i]) ? pkgFor({ p: list[i].p }) : null;
    var tag = tiles[i].querySelector(".rc-nl-price");
    if (!pk) { if (tag) tiles[i].removeChild(tag); continue; }
    if (!tag) {
      tag = document.createElement("b");
      tag.className = "rc-nl-price";
      tiles[i].appendChild(tag);
    }
    var txt = money(pk.price);
    if (tag.textContent !== txt) tag.textContent = txt;
  }

  /* Step 3's closed line: "Hoyt CRX 32 · Bowstring and two cables · $159.99".
     THE PRICE IS AN ELEMENT OF ITS OWN, found by its class, so last time's
     comes off without matching any text. It used to be appended to the line's
     text and taken off by comparing the end of the line with what had been
     appended. A browser translating the page rewrites that text ("$159.99" is
     "159,99 $" in French), the comparison failed, and every update() added the
     price again. data-rc holds the figure as written here, so an unchanged
     price is left alone. A catalogue bow's line never gets the element, and
     anything that sets the line's text (setCustomBow, pickBow) removes it. */
  var v3 = $("v3");
  if (!v3) return;
  var tail = v3.querySelector(".rc-v3-price");
  var bp = (setMode && S.bow && S.bow.custom) ? pkgFor(S.bow) : null;
  var want = bp ? " · " + money(bp.price) : "";
  if (tail && (!want || tail.getAttribute("data-rc") !== want)) { tail.parentNode.removeChild(tail); tail = null; }
  if (want && !tail && v3.textContent) {
    tail = document.createElement("span");
    tail.className = "rc-v3-price";
    tail.setAttribute("data-rc", want);
    tail.textContent = want;
    v3.appendChild(tail);
  }
}

function boot() {
  if (booted) return;
  ROOT = document.querySelector(".rc-builder");
  if (!ROOT) return;
  booted = true;

  if (!CFG.token) {
    $("rcLoading").hidden = true;
    showError("The builder has no Storefront API token yet. Add it in the section settings.");
    return;
  }

  buildPricing();
  buildNocks();
  buildShrinks();
  buildLoopColours();
  buildAmoLengths();
  buildServingOnly();
  buildMaterialColours();
  buildSpeckledColours();
  buildOutOfStock();
  buildServingOut();
  buildCentreColours();
  /* One delegated listener for the whole colour list. Bound before the first
     render, and it survives every innerHTML replacement because it lives on the
     container rather than on the rows. */
  bindSurfaces();
  /* No nock is preselected any more — see missingStep(). The "Not sure" entry
     stays first in the list as a real choice, it just is not made for anyone. */
  if (!S.colours.shrink) S.colours.shrink = SHRINKS[0] || "Black";
  if (!S.opts.loopColour) S.opts.loopColour = LOOP_COLOURS[0] || "Black";

  Promise.all([
    fetchAll("bow_model"),
    fetchAll("string_colour"),
    fetchAll("string_material")
  ]).then(function (res) {
    BOWS      = shapeBows(res[0]);
    COLOURS   = shapeColours(res[1]);
    MATERIALS = shapeMaterials(res[2]);

    if (!COLOURS.length || !MATERIALS.length) throw new Error("No colours or materials published to storefronts.");

    // defaults must exist in the real data, not the prototype's assumptions
    if (!colourByName(S.colours.main))    S.colours.main    = COLOURS[0].n;
    if (!colourByName(S.colours.serving)) S.colours.serving = COLOURS[0].n;
    if (!colourByName(S.colours.centre))  S.colours.centre  = COLOURS[0].n;
    if (!MATERIALS.find(function (m) { return m.id === S.material; })) S.material = MATERIALS[0].id;

    $("rcLoading").hidden = true;
    $("rcMain").hidden = false;

    renderTypes(); renderMakes(); renderModels(); renderYears(); update();

    applyPreselect();
    showContactPosted();
    bindPhotoUpload();

    var rz;
    window.addEventListener("resize", function () {
      clearTimeout(rz); rz = setTimeout(function () { placePreview(); renderPreview(); panelRoom(); }, 120);
    });
    placePreview();

    bindZoom();

    if (CFG.debug) {
      var n = 0;
      Object.keys(BOWS).forEach(function (t) {
        Object.keys(BOWS[t]).forEach(function (mk) { n += BOWS[t][mk].length; });
      });
      console.log("[rc-builder] " + n + " bows, " + COLOURS.length + " colours, " + MATERIALS.length + " materials");
      /* A centre-serving name that matches nothing is invisible otherwise: the
         colour simply is not on the row and nobody knows a name was typed. */
      var miss = CENTRE_COLOURS.filter(function (want) {
        return !COLOURS.some(function (c) { return String(c.n).toLowerCase() === want; });
      });
      if (miss.length) console.warn("[rc-builder] centre serving colours not in the catalogue: " + miss.join(", "));
    }
  }).catch(function (e) {
    var el = $("rcLoading"); if (el) el.hidden = true;
    showError((e && e.message ? e.message : "Unknown error")
      + " Check that all three metaobject definitions have Storefronts API access switched on, and that the token is a Storefront (public) token rather than an Admin one.");
    if (window.console) console.error("[rc-builder]", e);
  });
}

/* ---- the handlers the markup calls ---- */
window.RC = {
  coloursDone: coloursDone, showpieceNote: showpieceNote, setAddonVar: setAddonVar,
  toggleStep: toggleStep, pickType: pickType, pickMake: pickMake, notListed: notListed,
  pickBow: pickBow, pickYear: pickYear, pickMat: pickMat, setColour: setColour,
  notListedPath: notListedPath, chooseConfig: chooseConfig, setCustomBow: setCustomBow,
  customContinue: customContinue,
  toggleOpt: toggleOpt, bumpLoop: bumpLoop, setRope: setRope,
  setLoopColour: setLoopColour, addToCart: addToCart,
  renderModels: renderModels, setAmo: setAmo, setAmoChoice: setAmoChoice, amoKey: amoKey, setTab: setTab,
  setNock: setNock, setNockOther: setNockOther, setShrink: setShrink,
  pickBuy: pickBuy, pickPiece: pickPiece, removePhoto: removePhoto,
  shareBuild: shareBuild, _shareUrl: shareUrl,
  _state: S, _render: RENDER_INFO, _zoomFloor: zoomFloor, _options: function () { return OPTIONS; },
  _data: function () { return { BOWS: BOWS, COLOURS: COLOURS, MATERIALS: MATERIALS }; }
};

/* The theme editor re-renders a section over AJAX when a setting changes, but it
   does not re-run the section's script. Without this the builder would redraw a
   fresh spinner with nothing left running to fill it. */
document.addEventListener("shopify:section:load", function (e) {
  if (e.target && e.target.querySelector && e.target.querySelector(".rc-builder")) {
    CFG = window.RC_BUILDER_CONFIG || CFG;
    booted = false;
    boot();
  }
});

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();

})();
